import React, { useState, useEffect, useRef } from "react";
import ImportTool from './ImportTool';

const TETRUS_API_KEY = '791457bcf3d49ee84acd659e73b28a38bf39578a8f8cd065a57d83470bf343b1';
const apiHeaders = { 'X-API-Key': TETRUS_API_KEY };

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --bg: #080C14;
    --surface: #0D1421;
    --surface2: #111827;
    --border: #1E2A3A;
    --border2: #243044;
    --accent: #0EA5E9;
    --accent2: #38BDF8;
    --green: #10B981;
    --red: #EF4444;
    --yellow: #F59E0B;
    --purple: #8B5CF6;
    --text: #F1F5F9;
    --text2: #CBD5E1;
    --text3: #CBD5E1;
    --font: 'DM Sans', sans-serif;
    --mono: 'DM Mono', monospace;
  }

  body { background: var(--bg); color: var(--text); font-family: var(--font); }

  .app { display: flex; height: 100vh; overflow: hidden; }

  /* SIDEBAR */
  .sidebar {
    width: 220px;
    min-width: 220px;
    background: var(--surface);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .logo {
    padding: 24px 20px 20px;
    border-bottom: 1px solid var(--border);
  }

  .logo-name {
    font-size: 26px;
    font-weight: 900;
    letter-spacing: 0.12em;
    color: var(--text);
  }

  .logo-name span { color: var(--accent); }

  .logo-tag {
    font-size: 10px;
    color: var(--text3);
    letter-spacing: 1.5px;
    text-transform: uppercase;
    margin-top: 2px;
    font-family: var(--mono);
  }

  .nav { padding: 12px 10px; flex: 1; }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: var(--text2);
    transition: all 0.15s;
    margin-bottom: 2px;
    position: relative;
  }

  .nav-item:hover { background: var(--surface2); color: var(--text); }

  .nav-item.active {
    background: rgba(14, 165, 233, 0.1);
    color: #FFFFFF;
  }

  .nav-item.active::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 16px;
    background: var(--accent);
    border-radius: 2px;
  }

  .nav-icon { font-size: 14px; width: 16px; text-align: center; }

  .nav-badge {
    margin-left: auto;
    background: var(--red);
    color: white;
    font-size: 10px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 10px;
    font-family: var(--mono);
  }

  .status-bar {
    padding: 14px 16px;
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--green);
    box-shadow: 0 0 6px var(--green);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .status-text { color: var(--text3); font-family: var(--mono); }
  .status-text span { color: var(--green); }

  /* MAIN */
  .main { flex: 1; overflow-y: auto; background: var(--bg); }

  /* TOPBAR */
  .topbar {
    padding: 20px 32px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--bg);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .page-title { font-size: 20px; font-weight: 700; letter-spacing: -0.5px; }
  .page-sub { font-size: 12px; color: var(--text3); margin-top: 2px; font-family: var(--mono); }

  .topbar-actions { display: flex; gap: 10px; align-items: center; }

  .btn {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    font-family: var(--font);
    transition: all 0.15s;
  }

  .btn-ghost {
    background: transparent;
    border: 1px solid var(--border2);
    color: var(--text2);
  }

  .btn-ghost:hover { border-color: var(--accent); color: var(--accent); }

  .btn-primary {
    background: var(--accent);
    color: white;
  }

  .btn-primary:hover { background: var(--accent2); }

  /* CONTENT */
  .content { padding: 28px 32px; }

  /* STAT GRID */
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 20px;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
  .stat-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 18px 20px;
    position: relative;
    overflow: hidden;
  }

  .stat-card::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--accent), transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  .stat-card:hover::after { opacity: 1; }

  .stat-label { font-size: 12px; color: #CBD5E1; font-family: var(--mono); letter-spacing: 0.5px; text-transform: uppercase; }
  .stat-value { font-size: 26px; font-weight: 700; color: #FFFFFF; margin: 6px 0 4px; letter-spacing: -1px; }
  .stat-sub { font-size: 11px; color: #CBD5E1; }
  .stat-sub.up { color: var(--green); }
  .stat-sub.down { color: var(--red); }

  /* PIPELINE */
  .pipeline-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 20px 24px;
    margin-bottom: 20px;
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
  }

  .card-title { font-size: 14px; font-weight: 600; color: var(--text); }
  .card-sub { font-size: 11px; color: var(--text3); font-family: var(--mono); }

  .pipeline-flow {
    display: flex;
    align-items: center;
    gap: 0;
  }

  .pipeline-step {
    flex: 1;
    text-align: center;
    position: relative;
  }

  .pipeline-step:not(:last-child)::after {
    content: '→';
    position: absolute;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text3);
    font-size: 14px;
    z-index: 1;
  }

  .pipeline-num {
    font-size: 20px;
    font-weight: 700;
    font-family: var(--mono);
    color: var(--text);
  }

  .pipeline-label {
    font-size: 11px;
    color: #CBD5E1;
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .pipeline-bar {
    height: 3px;
    background: var(--border);
    border-radius: 2px;
    margin-top: 10px;
    overflow: hidden;
  }

  .pipeline-fill {
    height: 100%;
    border-radius: 2px;
    background: var(--accent);
  }

  /* TWO COL */
  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }
  .three-col { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-bottom: 20px; }

  /* TABLE */
  .table-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    overflow: hidden;
  }

  .table-card .card-header { padding: 18px 20px 0; margin-bottom: 0; }

  table { width: 100%; border-collapse: collapse; }

  th {
    padding: 10px 20px;
    text-align: left;
    font-size: 10px;
    font-weight: 600;
    color: var(--text3);
    letter-spacing: 0.8px;
    text-transform: uppercase;
    font-family: var(--mono);
    border-bottom: 1px solid var(--border);
  }

  td {
    padding: 12px 20px;
    font-size: 13px;
    border-bottom: 1px solid var(--border);
    color: #E2E8F0;
  }

  tr:last-child td { border-bottom: none; }
  tr:hover td { background: rgba(255,255,255,0.02); color: var(--text); }

  .td-name { color: var(--text); font-weight: 500; }

  /* TAGS */
  .tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    font-family: var(--mono);
  }

  .tag-green { background: rgba(16,185,129,0.12); color: var(--green); }
  .tag-red { background: rgba(239,68,68,0.12); color: var(--red); }
  .tag-yellow { background: rgba(245,158,11,0.12); color: var(--yellow); }
  .tag-blue { background: rgba(14,165,233,0.12); color: var(--accent); }

  /* SCORE BAR */
  .score-bar { display: flex; align-items: center; gap: 10px; }
  .score-track { flex: 1; height: 4px; background: var(--border); border-radius: 2px; overflow: hidden; }
  .score-fill { height: 100%; border-radius: 2px; }
  .score-num { font-size: 12px; font-family: var(--mono); color: var(--text2); width: 28px; text-align: right; }

  /* ENGAGEMENT */
  .eng-split {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px 0;
  }

  .eng-block { flex: 1; }
  .eng-pct { font-size: 32px; font-weight: 700; letter-spacing: -1px; font-family: var(--mono); }
  .eng-label { font-size: 11px; color: #CBD5E1; margin-top: 2px; }
  .eng-bar { height: 6px; border-radius: 3px; margin-top: 8px; }

  /* ALERT */
  .alert {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 8px;
    font-size: 12px;
  }

  .alert-red { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); color: #FCA5A5; }
  .alert-yellow { background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.2); color: #FCD34D; }
  .alert-green { background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.2); color: #6EE7B7; }

  /* METRIC ROW */
  .metric-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid var(--border); }
  .metric-row:last-child { border-bottom: none; }
  .metric-name { font-size: 13px; color: #E2E8F0; }
  .metric-val { font-size: 13px; font-weight: 600; font-family: var(--mono); }

  /* MINI CHART */
  .mini-bars { display: flex; align-items: flex-end; gap: 3px; height: 40px; }
  .mini-bar { flex: 1; border-radius: 2px 2px 0 0; transition: opacity 0.2s; }
  .mini-bar:hover { opacity: 0.8; }

  /* FILTER PANEL */
  .filter-panel {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 20px;
  }

  .filter-group { margin-bottom: 18px; }
  .filter-label { font-size: 11px; color: #CBD5E1; font-family: var(--mono); letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 8px; }

  .filter-tags { display: flex; flex-wrap: wrap; gap: 6px; }
  .filter-tag {
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    border: 1px solid var(--border2);
    color: #CBD5E1;
    cursor: pointer;
    transition: all 0.15s;
  }
  .filter-tag:hover, .filter-tag.selected {
    border-color: var(--accent);
    color: var(--accent);
    background: rgba(14,165,233,0.08);
  }

  /* SETTINGS */
  .setting-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 0;
    border-bottom: 1px solid var(--border);
  }

  .setting-row:last-child { border-bottom: none; }
  .setting-name { font-size: 13px; font-weight: 500; }
  .setting-desc { font-size: 11px; color: var(--text3); margin-top: 2px; }

  .toggle {
    width: 36px;
    height: 20px;
    border-radius: 10px;
    cursor: pointer;
    position: relative;
    transition: background 0.2s;
  }

  .toggle.on { background: var(--accent); }
  .toggle.off { background: var(--border2); }

  .toggle::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: white;
    top: 3px;
    transition: left 0.2s;
  }

  .toggle.on::after { left: 19px; }
  .toggle.off::after { left: 3px; }


  /* LOGIN */
  .login-page {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: var(--bg);
    position: relative;
    overflow: hidden;
  }

  .login-page::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(ellipse at 30% 50%, rgba(14, 165, 233, 0.06) 0%, transparent 60%),
                radial-gradient(ellipse at 70% 50%, rgba(139, 92, 246, 0.04) 0%, transparent 60%);
    animation: loginGlow 8s ease-in-out infinite alternate;
  }

  @keyframes loginGlow {
    0% { transform: translate(0, 0); }
    100% { transform: translate(-2%, 1%); }
  }

  .login-card {
    position: relative;
    width: 400px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 48px 40px 40px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);
  }

  .login-logo {
    text-align: center;
    margin-bottom: 8px;
  }

  .login-logo-name {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -1px;
    color: var(--text);
  }

  .login-logo-name span { color: var(--accent); }

  .login-logo-tag {
    font-size: 11px;
    color: var(--text3);
    letter-spacing: 2px;
    text-transform: uppercase;
    font-family: var(--mono);
    margin-top: 4px;
  }

  .login-divider {
    height: 1px;
    background: var(--border);
    margin: 28px 0;
  }

  .login-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 4px;
    text-align: center;
  }

  .login-subtitle {
    font-size: 12px;
    color: var(--text3);
    text-align: center;
    margin-bottom: 28px;
  }

  .login-field {
    margin-bottom: 18px;
  }

  .login-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--text2);
    margin-bottom: 6px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-family: var(--mono);
  }

  .login-input {
    width: 100%;
    padding: 11px 14px;
    background: var(--surface2);
    border: 1px solid var(--border2);
    border-radius: 8px;
    color: var(--text);
    font-size: 14px;
    font-family: var(--font);
    outline: none;
    transition: border-color 0.2s;
  }

  .login-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }

  .login-input::placeholder {
    color: var(--text3);
  }

  .login-btn {
    width: 100%;
    padding: 12px;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: var(--font);
    transition: background 0.2s;
    margin-top: 8px;
  }

  .login-btn:hover { background: var(--accent2); }

  .login-error {
    background: rgba(239,68,68,0.08);
    border: 1px solid rgba(239,68,68,0.2);
    color: #FCA5A5;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 12px;
    margin-bottom: 18px;
    text-align: center;
  }

  .login-footer {
    text-align: center;
    margin-top: 24px;
    font-size: 11px;
    color: var(--text3);
    font-family: var(--mono);
  }

  /* MODAL */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
  }

  .modal {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 32px;
    width: 480px;
    max-width: 90vw;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  }

  .modal-title {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 4px;
  }

  .modal-sub {
    font-size: 12px;
    color: var(--text3);
    margin-bottom: 24px;
    font-family: var(--mono);
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }

  .modal-field {
    margin-bottom: 18px;
  }

  .modal-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--text2);
    margin-bottom: 6px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-family: var(--mono);
  }

  .modal-input, .modal-select {
    width: 100%;
    padding: 10px 14px;
    background: var(--surface2);
    border: 1px solid var(--border2);
    border-radius: 8px;
    color: var(--text);
    font-size: 14px;
    font-family: var(--font);
    outline: none;
    transition: border-color 0.2s;
  }

  .modal-input:focus, .modal-select:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }

  .modal-select option {
    background: var(--surface);
    color: var(--text);
  }

  .modal-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 28px;
  }

  /* SCROLLBAR */
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 2px; }
`;

const NAV = [
  { id: "overview", label: "Overview", icon: "⬡" },
  { id: "universe", label: "Universe", icon: "🌐" },
  { id: "leads", label: "Lead Intelligence", icon: "◈" },
  { id: "cleaning", label: "Cleaning Engine", icon: "◎" },
  { id: "contacts", label: "Contacts", icon: "◈" },
  { id: "accounts", label: "Accounts / Companies", icon: "⊞" },
  { id: "campaigns", label: "Campaign Automation", icon: "◷" },
  { id: "engagement", label: "Engagement Intelligence", icon: "◉" },
  { id: "deliverability", label: "Deliverability", icon: "◈" },
  { id: "activity", label: "Activity Feed", icon: "⊛" },
  { id: "intelligence", label: "Reply Intelligence", icon: "⬡" },
  { id: "placement", label: "Inbox Placement", icon: "◎" },
  { id: "pipeline", label: "Hot Opportunities", icon: "▲" },
  { id: "integrations", label: "Integrations", icon: "⊕" },
  { id: "analytics", label: "Analytics", icon: "◫" },
  { id: "reports", label: "Reports", icon: "◫" },
  { id: "onboarding", label: "Onboarding", icon: "★" },
  { id: "settings", label: "Settings", icon: "◌" },
  { id: "search", label: "Search", icon: "⌕" },
  { id: "verification", label: "Verification Tools", icon: "✓" }
];

const PAGES = {
  overview: {
    title: "Command Center",
    sub: "SYSTEM OVERVIEW — LEAD-TO-REVENUE PLATFORM",
  },
  leads: { title: "Lead Intelligence", sub: "FILTER AND EXTRACT TARGET AUDIENCES" },
  universe: { title: "Universe", sub: "LIVE PROSPECT DATABASE — AGENTS ADDING RECORDS 24/7" },
  cleaning: { title: "Cleaning Engine", sub: "DATA VALIDATION AND QUALITY CONTROL" },
  campaigns: { title: "Campaign Automation", sub: "OUTBOUND EXECUTION AND SEQUENCING" },
  engagement: { title: "Engagement Intelligence", sub: "HUMAN VS BOT SIGNAL DETECTION" },
  placement: { title: "Inbox Placement Testing", sub: "ESP DELIVERY TESTING AND SEED RESULTS" },
  activity: { title: "Activity Feed", sub: "REAL-TIME EMAIL EVENTS — OPENS, CLICKS, BOUNCES" },
  intelligence: { title: "Reply Intelligence", sub: "REPLY & BOUNCE SIGNAL ANALYSIS" },
  deliverability: { title: "Deliverability", sub: "SENDING DOMAIN HEALTH AND REPUTATION MONITORING" },
  pipeline: { title: "Hot Opportunities", sub: "CONTACTS READY FOR HUMAN OUTREACH" },
  contacts: { title: "Contacts", sub: "ALL CONTACTS WITH ENGAGEMENT DATA" },
  accounts: { title: "Accounts / Companies", sub: "COMPANIES AND ORGANIZATIONS" },
  analytics: { title: "Analytics", sub: "PERFORMANCE TRENDS AND REPORTING" },
  settings: { title: "Settings", sub: "SYSTEM CONFIGURATION AND INTEGRATIONS" },
  integrations: { title: "Integrations", sub: "LIVE INTEGRATION STATUS AND CONNECTIONS" },
  onboarding: { title: "Client Onboarding", sub: "GUIDED SETUP WIZARD" },
  reports: { title: "Reports", sub: "ANALYTICS & INSIGHTS" },
  search: { title: "Contact Search", sub: "SEARCH ACROSS ALL LEADS" },
  verification: { title: "Verification Tools", sub: "EMAIL VERIFICATION — LAYER-BY-LAYER DIAGNOSTIC" },
};

function ScoreBar({ value, color }) {
  return (
    <div className="score-bar">
      <div className="score-track">
        <div className="score-fill" style={{ width: `${value}%`, background: color }} />
      </div>
      <div className="score-num">{value}</div>
    </div>
  );
}

function MiniChart({ data, color }) {
  const max = Math.max(...data);
  return (
    <div className="mini-bars">
      {data.map((v, i) => (
        <div
          key={i}
          className="mini-bar"
          style={{
            height: `${(v / max) * 100}%`,
            background: color,
            opacity: i === data.length - 1 ? 1 : 0.4 + (i / data.length) * 0.4,
          }}
        />
      ))}
    </div>
  );
}

function Overview({ liveStats, onShowCreateCampaign }) {
  const fmt = (n) => (n > 0 ? n.toLocaleString() : "0");
  const cleanPct = liveStats.totalLeads > 0 ? ((liveStats.cleanLeads / liveStats.totalLeads) * 100).toFixed(1) : "0";
  return (
    <>
      <div className="stat-grid">
        {[
          { label: "Total Leads", value: liveStats.totalLeads > 0 ? liveStats.totalLeads.toLocaleString() : "—", sub: `${liveStats.industryCount || 0} industries · ${liveStats.countryCount || 0} countries`, color: "#0EA5E9" },
          { label: "Clean Leads", value: fmt(liveStats.cleanLeads), sub: `${cleanPct}% pass rate`, color: "#10B981", cls: liveStats.cleanLeads > 0 ? "up" : "" },
          { label: "Avg. Human Score", value: liveStats.avgHumanScore > 0 ? String(liveStats.avgHumanScore) : "—", sub: `${fmt(liveStats.highScore)} high-quality contacts`, color: "#F59E0B" },
          { label: "Companies", value: fmt(liveStats.companyCount), sub: `${fmt(liveStats.industryCount)} unique industries`, color: "#8B5CF6", cls: "up" },
        ].map((s) => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: '#FFFFFF'}}>{s.value}</div>
            <div className={`stat-sub ${s.cls || ""}`}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="pipeline-card">
        <div className="card-header">
          <div>
            <div className="card-title">Lead Pipeline</div>
            <div className="card-sub">REAL-TIME FLOW — FILTER → CLEAN → CAMPAIGN → ENGAGE → QUALIFY</div>
          </div>
        </div>
        <div className="pipeline-flow">
          {[
            { label: "Imported", num: liveStats.totalLeads > 0 ? liveStats.totalLeads.toLocaleString() : "—", pct: 100 },
            { label: "Syntax Valid", num: liveStats.syntaxValid > 0 ? liveStats.syntaxValid.toLocaleString() : "—", pct: liveStats.totalLeads > 0 ? (liveStats.syntaxValid / liveStats.totalLeads) * 100 : 0 },
            { label: "MX Valid", num: liveStats.mxValid > 0 ? liveStats.mxValid.toLocaleString() : "—", pct: liveStats.totalLeads > 0 ? (liveStats.mxValid / liveStats.totalLeads) * 100 : 0 },
            { label: "Clean", num: liveStats.cleanLeads > 0 ? liveStats.cleanLeads.toLocaleString() : "—", pct: liveStats.totalLeads > 0 ? (liveStats.cleanLeads / liveStats.totalLeads) * 100 : 0 },
            { label: "Campaign Ready", num: liveStats.highScore > 0 ? liveStats.highScore.toLocaleString() : "—", pct: liveStats.totalLeads > 0 ? (liveStats.highScore / liveStats.totalLeads) * 100 : 0 },
          ].map((s, i) => (
            <div key={i} className="pipeline-step">
              <div className="pipeline-num">{s.num}</div>
              <div className="pipeline-label">{s.label}</div>
              <div className="pipeline-bar">
                <div className="pipeline-fill" style={{ width: `${Math.min(100, s.pct * 8)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="two-col">
        <div className="pipeline-card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <div className="card-title">Active Campaigns</div>
            {(liveStats.activeCampaignCount || 0) > 0 && <span className="tag tag-yellow">{liveStats.activeCampaignCount} Running</span>}
          </div>
          <div style={{ textAlign: "center", padding: "32px 20px" }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>&#9671;</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text2)", marginBottom: 6 }}>{(liveStats.activeCampaignCount || 0) > 0 ? (liveStats.activeCampaignCount || 0) + " campaign" + ((liveStats.activeCampaignCount || 0) !== 1 ? "s" : "") + " running" : "No campaigns running"}</div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 20, maxWidth: 280, margin: "0 auto 20px" }}>Launch your first campaign to start engaging qualified leads across your pipeline.</div>
            <button className="btn btn-primary" onClick={onShowCreateCampaign}>Launch Campaign</button>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="pipeline-card" style={{ marginBottom: 0, flex: 1 }}>
            <div className="card-header">
              <div className="card-title">System Alerts</div>
            </div>
            {liveStats.eliminated > 0 && <div className="alert alert-yellow">{"⚡ " + liveStats.eliminated.toLocaleString() + " leads eliminated by rules engine — " + ((liveStats.eliminated / (liveStats.totalLeads || 1)) * 100).toFixed(1) + "% elimination rate"}</div>}
            {liveStats.humanEngagementRate > 0 && <div className="alert alert-green">{"✓ Human engagement rate: " + liveStats.humanEngagementRate + "% across " + (liveStats.totalLeads || 0).toLocaleString() + " leads"}</div>}
            <div className="alert alert-green">{"✓ Database connected — " + liveStats.totalLeads.toLocaleString() + " records across " + liveStats.industryCount + " industries"}</div>
          </div>
        </div>
      </div>

      <div className="three-col">
        {(() => {
          const t = liveStats.totalLeads || 1;
          const bounceRate = ((liveStats.bounced || 0) / t * 100).toFixed(2);
          const elimRate = (liveStats.eliminationRate != null ? liveStats.eliminationRate : 0).toFixed(1);
          const deliveryRate = (liveStats.cleanRate != null ? liveStats.cleanRate : 0).toFixed(1);
          return [
            { label: "Bounce Rate", val: bounceRate + "%", sub: "From active campaign sends", color: parseFloat(bounceRate) < 3 ? "#10B981" : "#EF4444" },
            { label: "Elimination Rate", val: elimRate + "%", color: parseFloat(elimRate) < 35 ? "#F59E0B" : "#EF4444" },
            { label: "Clean Rate", val: deliveryRate + "%", sub: "Contacts passing full validation", color: parseFloat(deliveryRate) > 70 ? "#10B981" : "#F59E0B" },
          ];
        })().map((m) => (
          <div key={m.label} className="stat-card">
            <div className="stat-label">{m.label}</div>
            <div className="stat-value" style={{ color: '#FFFFFF', fontSize: 28 }}>{m.val}</div>
            {m.sub && <div className="stat-sub">{m.sub}</div>}
          </div>
        ))}
      </div>
    </>
  );
}

function LeadIntelligence({ liveStats }) {
  const [tab, setTab] = useState('builder');
  const [selected, setSelected] = useState({ industry: "", companysize: "", geography: "", revenue: "" });
  const [selectedList, setSelectedList] = useState("");
  const [importLists, setImportLists] = useState([]);
  const [chLists, setChLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [listPreviewLeads, setListPreviewLeads] = useState([]);
  const [listPreviewLoading, setListPreviewLoading] = useState(false);
  const [tagCloud, setTagCloud] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [tagLeads, setTagLeads] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [activeListId, setActiveListId] = useState(null);
  const [listContacts, setListContacts] = useState([]);
  const [listContactsLoading, setListContactsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [segmentAccounts, setSegmentAccounts] = useState([]);
  const [segmentLoading, setSegmentLoading] = useState(true);

  // Fetch import lists and poll while any list is cleaning
  const fetchImportLists = () => {
    fetch('/api/import-lists', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setImportLists(d.lists || []); setListsLoading(false); })
      .catch(() => setListsLoading(false));
  };

  useEffect(() => {
    fetch('/api/accounts', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setSegmentAccounts(d.accounts || []); setSegmentLoading(false); })
      .catch(() => setSegmentLoading(false));
  }, []);

  useEffect(() => {
    fetchImportLists();
    fetch('/api/lists', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => setChLists(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const hasCleaning = importLists.some(l => l.cleaning_status === 'cleaning' || l.cleaning_status === 'pending');
    if (!hasCleaning) return;
    const interval = setInterval(fetchImportLists, 5000);
    return () => clearInterval(interval);
  }, [importLists]);

  useEffect(() => {
    if (!activeListId) { setListContacts([]); return; }
    setListContactsLoading(true);
    fetch('/api/leads/search?list_id=' + encodeURIComponent(activeListId) + '&limit=50', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setListContacts(d.leads || []); setListContactsLoading(false); })
      .catch(() => setListContactsLoading(false));
  }, [activeListId]);

  async function deleteList(list) {
    try {
      if (list._chOnly) {
        const res = await fetch('/api/leads/lists/' + encodeURIComponent(list.filename), { method: 'DELETE', headers: apiHeaders });
        if (!res.ok) throw new Error('Delete failed');
        setChLists(prev => prev.filter(l => l.list_id !== list.filename));
      } else {
        const res = await fetch('/api/import-lists/' + list.id, { method: 'DELETE', headers: apiHeaders });
        if (!res.ok) throw new Error('Delete failed');
        setImportLists(prev => prev.filter(l => l.id !== list.id));
      }
      if (activeListId === list.filename) setActiveListId(null);
      setDeleteConfirm(null);
    } catch (err) {
      alert('Failed to delete list: ' + err.message);
    }
  }

  const extractSegment = async () => {
    setExtracting(true);
    const params = new URLSearchParams();
    if (selected.industry)     params.set('industry', selected.industry);
    if (selected.companysize)  params.set('companysize', selected.companysize);
    if (selected.geography)    params.set('geo', selected.geography);
    if (selected.revenue)      params.set('revenue', selected.revenue);
    if (selectedList)          params.set('list_id', selectedList);
    try {
      const resp = await fetch('https://tetrus.ai/api/leads/extract?' + params.toString(), { headers: apiHeaders });
      if (!resp.ok) {
        const e = await resp.json();
        alert('Extract failed: ' + (e.error || 'Unknown error'));
        setExtracting(false);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tetrus-segment-' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Network error: ' + e.message);
    }
    setExtracting(false);
  };

  const topInds = (liveStats.topIndustries || []).slice(0, 6).map(i => i.industry).filter(Boolean);
  if (topInds.length < 6) topInds.push(...["Healthcare", "SaaS", "Manufacturing", "Finance", "Retail", "Industrial"].filter(x => !topInds.includes(x)).slice(0, 6 - topInds.length));

  useEffect(() => {
    fetch('/api/tags', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => setTagCloud(d.tags || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedTag) { setTagLeads([]); return; }
    setTagLoading(true);
    fetch('/api/leads/search?tag=' + encodeURIComponent(selectedTag) + '&limit=25', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setTagLeads(d.leads || []); setTagLoading(false); })
      .catch(() => setTagLoading(false));
  }, [selectedTag]);

  useEffect(() => {
    if (!selectedList) { setListPreviewLeads([]); return; }
    setListPreviewLoading(true);
    fetch('/api/leads/search?list_id=' + encodeURIComponent(selectedList) + '&limit=6', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setListPreviewLeads(d.leads || []); setListPreviewLoading(false); })
      .catch(() => setListPreviewLoading(false));
  }, [selectedList]);

  const TAG_COLORS = ['#a78bfa','#34d399','#60a5fa','#f59e0b','#f87171','#00d4ff','#fb923c'];
  const tagColor = (tag) => TAG_COLORS[Math.abs(tag.split('').reduce((a,c) => a + c.charCodeAt(0), 0)) % TAG_COLORS.length];

  const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch(e) { return s || '—'; } };
  const passRate = (l) => { const t = l.total_contacts || 0; const c = l.clean_contacts || 0; return t > 0 ? ((c / t) * 100).toFixed(0) + '%' : '—'; };
  const importedNames = new Set(importLists.map(l => l.filename));
  const mergedLists = [
    ...importLists,
    ...chLists.filter(l => !importedNames.has(l.list_id)).map(l => ({
      _chOnly: true, filename: l.list_id, total_contacts: parseInt(l.total) || 0,
      created_at: null, cleaning_status: 'active',
    })),
  ];

  return (
    <>
    <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
      {['builder', 'lists'].map(t => (
        <button
          key={t}
          onClick={() => setTab(t)}
          style={{
            padding: '8px 20px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            fontFamily: 'var(--mono)', letterSpacing: 1, cursor: 'pointer', border: 'none',
            background: tab === t ? '#0EA5E9' : 'rgba(255,255,255,0.06)',
            color: tab === t ? '#fff' : '#CBD5E1',
            transition: 'all 0.15s',
          }}
        >
          {t === 'builder' ? 'AUDIENCE BUILDER' : 'LISTS'}
        </button>
      ))}
    </div>

    {tab === 'builder' && (
    <div className="two-col" style={{ alignItems: "start" }}>
      <div className="filter-panel">
        <div style={{ marginBottom: 16 }}>
          <div className="card-title">Audience Builder</div>
          <div className="card-sub" style={{ marginTop: 4 }}>DEFINE SEGMENT — {liveStats.totalLeads > 0 ? liveStats.totalLeads.toLocaleString() : "—"} RECORDS AVAILABLE</div>
        </div>

        <div className="filter-group">
          <div className="filter-label">Source List</div>
          <select
            value={selectedList}
            onChange={e => setSelectedList(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px', background: '#111827',
              border: '1px solid #1E2A3A', borderRadius: 6, color: selectedList ? '#F1F5F9' : '#CBD5E1',
              fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', outline: 'none',
            }}
          >
            <option value="">All Lists</option>
            {chLists.map(l => (
              <option key={l.list_id} value={l.list_id}>
                {l.list_id} ({parseInt(l.total || 0).toLocaleString()} contacts)
              </option>
            ))}
          </select>
        </div>

        {[
          { label: "Industry", options: topInds },
          { label: "Company Size", options: ["1-50", "50-200", "100-500", "500-2000", "2000+"] },
          { label: "Geography", options: ["United States", "Canada", "UK", "Europe", "Global"] },
          { label: "Revenue", options: ["<$5M", "$5M-$50M", "$50M-$150M", "$150M+"] },
        ].map((g) => (
          <div key={g.label} className="filter-group">
            <div className="filter-label">{g.label}</div>
            <div className="filter-tags">
              {g.options.map((o) => (
                <div
                  key={o}
                  className={`filter-tag ${selected[g.label.toLowerCase().replace(" ", "")] === o ? "selected" : ""}`}
                  onClick={() => setSelected({ ...selected, [g.label.toLowerCase().replace(" ", "")]: o })}
                >
                  {o}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="filter-group">
          <div className="filter-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Tags {tagCloud.length > 0 && <span style={{ color: '#CBD5E1', fontWeight: 400 }}>({tagCloud.length})</span>}</span>
            {selectedTag && (
              <span style={{ color: '#00d4ff', fontSize: 10, cursor: 'pointer', fontWeight: 600 }} onClick={() => setSelectedTag(null)}>✕ clear</span>
            )}
          </div>
          {tagCloud.length === 0 ? (
            <div style={{ fontSize: 11, color: '#CBD5E1', padding: '8px 0' }}>No tags yet — add tags in Contact Search</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {tagCloud.map(t => (
                <div
                  key={t.tag}
                  onClick={() => setSelectedTag(selectedTag === t.tag ? null : t.tag)}
                  style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                    fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                    background: selectedTag === t.tag ? tagColor(t.tag) + '30' : 'rgba(255,255,255,0.04)',
                    border: '1px solid ' + (selectedTag === t.tag ? tagColor(t.tag) : '#2a2a4a'),
                    color: selectedTag === t.tag ? tagColor(t.tag) : '#CBD5E1',
                    transition: 'all 0.15s',
                  }}
                >
                  {t.tag}
                  <span style={{ fontSize: 11 }}>{t.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 8, padding: "12px 16px", background: "rgba(14,165,233,0.08)", borderRadius: 8, border: "1px solid rgba(14,165,233,0.2)" }}>
          <div style={{ fontSize: 11, color: "#CBD5E1", fontFamily: "var(--mono)" }}>ESTIMATED SEGMENT SIZE</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#0EA5E9", letterSpacing: -1, marginTop: 4 }}>
            {selectedTag ? (tagLoading ? '…' : tagLeads.length) : selectedList ? (parseInt((chLists.find(l => l.list_id === selectedList) || {}).total || 0)).toLocaleString() : (liveStats.totalLeads > 0 ? liveStats.totalLeads.toLocaleString() : "—")} records
          </div>
          {selectedTag && <div style={{ fontSize: 11, color: '#00d4ff', marginTop: 2 }}>tagged: {selectedTag}</div>}
          {selectedList && <div style={{ fontSize: 11, color: '#a78bfa', marginTop: 2 }}>list: {selectedList}</div>}
        </div>
        <button className="btn btn-primary" style={{ width: "100%", marginTop: 12 }} onClick={extractSegment} disabled={extracting}>{extracting ? "Extracting…" : "Extract Segment →"}</button>
      </div>

      <div>
        <div className="table-card">
          <div className="card-header" style={{ padding: "18px 20px 14px" }}>
            <div className="card-title">{selectedTag ? `Tag: "${selectedTag}"` : selectedList ? `List: "${selectedList}"` : 'Segment Preview'}</div>
            {selectedTag
              ? <span className="tag tag-blue">{tagLoading ? '…' : tagLeads.length + ' matched'}</span>
              : selectedList
              ? <span className="tag tag-blue">{listPreviewLoading ? '…' : listPreviewLeads.length + ' shown'}</span>
              : <span className="tag tag-blue">live data</span>}
          </div>
          {selectedTag ? (
            tagLoading ? (
              <div style={{ padding: 24, color: '#CBD5E1', textAlign: 'center' }}>Loading tagged leads…</div>
            ) : tagLeads.length === 0 ? (
              <div style={{ padding: 24, color: '#CBD5E1', textAlign: 'center' }}>No leads with tag "{selectedTag}"</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Company</th>
                    <th>Industry</th>
                    <th>Score</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {tagLeads.map((l, i) => (
                    <tr key={i}>
                      <td className="td-name">
                        <div>{l.contact}</div>
                        <div style={{ fontSize: 11, color: '#CBD5E1' }}>{l.email}</div>
                      </td>
                      <td>{l.company || '—'}</td>
                      <td>{l.industry || '—'}</td>
                      <td>
                        <span style={{
                          fontFamily: 'monospace', fontWeight: 700,
                          color: l.score >= 80 ? '#10b981' : l.score >= 60 ? '#00d4ff' : l.score >= 40 ? '#a78bfa' : '#666'
                        }}>{l.score}</span>
                      </td>
                      <td style={{ color: '#CBD5E1' }}>{[l.city, l.state].filter(Boolean).join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : selectedList ? (
            listPreviewLoading ? (
              <div style={{ padding: 24, color: '#CBD5E1', textAlign: 'center' }}>Loading preview…</div>
            ) : listPreviewLeads.length === 0 ? (
              <div style={{ padding: 24, color: '#CBD5E1', textAlign: 'center' }}>No contacts found in this list.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Company</th>
                    <th>Industry</th>
                    <th>Score</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {listPreviewLeads.map((l, i) => (
                    <tr key={i}>
                      <td className="td-name">
                        <div>{l.contact}</div>
                        <div style={{ fontSize: 11, color: '#CBD5E1' }}>{l.email}</div>
                      </td>
                      <td>{l.company || '—'}</td>
                      <td>{l.industry || '—'}</td>
                      <td>
                        <span style={{
                          fontFamily: 'monospace', fontWeight: 700,
                          color: l.score >= 80 ? '#10b981' : l.score >= 60 ? '#00d4ff' : l.score >= 40 ? '#a78bfa' : '#666'
                        }}>{l.score}</span>
                      </td>
                      <td style={{ color: '#CBD5E1' }}>{[l.city, l.state].filter(Boolean).join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Industry</th>
                  <th>Size</th>
                  <th>Location</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {segmentLoading ? (
                  <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
                ) : segmentAccounts.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No segments yet. Import leads to get started.</td></tr>
                ) : segmentAccounts.slice(0, 10).map((a) => {
                  const status = a.active_contacts > 0 || a.avg_score >= 70 ? 'Verified' : 'Unverified';
                  const sz = a.contact_count >= 500 ? '500+' : a.contact_count >= 100 ? '100-500' : a.contact_count >= 50 ? '50-100' : String(a.contact_count);
                  return (
                    <tr key={a.company_name}>
                      <td className="td-name">{a.company_name}</td>
                      <td>{a.company_industry || '—'}</td>
                      <td>{sz}</td>
                      <td>{a.company_state || '—'}</td>
                      <td><span className={`tag ${status === 'Verified' ? 'tag-green' : 'tag-yellow'}`}>{status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
    )}

    {tab === 'lists' && (
    <div>
      <div className="table-card" style={{ marginBottom: 24 }}>
        <div className="card-header" style={{ padding: "18px 20px 14px" }}>
          <div className="card-title">Imported Lists</div>
          <span className="tag tag-blue">{mergedLists.length} lists</span>
        </div>
        {listsLoading ? (
          <div style={{ padding: 24, color: '#CBD5E1', textAlign: 'center' }}>Loading lists…</div>
        ) : mergedLists.length === 0 ? (
          <div style={{ padding: 24, color: '#CBD5E1', textAlign: 'center' }}>No lists imported yet. Use the Import tool to add contacts.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>List Name</th>
                <th>Import Date</th>
                <th>Total Contacts</th>
                <th>Clean</th>
                <th>Eliminated</th>
                <th>Status</th>
                <th>Pass Rate</th>
                <th style={{ width: 44 }}></th>
              </tr>
            </thead>
            <tbody>
              {mergedLists.map((l, i) => (
                <tr
                  key={i}
                  onClick={() => setActiveListId(activeListId === l.filename ? null : l.filename)}
                  style={{ cursor: 'pointer', background: activeListId === l.filename ? 'rgba(14,165,233,0.08)' : undefined }}
                >
                  <td className="td-name">
                    <span style={{ color: '#0EA5E9', fontWeight: 600 }}>{l.filename}</span>
                  </td>
                  <td style={{ color: '#CBD5E1' }}>{l._chOnly ? '—' : fmtDate(l.created_at)}</td>
                  <td style={{ fontWeight: 600, color: '#F1F5F9' }}>{(l.total_contacts || 0).toLocaleString()}</td>
                  <td style={{ color: '#10b981', fontWeight: 600 }}>{l._chOnly ? '—' : (l.clean_contacts || 0).toLocaleString()}</td>
                  <td style={{ color: '#f87171', fontWeight: 600 }}>{l._chOnly ? '—' : (l.eliminated_contacts || 0).toLocaleString()}</td>
                  <td>
                    {l._chOnly && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>Active</span>}
                    {!l._chOnly && l.cleaning_status === 'complete' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>Complete</span>}
                    {!l._chOnly && l.cleaning_status === 'cleaning' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: 'rgba(14,165,233,0.15)', color: '#0EA5E9', animation: 'pulse 1.5s ease-in-out infinite' }}>Cleaning…</span>}
                    {!l._chOnly && (!l.cleaning_status || l.cleaning_status === 'pending') && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: 'rgba(100,116,139,0.15)', color: '#94a3b8' }}>Pending</span>}
                  </td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)',
                      background: l._chOnly ? 'rgba(100,116,139,0.15)' : parseFloat(passRate(l)) >= 70 ? 'rgba(16,185,129,0.15)' : 'rgba(248,113,113,0.15)',
                      color: l._chOnly ? '#94a3b8' : parseFloat(passRate(l)) >= 70 ? '#10b981' : '#f87171',
                    }}>{l._chOnly ? '—' : passRate(l)}</span>
                  </td>
                  <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => setDeleteConfirm(l)}
                      title="Delete list"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: '#64748B', lineHeight: 1 }}
                      onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
                      onMouseLeave={e => e.currentTarget.style.color = '#64748B'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {activeListId && (
        <div className="table-card">
          <div className="card-header" style={{ padding: "18px 20px 14px" }}>
            <div className="card-title">Contacts in "{activeListId}"</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="tag tag-blue">{listContactsLoading ? '…' : listContacts.length + ' shown'}</span>
              <span style={{ fontSize: 11, color: '#CBD5E1', cursor: 'pointer' }} onClick={() => setActiveListId(null)}>✕ close</span>
            </div>
          </div>
          {listContactsLoading ? (
            <div style={{ padding: 24, color: '#CBD5E1', textAlign: 'center' }}>Loading contacts…</div>
          ) : listContacts.length === 0 ? (
            <div style={{ padding: 24, color: '#CBD5E1', textAlign: 'center' }}>No contacts found for this list.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Company</th>
                  <th>Industry</th>
                  <th>Score</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {listContacts.map((l, i) => (
                  <tr key={i}>
                    <td className="td-name">
                      <div>{l.contact}</div>
                      <div style={{ fontSize: 11, color: '#CBD5E1' }}>{l.email}</div>
                    </td>
                    <td>{l.company || '—'}</td>
                    <td>{l.industry || '—'}</td>
                    <td>
                      <span style={{
                        fontFamily: 'monospace', fontWeight: 700,
                        color: l.score >= 80 ? '#10b981' : l.score >= 60 ? '#00d4ff' : l.score >= 40 ? '#a78bfa' : '#666'
                      }}>{l.score}</span>
                    </td>
                    <td style={{ color: '#CBD5E1' }}>{[l.city, l.state].filter(Boolean).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1E293B', border: '1px solid #334155', borderRadius: 12, padding: '28px 32px', maxWidth: 360, width: '90%' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#F1F5F9', marginBottom: 16 }}>Delete <span style={{ color: '#0EA5E9' }}>{deleteConfirm.filename}</span> and all its contacts? This cannot be undone.</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid #334155', background: 'transparent', color: '#CBD5E1', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={() => deleteList(deleteConfirm)} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
    )}
    </>
  );
}

function CleaningEngine({ liveStats, onRefreshStats }) {
  const fmt = (n) => (n > 0 ? n.toLocaleString() : "0");
  const pct = (n, d) => d > 0 ? ((n / d) * 100).toFixed(1) + "%" : "0%";
  const t = liveStats.totalLeads;

  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);
  const [enrichError, setEnrichError] = useState('');
  const [listStats, setListStats] = useState([]);

  const fetchListStats = () => {
    fetch('/api/cleaning/list-stats', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => setListStats(d.lists || []))
      .catch(() => {});
  };

  useEffect(() => { fetchListStats(); }, []);

  useEffect(() => { if (onRefreshStats) fetchListStats(); }, [onRefreshStats]);

  const runEnrich = async () => {
    setEnriching(true);
    setEnrichResult(null);
    setEnrichError('');
    try {
      const r = await fetch('https://tetrus.ai/api/enrich', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchSize: 2000 }),
      });
      const d = await r.json();
      if (!r.ok) { setEnrichError(d.error || 'Enrichment failed'); setEnriching(false); return; }
      setEnrichResult(d);
      if (onRefreshStats) onRefreshStats();
    } catch (e) {
      setEnrichError('Network error: ' + e.message);
    }
    setEnriching(false);
  };

  return (
    <>
      <div className="stat-grid">
        {[
          { label: "Total Processed", value: fmt(t), sub: "In database", color: "#0EA5E9" },
          { label: "Clean", value: fmt(liveStats.cleanLeads), sub: pct(liveStats.cleanLeads, t) + " pass rate", color: "#10B981" },
          { label: "Eliminated", value: fmt(liveStats.eliminated), sub: pct(liveStats.eliminated, t) + " flagged", color: "#EF4444" },
          { label: "Enriched", value: fmt(enrichResult ? enrichResult.totals.totalEnriched : (liveStats.enrichedCount || 0)), sub: "with SIC/NAICS codes", color: "#8B5CF6" },
        ].map((s) => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: '#FFFFFF'}}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            onClick={runEnrich}
            disabled={enriching}
            style={{ minWidth: 140 }}>
            {enriching ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                Enriching...
              </span>
            ) : '⚡ Enrich Leads'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>
            Fills missing names from email patterns · matches SIC/NAICS codes from company names
          </span>
        </div>

        {enrichError && (
          <div className="alert alert-yellow" style={{ marginTop: 14 }}>
            <span>⚠</span><span>{enrichError}</span>
          </div>
        )}

        {enrichResult && (
          <div style={{
            marginTop: 14, padding: '16px 20px', borderRadius: 10,
            background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)',
          }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#10B981', marginBottom: 12 }}>
              ✓ Enrichment complete
            </div>
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
              {[
                { label: 'Names filled', value: enrichResult.enriched.namesFromEmail, color: '#0EA5E9' },
                { label: 'Industries matched', value: enrichResult.enriched.industriesMatched, color: '#8B5CF6' },
                { label: 'SIC/NAICS added', value: enrichResult.enriched.sicNaicsUpdated, color: '#F59E0B' },
                { label: 'Total enriched', value: enrichResult.totals.totalEnriched, color: '#10B981' },
              ].map(m => (
                <div key={m.label}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#FFFFFF', letterSpacing: -0.5 }}>
                    {m.value.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{m.label}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <span>With names: {enrichResult.totals.withNames.toLocaleString()}</span>
              <span>With SIC: {enrichResult.totals.withSic.toLocaleString()}</span>
              <span>With NAICS: {enrichResult.totals.withNaics.toLocaleString()}</span>
              <span>With industry: {enrichResult.totals.withIndustry.toLocaleString()}</span>
              <span style={{ color: 'var(--text2)' }}>of {enrichResult.totals.totalLeads.toLocaleString()} total leads</span>
            </div>
          </div>
        )}
      </div>
      <div className="two-col">
        <div className="table-card">
          <div className="card-header" style={{ padding: "18px 20px 14px" }}>
            <div className="card-title">Validation Breakdown</div>
          </div>
          <table>
            <thead>
              <tr><th>Check Type</th><th>Passed</th><th>Failed</th><th>Rate</th></tr>
            </thead>
            <tbody>
              {[
                { type: "Syntax Validation", pass: fmt(liveStats.syntaxValid), fail: fmt(t - liveStats.syntaxValid), rate: pct(liveStats.syntaxValid, t) },
                { type: "MX Record Check", pass: fmt(liveStats.mxValid), fail: fmt(t - liveStats.mxValid), rate: pct(liveStats.mxValid, t) },
                { type: "Address Validated", pass: fmt(liveStats.sendgridVerified || 0), fail: fmt(t - (liveStats.sendgridVerified || 0)), rate: pct(liveStats.sendgridVerified || 0, t) },
                { type: "Deliverability (Clean)", pass: fmt(liveStats.cleanLeads), fail: fmt(t - liveStats.cleanLeads), rate: pct(liveStats.cleanLeads, t) },
                { type: "Verification", pass: fmt(liveStats.verified), fail: fmt(t - liveStats.verified), rate: pct(liveStats.verified, t) },
                { type: "Bounce Detected", pass: fmt(t - liveStats.bounced), fail: fmt(liveStats.bounced), rate: pct(t - liveStats.bounced, t) },
              ].map((r) => (
                <tr key={r.type}>
                  <td className="td-name">{r.type}</td>
                  <td><span className="tag tag-green">{r.pass}</span></td>
                  <td><span className="tag tag-red">{r.fail}</span></td>
                  <td>{r.rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pipeline-card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <div className="card-title">Validation Pipeline</div>
          </div>
          {[
            { label: "Raw Input", val: t, pct: 100 },
            { label: "Syntax Pass", val: liveStats.syntaxValid, pct: t > 0 ? (liveStats.syntaxValid / t) * 100 : 0 },
            { label: "MX Valid", val: liveStats.mxValid, pct: t > 0 ? (liveStats.mxValid / t) * 100 : 0 },
            { label: "Address Validated", val: liveStats.sendgridVerified || 0, pct: t > 0 ? ((liveStats.sendgridVerified || 0) / t) * 100 : 0 },
            { label: "Deliverable (Clean)", val: liveStats.cleanLeads, pct: t > 0 ? (liveStats.cleanLeads / t) * 100 : 0 },
            { label: "Verified", val: liveStats.verified, pct: t > 0 ? (liveStats.verified / t) * 100 : 0 },
          ].map((s) => (
            <div key={s.label} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 12, color: "#CBD5E1" }}>{s.label}</span>
                <span style={{ fontSize: 12, fontFamily: "var(--mono)", color: "#F1F5F9" }}>{s.val.toLocaleString()}</span>
              </div>
              <div className="pipeline-bar">
                <div className="pipeline-fill" style={{ width: `${s.pct}%`, background: s.pct > 80 ? "#10B981" : s.pct > 50 ? "#F59E0B" : "#EF4444" }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {listStats.length > 0 && (
        <div className="table-card" style={{ marginTop: 24 }}>
          <div className="card-header" style={{ padding: "18px 20px 14px" }}>
            <div className="card-title">Per-List Cleaning Report</div>
            <span className="tag tag-blue">{listStats.length} lists</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>List Name</th>
                <th>Total</th>
                <th>Syntax Pass</th>
                <th>MX Valid</th>
                <th>Clean</th>
                <th>Eliminated</th>
                <th>Pass Rate</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {listStats.map((ls, i) => (
                <tr key={i}>
                  <td className="td-name"><span style={{ color: '#0EA5E9', fontWeight: 600 }}>{ls.list_id}</span></td>
                  <td style={{ fontWeight: 600, color: '#F1F5F9' }}>{(ls.total || 0).toLocaleString()}</td>
                  <td><span className="tag tag-green">{(ls.syntax_valid || 0).toLocaleString()}</span></td>
                  <td><span className="tag tag-green">{(ls.mx_valid || 0).toLocaleString()}</span></td>
                  <td style={{ color: '#10b981', fontWeight: 600 }}>{(ls.clean || 0).toLocaleString()}</td>
                  <td style={{ color: '#f87171', fontWeight: 600 }}>{(ls.eliminated || 0).toLocaleString()}</td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)',
                      background: parseFloat(ls.pass_rate) >= 70 ? 'rgba(16,185,129,0.15)' : 'rgba(248,113,113,0.15)',
                      color: parseFloat(ls.pass_rate) >= 70 ? '#10b981' : '#f87171',
                    }}>{parseFloat(ls.pass_rate || 0).toFixed(1)}%</span>
                  </td>
                  <td>
                    {ls.cleaning_status === 'complete' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>Complete</span>}
                    {ls.cleaning_status === 'cleaning' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: 'rgba(14,165,233,0.15)', color: '#0EA5E9' }}>Cleaning…</span>}
                    {(!ls.cleaning_status || ls.cleaning_status === 'pending') && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: 'rgba(100,116,139,0.15)', color: '#94a3b8' }}>Pending</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function MauticActivityTab() {
  const [summary, setSummary]     = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [contacts, setContacts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState(null);
  const [days, setDays]           = useState(7);

  const load = (d = days) => {
    setLoading(true);
    Promise.all([
      fetch('https://tetrus.ai/api/mautic/summary', { headers: apiHeaders }).then(r => r.json()),
      fetch('https://tetrus.ai/api/mautic/campaigns', { headers: apiHeaders }).then(r => r.json()),
      fetch(`https://tetrus.ai/api/mautic/contacts/active?days=${d}`, { headers: apiHeaders }).then(r => r.json()),
    ]).then(([s, c, a]) => {
      setSummary(s);
      setCampaigns(c.campaigns || []);
      setContacts(a.contacts || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const fmt = n => Number(n || 0).toLocaleString();
  const fmtDate = dt => dt ? new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)' }}>Loading Campaign Automation data...</div>
  );

  return (
    <>
      {/* Summary stat row */}
      <div className="stat-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Email Sequences',   value: fmt(summary?.campaigns),        sub: 'email sequences',          color: '#0EA5E9' },
          { label: 'Total Contacts',     value: fmt(summary?.contacts),          sub: 'in Campaign Automation',            color: '#8B5CF6' },
          { label: 'Emails Sent',        value: fmt(summary?.emailsSent),        sub: 'all time',                 color: '#10B981' },
          { label: 'Active (7d)',         value: fmt(summary?.activeContacts7d),  sub: 'contacts w/ recent activity', color: '#F59E0B' },
        ].map(st => (
          <div key={st.label} className="stat-card">
            <div className="stat-label">{st.label}</div>
            <div className="stat-value" style={{ color: '#FFFFFF'}}>{st.value}</div>
            <div className="stat-sub">{st.sub}</div>
          </div>
        ))}
      </div>

      {/* Campaign sequences */}
      <div className="table-card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ padding: '18px 20px 14px' }}>
          <div>
            <div className="card-title">Email Sequences</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Campaign Automation sequences with step-by-step performance</div>
          </div>
          <span className="tag tag-blue">{campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}</span>
        </div>

        {campaigns.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            No campaigns found in Campaign Automation. Activate a campaign from Campaign Automation to create one.
          </div>
        ) : (
          <div style={{ padding: '0 0 8px' }}>
            {campaigns.map(camp => {
              const isExp = expanded === camp.id;
              return (
                <div key={camp.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  {/* Campaign header row */}
                  <div
                    onClick={() => setExpanded(isExp ? null : camp.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {/* Status dot */}
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: camp.status === 'active' ? '#10b981' : '#CBD5E1',
                    }} />

                    {/* Name */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {camp.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                        {camp.emailCount} email{camp.emailCount !== 1 ? 's' : ''} · {fmt(camp.contactCount)} contacts
                      </div>
                    </div>

                    {/* Stats */}
                    <div style={{ display: 'flex', gap: 24, flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{fmt(camp.totalSent)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>sent</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: camp.openRate > 0 ? '#10b981' : 'var(--text3)' }}>{camp.openRate}%</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>open rate</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: camp.clickRate > 0 ? '#0ea5e9' : 'var(--text3)' }}>{camp.clickRate}%</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>click rate</div>
                      </div>
                      <span className={`tag ${camp.status === 'active' ? 'tag-green' : 'tag-blue'}`} style={{ alignSelf: 'center' }}>
                        {camp.status}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text3)', fontSize: 11, marginLeft: 8 }}>{isExp ? '▲' : '▼'}</div>
                  </div>

                  {/* Email sequence steps */}
                  {isExp && (
                    <div style={{ background: 'var(--surface2)', padding: '12px 20px 16px 44px', borderTop: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', letterSpacing: 1, marginBottom: 10 }}>EMAIL SEQUENCE</div>
                      {camp.sequence.length === 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--text3)' }}>No email steps found.</div>
                      ) : (
                        <div style={{ position: 'relative' }}>
                          {/* vertical line */}
                          <div style={{ position: 'absolute', left: 11, top: 12, bottom: 12, width: 1, background: 'var(--border)' }} />
                          {camp.sequence.map((step, i) => (
                            <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 12, position: 'relative' }}>
                              <div style={{
                                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                background: 'var(--surface)', border: '1px solid var(--accent)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, fontWeight: 700, color: 'var(--accent)', zIndex: 1,
                              }}>{i + 1}</div>
                              <div style={{ flex: 1, paddingTop: 2 }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{step.subject}</span>
                                  <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{step.delay}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                                    <span style={{ color: 'var(--text2)', fontWeight: 600 }}>{fmt(step.sent)}</span> sent
                                  </span>
                                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                                    <span style={{ color: step.opened > 0 ? '#10b981' : 'var(--text3)', fontWeight: 600 }}>{fmt(step.opened)}</span> opened
                                    {step.sent > 0 && <span style={{ color: 'var(--text3)' }}> ({step.openRate}%)</span>}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Active contacts */}
      <div className="table-card">
        <div className="card-header" style={{ padding: '18px 20px 14px' }}>
          <div>
            <div className="card-title">Recently Active Contacts</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Contacts with activity in Campaign Automation in the last {days} days</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {[7, 14, 30].map(d => (
              <div key={d}
                onClick={() => { setDays(d); load(d); }}
                style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                  background: days === d ? 'var(--accent)' : 'var(--surface2)',
                  color: days === d ? '#fff' : 'var(--text3)',
                  border: `1px solid ${days === d ? 'var(--accent)' : 'var(--border2)'}`,
                }}>
                {d}d
              </div>
            ))}
          </div>
        </div>

        {contacts.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            No contacts with activity in the last {days} days.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Contact</th>
                <th>Company</th>
                <th>Points</th>
                <th>Emails Received</th>
                <th>Opened</th>
                <th>Open Rate</th>
                <th>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.id}>
                  <td>
                    <div className="td-name">{c.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>{c.email}</div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text3)' }}>{c.company || '—'}</td>
                  <td>
                    <span className={`tag ${c.points >= 61 ? 'tag-red' : c.points >= 31 ? 'tag-yellow' : 'tag-blue'}`}>
                      {c.points}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{c.emailsReceived}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{c.emailsOpened}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--border)', maxWidth: 60 }}>
                        <div style={{
                          height: '100%', borderRadius: 2,
                          width: `${Math.min(parseFloat(c.openRate), 100)}%`,
                          background: parseFloat(c.openRate) > 30 ? '#10b981' : parseFloat(c.openRate) > 10 ? '#f59e0b' : '#CBD5E1',
                        }} />
                      </div>
                      <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{c.openRate}%</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text3)' }}>{fmtDate(c.lastActive)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}


function EngagementIntelligence() {
  const [tab, setTab] = useState('engagement');
  const [eng, setEng] = useState(null);
  useEffect(() => {
    fetch('https://tetrus.ai/api/engagement?_t=' + Date.now(), { headers: apiHeaders })
      .then(r => r.json())
      .then(d => setEng(d))
      .catch(() => {});
  }, []);

  const fmt = (n) => n != null ? Number(n).toLocaleString() : '—';
  const s = eng?.summary || {};
  const d = eng?.distribution || {};
  const signals = eng?.detectionSignals || [];

  const humanRate = Number(s.humanRate) || 0;
  const humanEvents = parseInt(s.humanEvents) || 0;
  const botRate = Number(s.botRate) || 0;
  const noActivity = Number(s.noActivityPct) || 0;
  const suspiciousRate = Number(s.suspiciousRate) || 0;

  const tabStyle = active => ({
    padding: '10px 22px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
    color: active ? 'var(--accent2)' : 'var(--text3)',
    transition: 'all 0.15s',
  });

  if (tab === 'mautic') {
    return (
      <>
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
          <div style={tabStyle(false)} onClick={() => setTab('engagement')}>Engagement Intelligence</div>
          <div style={tabStyle(true)} onClick={() => setTab('mautic')}>Campaign Automation Activity</div>
        </div>
        <MauticActivityTab />
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        <div style={tabStyle(true)} onClick={() => setTab('engagement')}>Engagement Intelligence</div>
        <div style={tabStyle(false)} onClick={() => setTab('mautic')}>Campaign Automation Activity</div>
      </div>
      <div className="stat-grid">
        {[
          { label: "Human Engagement", value: eng ? humanRate.toFixed(1) + "%" : "—", sub: "of total events", color: "#10B981" },
          { label: "Human Events", value: eng ? fmt(humanEvents) : "—", sub: humanEvents > 0 ? humanEvents + " confirmed opens/clicks" : "No events yet", color: "#10B981" },
          { label: "Bot Activity", value: eng ? botRate.toFixed(1) + "%" : "—", sub: botRate > 30 ? "Above threshold" : "Within threshold", color: "#EF4444" },
          { label: "Avg. Engagement Score", value: eng ? String(Number(s.avgScore) || 0) : "—", sub: "Out of 100", color: "#0EA5E9" },
        ].map((st) => (
          <div key={st.label} className="stat-card">
            <div className="stat-label">{st.label}</div>
            <div className="stat-value" style={{ color: '#FFFFFF'}}>{st.value}</div>
            <div className="stat-sub">{st.sub}</div>
          </div>
        ))}
      </div>

      <div className="two-col">
        <div className="pipeline-card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <div className="card-title">Human vs Bot Split</div>
            <span className="tag tag-blue">{eng ? fmt(s.totalEvents) + " events" : "Loading..."}</span>
          </div>
          <div className="eng-split">
            <div className="eng-block">
              <div className="eng-pct" style={{ color: "#10B981" }}>{eng ? humanRate + "%" : "—"}</div>
              <div className="eng-label">Confirmed Human</div>
              <div className="eng-bar" style={{ background: "#10B981" }} />
            </div>
            <div style={{ width: 1, height: 60, background: "var(--border)" }} />
            <div className="eng-block">
              <div className="eng-pct" style={{ color: "#EF4444" }}>{eng ? botRate + "%" : "—"}</div>
              <div className="eng-label">Bot / Automated</div>
              <div className="eng-bar" style={{ background: "#EF4444" }} />
            </div>
            <div style={{ width: 1, height: 60, background: "var(--border)" }} />
            <div className="eng-block">
              <div className="eng-pct" style={{ color: "#F59E0B" }}>{eng ? suspiciousRate + "%" : "—"}</div>
              <div className="eng-label">Suspicious</div>
              <div className="eng-bar" style={{ background: "#F59E0B" }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            {eng && s.botEvents > 0 && <div className="alert alert-red">{"Bot activity detected \u2014 " + fmt(s.botEvents) + " bot events across " + fmt(s.uniqueLeadsEngaged) + " engaged leads"}</div>}
            {eng && s.suspiciousEvents > 0 && <div className="alert alert-yellow">{fmt(s.suspiciousEvents) + " events with ambiguous signals. Recommend manual review."}</div>}
            {eng && <div className="alert alert-green">{fmt(s.humanEvents) + " confirmed human engagement events from " + fmt(s.uniqueLeadsEngaged) + " unique leads"}</div>}
          </div>
        </div>

        <div className="table-card">
          <div className="card-header" style={{ padding: "18px 20px 14px" }}>
            <div className="card-title">Engagement Score Distribution</div>
          </div>
          <table>
            <thead>
              <tr><th>Score Range</th><th>Count</th><th>Classification</th><th>Action</th></tr>
            </thead>
            <tbody>
              {[
                { range: "80\u2013100", count: fmt(d.score_80_100), cls: "Human \u2014 High", act: "Send to Sales", color: "tag-green" },
                { range: "60\u201379", count: fmt(d.score_60_79), cls: "Human \u2014 Medium", act: "Continue nurture", color: "tag-blue" },
                { range: "40\u201359", count: fmt(d.score_40_59), cls: "Uncertain", act: "Re-engage", color: "tag-yellow" },
                { range: "20\u201339", count: fmt(d.score_20_39), cls: "Likely Bot", act: "Flag", color: "tag-red" },
                { range: "0\u201319", count: fmt(d.score_0_19), cls: "Bot / Scanner", act: "Remove", color: "tag-red" },
              ].map((r) => (
                <tr key={r.range}>
                  <td className="td-name" style={{ fontFamily: "var(--mono)" }}>{r.range}</td>
                  <td>{r.count}</td>
                  <td><span className={`tag ${r.color}`}>{r.cls}</span></td>
                  <td style={{ color: "#CBD5E1", fontSize: 13 }}>{r.act}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pipeline-card">
        <div className="card-header">
          <div className="card-title">Detection Signal Analysis</div>
          <div className="card-sub">INPUTS USED TO CALCULATE HUMAN ENGAGEMENT SCORE</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {(signals.length > 0 ? signals : [
            { signal: "Response Timing", desc: "Instant opens (<2s) classified as bot", score: 0 },
            { signal: "IP Ownership", desc: "Cloud/datacenter IPs vs residential", score: 0 },
            { signal: "Click Pattern", desc: "Multi-link bursts flagged as scanner", score: 0 },
            { signal: "User Agent", desc: "Security tools vs browser signatures", score: 0 },
            { signal: "Session Behavior", desc: "Multi-page engagement scoring", score: 0 },
            { signal: "Reply Detection", desc: "Highest confidence human signal", score: 0 },
          ]).map((sg) => (
            <div key={sg.signal} style={{ padding: "14px 16px", background: "var(--surface2)", borderRadius: 8, border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{sg.signal}</div>
              <div style={{ fontSize: 11, color: "#CBD5E1", marginBottom: 10 }}>{sg.desc}</div>
              <ScoreBar value={sg.score} color={sg.score > 85 ? "#10B981" : sg.score > 70 ? "#F59E0B" : "#EF4444"} />
            </div>
          ))}
        </div>
      </div>

      <div className="table-card">
        <div className="card-header" style={{ padding: '18px 20px 14px' }}>
          <div className="card-title">Recently Active Contacts</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Real opens and clicks from SendGrid — sorted by most opens</div>
        </div>
        {(!eng || !eng.recentlyActiveContacts || eng.recentlyActiveContacts.length === 0) ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No open or click events yet. Send a campaign to see real engagement.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Email</th><th>Emails Received</th><th>Opened</th><th>Clicked</th><th>Open Rate</th><th>Last Active</th></tr>
            </thead>
            <tbody>
              {(eng.recentlyActiveContacts || []).map((c, i) => (
                <tr key={i}>
                  <td className="td-name" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{c.email}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{c.emails_received}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 13, color: c.opened > 0 ? '#10b981' : 'var(--text3)', fontWeight: c.opened > 0 ? 600 : 400 }}>{Math.min(c.opened, c.emails_received)}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 13, color: c.clicked > 0 ? '#0ea5e9' : 'var(--text3)', fontWeight: c.clicked > 0 ? 600 : 400 }}>{c.clicked}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--border)', maxWidth: 60 }}>
                        <div style={{ height: '100%', borderRadius: 2, width: Math.min(parseFloat(c.open_rate), 100) + '%', background: parseFloat(c.open_rate) > 30 ? '#10b981' : parseFloat(c.open_rate) > 10 ? '#f59e0b' : '#CBD5E1' }} />
                      </div>
                      <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{Math.min(100, parseFloat(c.open_rate))}%</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text3)' }}>{c.last_active && c.last_active !== '1970-01-01 00:00:00' ? new Date(c.last_active).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function CreateCampaignModal({ onClose, onCreated }) {
  const [name, setName]                       = useState("");
  const [industry, setIndustry]               = useState("");
  const [minScore, setMinScore]               = useState(50);
  const [mauticCampaignId, setMauticCampaignId] = useState("");
  const [listId, setListId]                   = useState("");
  const [fromAddress, setFromAddress]         = useState("outreach@tetrusemail1.com");
  const [startDate, setStartDate]             = useState(new Date().toISOString().slice(0, 10));
  const [dailyLimit, setDailyLimit]           = useState(50);
  const [description, setDescription]         = useState("");
  const [campaignMode, setCampaignMode]         = useState("live");
  const [industries, setIndustries]           = useState([]);
  const [mauticCampaigns, setMauticCampaigns]   = useState([]);
  const [mauticLoading, setMauticLoading]       = useState(true);
  const [mauticLoadError, setMauticLoadError]   = useState(false);
  const [importLists, setImportLists]           = useState([]);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState("");

  useEffect(() => {
    fetch("https://tetrus.ai/api/industries", { headers: apiHeaders })
      .then((r) => r.json()).then((d) => setIndustries((d.industries || []).sort((a, b) => a.industry.localeCompare(b.industry)))).catch(() => {});
    loadMauticCampaigns();
    fetch("https://tetrus.ai/api/import-lists", { headers: apiHeaders })
      .then((r) => r.json()).then((d) => setImportLists(d.lists || [])).catch(() => {});
  }, []);

  const loadMauticCampaigns = () => {
    setMauticLoading(true);
    setMauticLoadError(false);
    fetch("https://tetrus.ai/api/mautic/campaigns", { headers: apiHeaders })
      .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then((d) => {
        setMauticCampaigns(d.campaigns || []);
        setMauticLoading(false);
      })
      .catch((e) => {
        console.error('[CreateCampaignModal] Failed to load sequences:', e.message);
        setMauticLoading(false);
        setMauticLoadError(true);
      });
  };

  const handleCreate = async () => {
    if (!name.trim()) { setError("Campaign name is required."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch("https://tetrus.ai/api/campaigns", {
        method: "POST",
        headers: { ...apiHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          status: "active",
          target_industry: industry,
          target_min_score: minScore,
          mautic_campaign_id: mauticCampaignId ? parseInt(mauticCampaignId) : 0,
          list_id: listId,
          from_address: fromAddress,
          start_date: startDate,
          daily_limit: dailyLimit,
          description: description.trim(),
          sequence_delay_days: campaignMode === "test" ? 0.002 : 3,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Failed to create campaign."); setLoading(false); return; }
      onCreated(data.campaign);
    } catch (err) {
      setError("Unable to connect to server.");
      setLoading(false);
    }
  };

  const seen = new Set();
  const uniqueIndustries = industries.filter((i) => {
    const key = i.industry.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const inputStyle = { width: "100%", padding: "8px 10px", background: "var(--surface2)", border: "1px solid var(--border2)", color: "var(--text)", borderRadius: 6, fontSize: 13, boxSizing: "border-box", fontFamily: "inherit" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, color: "#CBD5E1", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 5 };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxHeight: "88vh", overflowY: "auto", width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Launch New Campaign</div>
        <div className="modal-sub">Configure targeting, sequence, and send settings</div>
        {error && <div className="login-error">{error}</div>}

        <div className="modal-field">
          <label className="modal-label">Campaign Name *</label>
          <input className="modal-input" placeholder="e.g. US Healthcare Outreach Q2" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>

        <div className="modal-field">
          <label style={labelStyle}>Email Sequence</label>
          {mauticLoading ? (
            <div style={{ fontSize: 12, color: "#94A3B8", padding: "8px 0" }}>Loading sequences...</div>
          ) : mauticLoadError ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
              <span style={{ fontSize: 12, color: "#EF4444" }}>Failed to load sequences.</span>
              <button type="button" onClick={loadMauticCampaigns}
                style={{ fontSize: 11, color: "#0EA5E9", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                Retry
              </button>
            </div>
          ) : (
            <>
              <select className="modal-select" value={mauticCampaignId} onChange={(e) => setMauticCampaignId(e.target.value)}>
                <option value="">— No sequence selected —</option>
                {mauticCampaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.contactCount ? " (" + Number(c.contactCount).toLocaleString() + " contacts)" : ""}
                  </option>
                ))}
              </select>
              {mauticCampaigns.length === 0 && (
                <div style={{ fontSize: 11, color: "#F59E0B", marginTop: 4 }}>No email sequences found yet — create one in Campaign Builder first, then return here.</div>
              )}
            </>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div className="modal-field" style={{ marginBottom: 0 }}>
            <label className="modal-label">Target Industry</label>
            <select className="modal-select" value={industry} onChange={(e) => setIndustry(e.target.value)}>
              <option value="">All Industries</option>
              {uniqueIndustries.map((i) => (
                <option key={i.industry} value={i.industry}>{i.industry} ({Number(i.cnt).toLocaleString()})</option>
              ))}
            </select>
          </div>
          <div className="modal-field" style={{ marginBottom: 0 }}>
            <label style={labelStyle}>Target List</label>
            <select className="modal-select" value={listId} onChange={(e) => setListId(e.target.value)}>
              <option value="">All Clean Leads</option>
              {importLists.map((l) => (
                <option key={l.id} value={String(l.id)}>
                  {l.filename.replace(/\.[^.]+$/, "")} ({Number(l.imported).toLocaleString()} leads)
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="modal-field">
          <label className="modal-label">Minimum Human Score</label>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <input type="range" min="0" max="90" step="10" value={minScore} onChange={(e) => setMinScore(parseInt(e.target.value))}
              style={{ flex: 1, accentColor: "var(--accent)" }} />
            <span style={{ fontFamily: "var(--mono)", fontSize: 14, color: "var(--accent2)", minWidth: 30 }}>{minScore}</span>
          </div>
        </div>

        <div className="modal-field">
          <label style={labelStyle}>Campaign Mode</label>
          <div style={{ display: "flex", gap: 0, borderRadius: 6, overflow: "hidden", border: "1px solid var(--border2)" }}>
            {[["live", "Live Mode", "3-day delays"], ["test", "Test Mode", "3-min delays"]].map(([val, label, sub]) => (
              <button
                key={val}
                type="button"
                onClick={() => setCampaignMode(val)}
                style={{
                  flex: 1, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none",
                  background: campaignMode === val ? (val === "test" ? "#F59E0B" : "#10B981") : "var(--surface2)",
                  color: campaignMode === val ? "#000" : "var(--text-muted)",
                  transition: "background 0.15s",
                }}
              >
                {label}<br /><span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>{sub}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="modal-field">
          <label style={labelStyle}>From Address</label>
          <select className="modal-select" value={fromAddress} onChange={(e) => setFromAddress(e.target.value)}>
            <option value="outreach@tetrusemail1.com">outreach@tetrusemail1.com</option>
            <option value="outreach@tetrusemail2.com">outreach@tetrusemail2.com</option>
            <option value="outreach@tetrusemail3.com">outreach@tetrusemail3.com</option>
          </select>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div className="modal-field" style={{ marginBottom: 0 }}>
            <label style={labelStyle}>Start Date</label>
            <input type="date" style={inputStyle} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="modal-field" style={{ marginBottom: 0 }}>
            <label style={labelStyle}>Daily Send Limit (max 150)</label>
            <input type="number" min="1" max="150" style={inputStyle} value={dailyLimit}
              onChange={(e) => setDailyLimit(Math.min(150, Math.max(1, parseInt(e.target.value) || 50)))} />
          </div>
        </div>

        <div className="modal-field">
          <label style={labelStyle}>Description (internal notes)</label>
          <textarea style={{ ...inputStyle, height: 72, resize: "vertical" }}
            placeholder="Optional — notes about this campaign's goal, audience, or strategy"
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={loading}>
            {loading ? "Creating..." : "Launch Campaign"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// COMPETITOR ANALYSIS PANEL
// ============================================================
function CompetitorPanel() {
  const [url, setUrl]         = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [result, setResult]   = useState(null);

  const analyze = async () => {
    if (!url.trim()) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const r = await fetch('https://tetrus.ai/api/competitor-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...apiHeaders },
        body: JSON.stringify({ url: url.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Analysis failed'); }
      else { setResult(d.analysis); }
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  const tagStyle = (color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 4,
    fontSize: 11, fontWeight: 600, background: color + '18',
    border: '1px solid ' + color + '44', color: color,
  });

  const section = (title, color, children) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );

  const pill = (txt, color) => (
    <span key={txt} style={{ display: 'inline-block', margin: '3px 4px 3px 0', padding: '4px 10px', background: color + '14', border: '1px solid ' + color + '40', color, borderRadius: 5, fontSize: 12, fontWeight: 500 }}>{txt}</span>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Input card */}
      <div className="pipeline-card" style={{ marginBottom: 0 }}>
        <div className="card-header">
          <div>
            <div className="card-title">Competitor Intelligence</div>
            <div className="card-sub">Enter a competitor URL. Claude will scrape their site and build a battle card.</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>Competitor Website URL</label>
            <input
              type="text"
              placeholder="e.g. apollo.io or https://zoominfo.com"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !loading && analyze()}
              style={{ width: '100%', padding: '9px 12px', background: 'var(--surface2)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
            />
          </div>
          <button
            onClick={analyze}
            disabled={loading || !url.trim()}
            style={{ padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', background: !url.trim() ? 'var(--surface2)' : 'var(--accent)', border: 'none', color: !url.trim() ? '#CBD5E1' : '#fff', borderRadius: 6, whiteSpace: 'nowrap', opacity: loading ? 0.7 : 1, transition: 'background 0.15s', flexShrink: 0 }}
          >
            {loading ? 'Analyzing...' : 'Analyze \u2192'}
          </button>
        </div>
        {loading && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 8, fontSize: 12, color: '#0EA5E9', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#0EA5E9', animation: 'pulse 1.2s infinite' }} />
            Scraping site and running Claude analysis... this takes 10–20 seconds.
          </div>
        )}
        {error && (
          <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 8, fontSize: 12, color: '#EF4444' }}>{error}</div>
        )}
      </div>

      {/* Results */}
      {result && (
        <>
          {/* Battle card header */}
          <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(14,165,233,0.08))', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Battle Card</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', lineHeight: 1.2, marginBottom: 8 }}>{result.battle_card_headline}</div>
                <div style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.6, maxWidth: 520 }}>{result.positioning_angle}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                <div style={{ fontSize: 11, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '.06em' }}>Competitor</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>{result.company_name}</div>
                {result.tagline && <div style={{ fontSize: 11, color: '#CBD5E1', fontStyle: 'italic', maxWidth: 200 }}>"{result.tagline}"</div>}
                {result.confidence && <span style={tagStyle(result.confidence === 'high' ? '#10b981' : result.confidence === 'medium' ? '#f59e0b' : '#CBD5E1')}>{result.confidence} confidence</span>}
              </div>
            </div>
          </div>

          <div className="two-col" style={{ alignItems: 'start', gap: 16 }}>
            {/* Left col */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="pipeline-card" style={{ marginBottom: 0 }}>
                <div className="card-header"><div className="card-title">Their Profile</div></div>
                {section('Target Market', '#0EA5E9',
                  <p style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.65 }}>{result.target_market}</p>
                )}
                {section('Value Proposition', '#8B5CF6',
                  <p style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.65 }}>{result.value_proposition}</p>
                )}
                {section('Messaging Style', '#F59E0B',
                  <p style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.65 }}>{result.messaging_style}</p>
                )}
                {result.pricing_model && section('Pricing Model', '#CBD5E1',
                  <p style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.65 }}>{result.pricing_model}</p>
                )}
              </div>
              <div className="pipeline-card" style={{ marginBottom: 0 }}>
                <div className="card-header"><div className="card-title">Key Features They Promote</div></div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingTop: 4 }}>
                  {(result.key_features || []).map(f => pill(f, '#0EA5E9'))}
                </div>
              </div>
            </div>

            {/* Right col */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="pipeline-card" style={{ marginBottom: 0, borderColor: 'rgba(239,68,68,0.25)' }}>
                <div className="card-header"><div className="card-title" style={{ color: '#EF4444' }}>Their Weaknesses</div></div>
                {(result.weaknesses || []).map((w, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
                    <span style={{ color: '#EF4444', fontSize: 14, marginTop: 1, flexShrink: 0 }}>&#10005;</span>
                    <span style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.55 }}>{w}</span>
                  </div>
                ))}
              </div>
              <div className="pipeline-card" style={{ marginBottom: 0, borderColor: 'rgba(16,185,129,0.25)' }}>
                <div className="card-header"><div className="card-title" style={{ color: '#10B981' }}>Why Tetrus Wins</div></div>
                {(result.tetrus_advantages || []).map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
                    <span style={{ color: '#10B981', fontSize: 14, marginTop: 1, flexShrink: 0 }}>&#10003;</span>
                    <span style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.55 }}>{a}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding: '14px 16px', background: 'rgba(14,165,233,0.05)', border: '1px solid rgba(14,165,233,0.15)', borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0EA5E9', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>Positioning Script</div>
                <p style={{ fontSize: 12, color: '#CBD5E1', lineHeight: 1.7, fontStyle: 'italic' }}>"{result.positioning_angle}"</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ResearchPanel() {
  const [product, setProduct] = useState("");
  const [tab, setTab] = useState('general');
  const [segment, setSegment] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    fetch("https://tetrus.ai/api/research", { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setHistory(d.research || []); setHistoryLoading(false); })
      .catch(() => setHistoryLoading(false));
  }, []);

  const runResearch = async () => {
    if (!product.trim() || !segment.trim()) { setError("Product and segment are required."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await fetch("https://tetrus.ai/api/research", {
        method: "POST",
        headers: { ...apiHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ product: product.trim(), segment: segment.trim() }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) { setError(d.error || "Research failed."); setLoading(false); return; }
      setResult(d);
      setHistory(prev => [{ ...d, created_at: new Date().toISOString() }, ...prev]);
    } catch { setError("Network error."); }
    setLoading(false);
  };

  const inputStyle = {
    width: "100%", padding: "8px 12px", borderRadius: 6,
    border: "1px solid var(--border2)", background: "var(--surface2)",
    color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none",
  };


  return (
    <>
      <div className="two-col" style={{ alignItems: "start" }}>
        <div className="filter-panel">
          <div style={{ marginBottom: 16 }}>
            <div className="card-title">Industry Research Agent</div>
            <div className="card-sub" style={{ marginTop: 4 }}>AI-POWERED SEGMENT ANALYSIS + EMAIL GENERATION</div>
          </div>
          <div className="filter-group">
            <div className="filter-label">Your Product / Service</div>
            <input type="text" value={product} onChange={e => setProduct(e.target.value)}
              placeholder="e.g. Marketing automation platform for mid-market"
              style={inputStyle} />
          </div>
          <div className="filter-group">
            <div className="filter-label">Buyer Segment</div>
            <input type="text" value={segment} onChange={e => setSegment(e.target.value)}
              placeholder="e.g. VP Marketing at Financial Services companies"
              onKeyDown={e => { if (e.key === "Enter") runResearch(); }}
              style={inputStyle} />
          </div>
          {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 8 }}>{error}</div>}
          <button className="btn btn-primary" style={{ width: "100%", marginTop: 8 }}
            onClick={runResearch} disabled={loading}>
            {loading ? "Generating with TETRUS AI..." : "Generate Research + Emails"}
          </button>

          {!historyLoading && history.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div className="filter-label">Previous Research</div>
              {history.slice(0, 5).map((h, i) => (
                <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)", cursor: "pointer" }}
                  onClick={() => setResult(h)}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text)" }}>{h.segment}</div>
                  <div style={{ fontSize: 10, color: "var(--text3)" }}>{h.product} • {new Date(h.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          {result ? (
            <>
              <div className="pipeline-card" style={{ marginBottom: 14 }}>
                <div className="card-header"><div className="card-title">Industry Terminology</div></div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 0 4px" }}>
                  {(result.terminology || []).map((t, i) => (
                    <span key={i} className="tag tag-blue">{t}</span>
                  ))}
                </div>
              </div>

              <div className="pipeline-card" style={{ marginBottom: 14 }}>
                <div className="card-header"><div className="card-title">Pain Points</div></div>
                {(result.pain_points || []).map((p, i) => (
                  <div key={i} className="metric-row">
                    <span style={{ fontSize: 12, color: "var(--text2)" }}>{p}</span>
                  </div>
                ))}
              </div>

              <div className="pipeline-card" style={{ marginBottom: 14 }}>
                <div className="card-header"><div className="card-title">Subject Lines</div><span className="tag tag-green">{(result.subject_lines || []).length} generated</span></div>
                {(result.subject_lines || []).map((s, i) => (
                  <div key={i} className="metric-row">
                    <span style={{ fontSize: 13, fontWeight: 500, color: "var(--accent2)" }}>{s}</span>
                  </div>
                ))}
              </div>

              <div className="pipeline-card" style={{ marginBottom: 0 }}>
                <div className="card-header"><div className="card-title">Email Body Variants</div><span className="tag tag-green">{(result.email_bodies || []).length} variants</span></div>
                {(result.email_bodies || []).map((b, i) => (
                  <div key={i} style={{ padding: "14px 0", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 10, fontFamily: "var(--mono)", color: "var(--text3)", marginBottom: 6 }}>VARIANT {i + 1}</div>
                    <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{b}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="pipeline-card" style={{ marginBottom: 0 }}>
              <div style={{ textAlign: "center", padding: 40 }}>
                <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.2 }}>◈</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text2)", marginBottom: 6 }}>No research generated yet</div>
                <div style={{ fontSize: 12, color: "var(--text3)", maxWidth: 300, margin: "0 auto" }}>
                  Enter your product and buyer segment, then click Generate to get AI-powered industry research, subject lines, and email variants.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ABTestingPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [variants, setVariants] = useState([]);
  const [winnerId, setWinnerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variantLoading, setVariantLoading] = useState(false);

  useEffect(() => {
    fetch("https://tetrus.ai/api/campaigns", { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setCampaigns(d.campaigns || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const loadVariants = async (cid) => {
    setSelectedCampaign(cid);
    setVariantLoading(true);
    try {
      const r = await fetch("https://tetrus.ai/api/ab-tests/" + cid, { headers: apiHeaders });
      const d = await r.json();
      setVariants(d.variants || []);
      setWinnerId(d.winner_id || null);
    } catch { setVariants([]); }
    setVariantLoading(false);
  };

  useEffect(() => {
    if (campaigns.length > 0 && !selectedCampaign) {
      loadVariants(campaigns[0].id);
    }
  }, [campaigns]);

  const barWidth = (val, max) => max > 0 ? Math.max(4, (val / max) * 100) : 4;

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text3)" }}>Loading...</div>;

  const maxOpen = Math.max(...variants.map(v => v.open_rate), 1);
  const maxClick = Math.max(...variants.map(v => v.click_rate), 1);

  return (
    <div className="two-col" style={{ alignItems: "start" }}>
      <div className="filter-panel">
        <div style={{ marginBottom: 16 }}>
          <div className="card-title">A/B Test Results</div>
          <div className="card-sub" style={{ marginTop: 4 }}>SELECT A CAMPAIGN TO VIEW VARIANT PERFORMANCE</div>
        </div>
        <div className="filter-group">
          <div className="filter-label">Campaign</div>
          <select style={{
            width: "100%", padding: "8px 12px", borderRadius: 6,
            border: "1px solid var(--border2)", background: "var(--surface2)",
            color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none",
          }} value={selectedCampaign || ""} onChange={e => loadVariants(parseInt(e.target.value))}>
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        {variants.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="filter-label" style={{ marginBottom: 8 }}>Quick Stats</div>
            {variants.map(v => (
              <div key={v.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
                    Variant {v.variant_name}
                    {winnerId === v.id && <span style={{
                      marginLeft: 8, fontSize: 10, padding: "2px 8px", borderRadius: 10,
                      background: "linear-gradient(135deg, #F59E0B, #EF4444)",
                      color: "#fff", fontWeight: 700, letterSpacing: 0.5,
                    }}>WINNER</span>}
                  </span>
                  <span style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)" }}>{v.sends} sends</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        {variantLoading ? (
          <div style={{ textAlign: "center", padding: 60, color: "var(--text3)" }}>Loading variants...</div>
        ) : variants.length === 0 ? (
          <div className="pipeline-card" style={{ marginBottom: 0 }}>
            <div style={{ textAlign: "center", padding: 40 }}>
              <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.2 }}>&#9878;</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text2)", marginBottom: 6 }}>No A/B tests for this campaign</div>
              <div style={{ fontSize: 12, color: "var(--text3)", maxWidth: 300, margin: "0 auto" }}>
                Activate a campaign with Industry Research results to auto-generate A/B test variants.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: variants.length <= 2 ? "repeat(" + variants.length + ", 1fr)" : "repeat(2, 1fr)", gap: 14, marginBottom: 14 }}>
              {variants.map(v => (
                <div key={v.id} className="pipeline-card" style={{
                  marginBottom: 0,
                  border: winnerId === v.id ? "1.5px solid #F59E0B" : undefined,
                  boxShadow: winnerId === v.id ? "0 0 20px rgba(245,158,11,0.12)" : undefined,
                }}>
                  <div className="card-header" style={{ paddingBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div className="card-title">Variant {v.variant_name}</div>
                      {winnerId === v.id && <span style={{
                        fontSize: 10, padding: "2px 10px", borderRadius: 10,
                        background: "linear-gradient(135deg, #F59E0B, #EF4444)",
                        color: "#fff", fontWeight: 700, letterSpacing: 0.5,
                      }}>WINNER</span>}
                    </div>
                  </div>

                  <div style={{ padding: "4px 0 12px" }}>
                    <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", marginBottom: 4 }}>SUBJECT LINE</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--accent2)", lineHeight: 1.5 }}>{v.subject_line}</div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                    <div style={{ background: "var(--surface2)", borderRadius: 8, padding: 12, textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#0EA5E9", fontFamily: "var(--mono)" }}>{v.open_rate}%</div>
                      <div style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)", marginTop: 2 }}>OPEN RATE</div>
                    </div>
                    <div style={{ background: "var(--surface2)", borderRadius: 8, padding: 12, textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#8B5CF6", fontFamily: "var(--mono)" }}>{v.click_rate}%</div>
                      <div style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)", marginTop: 2 }}>CLICK RATE</div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>
                      <span>Open Rate</span><span>{v.open_rate}%</span>
                    </div>
                    <div style={{ height: 6, background: "var(--surface2)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: barWidth(v.open_rate, maxOpen) + "%", height: "100%", background: "linear-gradient(90deg, #0EA5E9, #38BDF8)", borderRadius: 3, transition: "width 0.5s" }} />
                    </div>
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>
                      <span>Click Rate</span><span>{v.click_rate}%</span>
                    </div>
                    <div style={{ height: 6, background: "var(--surface2)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: barWidth(v.click_rate, maxClick) + "%", height: "100%", background: "linear-gradient(90deg, #8B5CF6, #A78BFA)", borderRadius: 3, transition: "width 0.5s" }} />
                    </div>
                  </div>

                  <div className="metric-row" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 4, width: "100%", textAlign: "center" }}>
                      <div><div style={{ fontSize: 14, fontWeight: 600, fontFamily: "var(--mono)", color: "var(--text)" }}>{v.sends}</div><div style={{ fontSize: 9, color: "var(--text3)" }}>SENDS</div></div>
                      <div><div style={{ fontSize: 14, fontWeight: 600, fontFamily: "var(--mono)", color: "#0EA5E9" }}>{v.opens}</div><div style={{ fontSize: 9, color: "var(--text3)" }}>OPENS</div></div>
                      <div><div style={{ fontSize: 14, fontWeight: 600, fontFamily: "var(--mono)", color: "#8B5CF6" }}>{v.clicks}</div><div style={{ fontSize: 9, color: "var(--text3)" }}>CLICKS</div></div>
                      <div><div style={{ fontSize: 14, fontWeight: 600, fontFamily: "var(--mono)", color: "#10B981" }}>{v.conversions}</div><div style={{ fontSize: 9, color: "var(--text3)" }}>CONV</div></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {variants.length >= 2 && variants.some(v => v.sends > 0) && (
              <div className="pipeline-card" style={{ marginBottom: 0 }}>
                <div className="card-header"><div className="card-title">Performance Comparison</div></div>
                <table>
                  <thead>
                    <tr><th>Variant</th><th>Subject Line</th><th>Sends</th><th>Open Rate</th><th>Click Rate</th><th>Conv Rate</th><th>Result</th></tr>
                  </thead>
                  <tbody>
                    {variants.map(v => (
                      <tr key={v.id}>
                        <td style={{ fontWeight: 600 }}>{v.variant_name}</td>
                        <td style={{ fontSize: 13, color: "var(--text2)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.subject_line}</td>
                        <td style={{ fontFamily: "var(--mono)" }}>{v.sends}</td>
                        <td style={{ fontFamily: "var(--mono)", color: "#0EA5E9", fontWeight: 600 }}>{v.open_rate}%</td>
                        <td style={{ fontFamily: "var(--mono)", color: "#8B5CF6", fontWeight: 600 }}>{v.click_rate}%</td>
                        <td style={{ fontFamily: "var(--mono)", color: "#10B981", fontWeight: 600 }}>{v.conv_rate}%</td>
                        <td>
                          {winnerId === v.id
                            ? <span style={{ fontSize: 10, padding: "2px 10px", borderRadius: 10, background: "linear-gradient(135deg, #F59E0B, #EF4444)", color: "#fff", fontWeight: 700 }}>WINNER</span>
                            : <span className="tag tag-blue" style={{ fontSize: 10 }}>Testing</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function NewSequenceModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [emails, setEmails] = useState([{ subject: '', body: '', delayDays: 0 }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const addEmail = () => setEmails(prev => [...prev, { subject: '', body: '', delayDays: 0 }]);
  const removeEmail = (i) => setEmails(prev => prev.filter((_, idx) => idx !== i));
  const updateEmail = (i, field, val) => setEmails(prev => prev.map((e, idx) => idx === i ? { ...e, [field]: val } : e));

  const save = async () => {
    setError('');
    if (!name.trim()) { setError('Sequence name is required.'); return; }
    if (emails.some(e => !e.subject.trim())) { setError('All emails must have a subject line.'); return; }
    setLoading(true);
    try {
      const res = await fetch('https://tetrus.ai/api/mautic/sequences', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), emails }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to create sequence.'); setLoading(false); return; }
      onCreated(data);
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">New Email Sequence</div>
          <button className="modal-close" onClick={onClose}>&#x2715;</button>
        </div>
        {error && <div className="alert alert-red" style={{ margin: '0 0 16px' }}><span>&#x2717;</span><span>{error}</span></div>}
        <div className="modal-body">
          <label className="modal-label">Sequence Name *</label>
          <input className="modal-input" placeholder="e.g. SaaS Founders Outreach" value={name} onChange={e => setName(e.target.value)} />
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Emails ({emails.length})</div>
              <button className="btn btn-primary" style={{ fontSize: 11, padding: '4px 12px' }} onClick={addEmail}>+ Add Email</button>
            </div>
            {emails.map((em, i) => (
              <div key={i} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', fontFamily: 'var(--mono)' }}>EMAIL {i + 1}</div>
                  {emails.length > 1 && (
                    <button className="btn" style={{ fontSize: 10, padding: '2px 8px', background: '#EF4444', borderColor: '#EF4444', color: '#fff' }} onClick={() => removeEmail(i)}>Remove</button>
                  )}
                </div>
                {i > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <label className="modal-label">Delay (days after previous email)</label>
                    <input className="modal-input" type="number" min="0" placeholder="3" value={em.delayDays} onChange={e => updateEmail(i, 'delayDays', parseInt(e.target.value) || 0)} style={{ width: 100 }} />
                  </div>
                )}
                <label className="modal-label">Subject Line *</label>
                <input className="modal-input" placeholder="e.g. Quick question about your pipeline" value={em.subject} onChange={e => updateEmail(i, 'subject', e.target.value)} />
                <label className="modal-label" style={{ marginTop: 10 }}>Body Text</label>
                <textarea className="modal-input" rows={5} placeholder="Email body..." value={em.body} onChange={e => updateEmail(i, 'body', e.target.value)} style={{ resize: 'vertical', fontFamily: 'var(--mono)', fontSize: 12 }} />
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={loading} onClick={save}>{loading ? 'Creating...' : 'Create Sequence'}</button>
        </div>
      </div>
    </div>
  );
}

function SequencesTab() {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [seqEmails, setSeqEmails] = useState({});
  const [editModal, setEditModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const fetchSequences = () => {
    setLoading(true);
    fetch('https://tetrus.ai/api/mautic/campaigns', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setSequences(d.campaigns || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchSequences(); }, []);

  const handleCreated = (data) => {
    setShowModal(false);
    setSuccessMsg(data.message || 'Sequence created!');
    fetchSequences();
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const deleteSequence = async (seq) => {
    try {
      const r = await fetch('https://tetrus.ai/api/mautic/sequences/' + seq.id, { method: 'DELETE', headers: apiHeaders });
      if (!r.ok) throw new Error('Delete failed');
      setSequences(prev => prev.filter(s => s.id !== seq.id));
      setDeleteConfirm(null);
    } catch (err) {
      alert('Failed to delete sequence: ' + err.message);
    }
  };

  const toggleExpand = (seq) => {
    const newId = expandedId === seq.id ? null : seq.id;
    setExpandedId(newId);
    if (newId && !seqEmails[newId]) {
      setSeqEmails(prev => ({ ...prev, [newId]: { loading: true, emails: [] } }));
      fetch('https://tetrus.ai/api/mautic/sequences/' + newId + '/emails', { headers: apiHeaders })
        .then(r => r.json())
        .then(d => setSeqEmails(prev => ({ ...prev, [newId]: { loading: false, emails: d.emails || [] } })))
        .catch(() => setSeqEmails(prev => ({ ...prev, [newId]: { loading: false, emails: [], error: true } })));
    }
  };

  const saveEmail = async () => {
    if (!editModal) return;
    setSaving(true); setSaveMsg('');
    try {
      const r = await fetch('https://tetrus.ai/api/mautic/emails/' + editModal.emailId, {
        method: 'PUT',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: editModal.subject, body: editModal.body }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'Save failed');
      setSeqEmails(prev => {
        const cur = prev[editModal.seqId];
        if (!cur) return prev;
        return { ...prev, [editModal.seqId]: { ...cur, emails: cur.emails.map(e => e.emailId === editModal.emailId ? { ...e, subject: editModal.subject, body: editModal.body } : e) } };
      });
      setSaveMsg('Saved!');
      setTimeout(() => { setSaveMsg(''); setEditModal(null); }, 1200);
    } catch (e) { setSaveMsg('Error: ' + e.message); }
    setSaving(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Loading sequences...</div>;

  const PencilIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );

  return (
    <>
      {showModal && <NewSequenceModal onClose={() => setShowModal(false)} onCreated={handleCreated} />}

      {editModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#1E293B', border: '1px solid #334155', borderRadius: 12, padding: '28px 32px', width: '92%', maxWidth: 580 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#F1F5F9', marginBottom: 20 }}>Edit Email</div>
            <label style={{ display: 'block', fontSize: 12, color: '#94A3B8', marginBottom: 6 }}>Subject Line</label>
            <input
              value={editModal.subject}
              onChange={e => setEditModal(m => ({ ...m, subject: e.target.value }))}
              style={{ width: '100%', background: '#0F172A', border: '1px solid #334155', borderRadius: 6, padding: '8px 12px', color: '#F1F5F9', fontSize: 13, marginBottom: 16, boxSizing: 'border-box' }}
            />
            <label style={{ display: 'block', fontSize: 12, color: '#94A3B8', marginBottom: 6 }}>Body</label>
            <textarea
              value={editModal.body}
              onChange={e => setEditModal(m => ({ ...m, body: e.target.value }))}
              rows={11}
              style={{ width: '100%', background: '#0F172A', border: '1px solid #334155', borderRadius: 6, padding: '8px 12px', color: '#F1F5F9', fontSize: 13, fontFamily: 'var(--mono)', resize: 'vertical', boxSizing: 'border-box', marginBottom: 16 }}
            />
            {saveMsg && <div style={{ fontSize: 13, color: saveMsg.startsWith('Error') ? '#f87171' : '#10b981', marginBottom: 12 }}>{saveMsg}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditModal(null)} style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid #334155', background: 'transparent', color: '#CBD5E1', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={saveEmail} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#0EA5E9', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {successMsg && (
        <div className="alert alert-green" style={{ marginBottom: 16 }}>
          <span>&#x2713;</span><span>{successMsg}</span>
          <span style={{ marginLeft: 'auto', cursor: 'pointer', opacity: 0.6 }} onClick={() => setSuccessMsg('')}>&#x2715;</span>
        </div>
      )}
      <div className="table-card">
        <div className="card-header" style={{ padding: '18px 20px 14px' }}>
          <div className="card-title">Email Sequences</div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Sequence</button>
        </div>
        {sequences.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>&#9993;</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>No sequences yet</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>Create your first email sequence to start automating outreach.</div>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Sequence</button>
          </div>
        ) : (
          <table>
            <thead>
              <tr><th style={{ width: 28 }}></th><th>Sequence Name</th><th>Emails</th><th>Contacts</th><th>Status</th><th>Open Rate</th><th style={{ width: 44 }}></th></tr>
            </thead>
            <tbody>
              {sequences.map(seq => {
                const isExpanded = expandedId === seq.id;
                const emailData = seqEmails[seq.id];
                return (
                  <React.Fragment key={seq.id}>
                    <tr
                      style={{ cursor: 'pointer', background: isExpanded ? 'rgba(14,165,233,0.06)' : '', transition: 'background 0.15s' }}
                      onClick={() => toggleExpand(seq)}
                    >
                      <td style={{ textAlign: 'center', color: '#64748B', fontSize: 10, userSelect: 'none' }}>
                        {isExpanded ? '▼' : '▶'}
                      </td>
                      <td className="td-name">
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {seq.name}
                          <span
                            title="Edit emails"
                            style={{ color: '#475569', flexShrink: 0, lineHeight: 1 }}
                          >
                            <PencilIcon />
                          </span>
                        </span>
                      </td>
                      <td style={{ fontFamily: 'var(--mono)' }}>{seq.emailCount}</td>
                      <td style={{ fontFamily: 'var(--mono)' }}>{(seq.contactCount || 0).toLocaleString()}</td>
                      <td><span className={"tag " + (seq.status === 'active' ? 'tag-green' : 'tag-yellow')}>{seq.status === 'active' ? 'Active' : 'Paused'}</span></td>
                      <td style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{seq.openRate}%</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          onClick={e => { e.stopPropagation(); setDeleteConfirm(seq); }}
                          title="Delete sequence"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: '#64748B', lineHeight: 1 }}
                          onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
                          onMouseLeave={e => e.currentTarget.style.color = '#64748B'}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} style={{ background: '#080F1A', padding: 0, borderBottom: '2px solid #1E293B' }}>
                          <div style={{ padding: '18px 24px 20px' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.08em', marginBottom: 12, textTransform: 'uppercase' }}>Email Steps</div>
                            {!emailData || emailData.loading ? (
                              <div style={{ color: '#64748B', fontSize: 13, padding: '8px 0' }}>Loading…</div>
                            ) : emailData.error ? (
                              <div style={{ color: '#f87171', fontSize: 13 }}>Failed to load emails.</div>
                            ) : emailData.emails.length === 0 ? (
                              <div style={{ color: '#64748B', fontSize: 13 }}>No email steps found for this sequence.</div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {emailData.emails.map((em, i) => (
                                  <div key={em.emailId || i} style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#131E2E', borderRadius: 8, padding: '11px 16px', border: '1px solid #1E2D42' }}>
                                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg,#0EA5E9,#2563EB)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{em.subject}</div>
                                      <div style={{ fontSize: 11, color: '#475569', fontFamily: 'var(--mono)' }}>{em.delay}</div>
                                    </div>
                                    <button
                                      onClick={() => setEditModal({ emailId: em.emailId, subject: em.subject, body: em.body, seqId: seq.id })}
                                      style={{ background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 6, color: '#38BDF8', cursor: 'pointer', padding: '5px 12px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, transition: 'background 0.15s' }}
                                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(14,165,233,0.2)'}
                                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(14,165,233,0.1)'}
                                    >
                                      <PencilIcon /> Edit
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1E293B', border: '1px solid #334155', borderRadius: 12, padding: '28px 32px', maxWidth: 360, width: '90%' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#F1F5F9', marginBottom: 8 }}>Delete this sequence?</div>
            <div style={{ color: '#94A3B8', fontSize: 13, marginBottom: 8 }}>
              <span style={{ color: '#0EA5E9', fontWeight: 600 }}>{deleteConfirm.name}</span>
            </div>
            <div style={{ color: '#f87171', fontSize: 13, marginBottom: 24 }}>This cannot be undone.</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid #334155', background: 'transparent', color: '#CBD5E1', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={() => deleteSequence(deleteConfirm)} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ============================================================
// CAMPAIGN DETAIL MODAL
// ============================================================
function CampaignDetailModal({ campaignId, onClose, onUpdated }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]   = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [mauticCampaigns, setMauticCampaigns] = useState([]);
  const [mauticLoading, setMauticLoading] = useState(true);
  const [mauticLoadError, setMauticLoadError] = useState(false);
  const [editImportLists, setEditImportLists] = useState([]);

  const loadMauticCampaigns = () => {
    setMauticLoading(true);
    setMauticLoadError(false);
    fetch('/api/mautic/campaigns', { headers: apiHeaders })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => { setMauticCampaigns(d.campaigns || []); setMauticLoading(false); })
      .catch(() => { setMauticLoading(false); setMauticLoadError(true); });
  };
  const load = () => {
    setLoading(true);
    fetch('/api/campaigns/' + campaignId + '/detail', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => {
        setDetail(d);
        setEditForm({
          status:             d.campaign.status,
          from_address:       d.campaign.from_address || '',
          daily_limit:        d.campaign.daily_limit  || 50,
          description:        d.campaign.description  || '',
          mautic_campaign_id: d.campaign.mautic_campaign_id != null ? String(d.campaign.mautic_campaign_id) : '',
          list_id:            d.campaign.list_id != null ? String(d.campaign.list_id) : '',
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    loadMauticCampaigns();
    fetch('/api/import-lists', { headers: apiHeaders })
      .then(r => r.json()).then(d => setEditImportLists(d.lists || [])).catch(() => {});
  }, [campaignId]);

  const saveEdit = async () => {
    setSaving(true); setSaveErr('');
    try {
      const r = await fetch('/api/campaigns/' + campaignId, {
        method: 'PATCH',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const d = await r.json();
      if (d.campaign) {
        setEditing(false);
        load();
        if (onUpdated) onUpdated();
      } else { setSaveErr(d.error || 'Save failed'); }
    } catch(e) { setSaveErr('Network error'); }
    setSaving(false);
  };

  const s = {
    overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' },
    panel:    { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, width: '100%', maxWidth: 860, display: 'flex', flexDirection: 'column', gap: 0 },
    header:   { display: 'flex', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border)', gap: 12 },
    title:    { flex: 1, fontSize: 16, fontWeight: 700, color: '#F1F5F9' },
    body:     { padding: '24px', display: 'flex', flexDirection: 'column', gap: 24 },
    section:  { display: 'flex', flexDirection: 'column', gap: 10 },
    secTitle: { fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 },
    grid2:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
    statBox:  { background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' },
    statVal:  { fontSize: 22, fontWeight: 700, color: '#F1F5F9', fontFamily: 'var(--mono)' },
    statLbl:  { fontSize: 11, color: '#64748B', marginTop: 2 },
    fieldBox: { background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' },
    fieldLbl: { fontSize: 10, color: '#64748B', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.07em' },
    fieldVal: { fontSize: 13, color: '#E2E8F0', marginTop: 2 },
    emailRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8 },
    badge:    { fontSize: 10, fontFamily: 'var(--mono)', color: '#94A3B8', background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap' },
    input:    { background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, padding: '8px 12px', color: '#F1F5F9', fontSize: 13, fontFamily: 'var(--font)', outline: 'none', width: '100%', boxSizing: 'border-box' },
    textarea: { background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, padding: '8px 12px', color: '#F1F5F9', fontSize: 13, fontFamily: 'var(--font)', outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 72 },
    contactTh:{ fontSize: 11, color: '#64748B', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.07em', padding: '6px 0' },
    contactTd:{ fontSize: 12, color: '#E2E8F0', padding: '7px 0', borderBottom: '1px solid var(--border)' },
  };

  const statusColor = { active: '#10B981', paused: '#F59E0B', draft: '#64748B', completed: '#38BDF8' };

  if (loading) return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.panel, alignItems: 'center', justifyContent: 'center', minHeight: 200 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: 40, color: '#64748B', fontSize: 13 }}>Loading campaign detail…</div>
      </div>
    </div>
  );

  if (!detail) return null;
  const { campaign, stats, sequence, contacts } = detail;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.panel} onClick={e => e.stopPropagation()}>

        <div style={s.header}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor[campaign.status] || '#64748B', flexShrink: 0 }} />
          <div style={s.title}>{campaign.name}</div>
          <span style={{ fontSize: 12, color: statusColor[campaign.status] || '#64748B', fontWeight: 600, textTransform: 'capitalize' }}>{campaign.status}</span>
          {!editing && <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 14px', marginLeft: 8 }} onClick={() => setEditing(true)}>Edit</button>}
          {editing  && <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 14px', marginLeft: 8 }} onClick={() => { setEditing(false); setSaveErr(''); }}>Cancel</button>}
          {editing  && <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 14px' }} disabled={saving} onClick={saveEdit}>{saving ? 'Saving…' : 'Save'}</button>}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 18, cursor: 'pointer', lineHeight: 1, marginLeft: 4 }}>&#10005;</button>
        </div>

        <div style={s.body}>
          {saveErr && <div style={{ color: '#EF4444', fontSize: 12 }}>{saveErr}</div>}

          <div style={s.section}>
            <div style={s.secTitle}>Campaign Settings</div>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={s.grid2}>
                  <div>
                    <div style={{ ...s.fieldLbl, marginBottom: 4 }}>Status</div>
                    <select style={s.input} value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ ...s.fieldLbl, marginBottom: 4 }}>From Address</div>
                    <select style={s.input} value={editForm.from_address} onChange={e => setEditForm(p => ({ ...p, from_address: e.target.value }))}>
                      <option>outreach@tetrusemail1.com</option>
                      <option>outreach@tetrusemail2.com</option>
                      <option>outreach@tetrusemail3.com</option>
                    </select>
                  </div>
                </div>
                <div>
                  <div style={{ ...s.fieldLbl, marginBottom: 4 }}>Daily Send Limit</div>
                  <input type="number" style={{ ...s.input, maxWidth: 160 }} min={1} max={500} value={editForm.daily_limit} onChange={e => setEditForm(p => ({ ...p, daily_limit: e.target.value }))} />
                </div>
                <div>
                  <div style={{ ...s.fieldLbl, marginBottom: 4 }}>Email Sequence</div>
                  {mauticLoading ? (
                    <div style={{ fontSize: 12, color: '#94A3B8', padding: '8px 0' }}>Loading sequences...</div>
                  ) : mauticLoadError ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: 12, color: '#EF4444' }}>Failed to load sequences.</span>
                      <button type="button" onClick={loadMauticCampaigns} style={{ fontSize: 11, color: '#0EA5E9', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>Retry</button>
                    </div>
                  ) : (
                    <>
                      <select style={s.input} value={editForm.mautic_campaign_id} onChange={e => setEditForm(p => ({ ...p, mautic_campaign_id: e.target.value }))}>
                        <option value="">— No sequence selected —</option>
                        {mauticCampaigns.map(c => (
                          <option key={c.id} value={String(c.id)}>{c.name}{c.contactCount ? ' (' + Number(c.contactCount).toLocaleString() + ' contacts)' : ''}</option>
                        ))}
                      </select>
                      {mauticCampaigns.length === 0 && (
                        <div style={{ fontSize: 11, color: '#F59E0B', marginTop: 4 }}>No email sequences found yet — create one in Campaign Builder first.</div>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <div style={{ ...s.fieldLbl, marginBottom: 4 }}>Target List</div>
                  <select style={s.input} value={editForm.list_id} onChange={e => setEditForm(p => ({ ...p, list_id: e.target.value }))}>
                    <option value="">All Clean Leads</option>
                    {editImportLists.map(l => (
                      <option key={l.id} value={String(l.id)}>{l.filename.replace(/\.[^.]+$/, '')} ({Number(l.imported).toLocaleString()} leads)</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{ ...s.fieldLbl, marginBottom: 4 }}>Description</div>
                  <textarea style={s.textarea} value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} />
                </div>
              </div>
            ) : (
              <div style={s.grid2}>
                {[
                  { label: 'From Address',    value: campaign.from_address || '—' },
                  { label: 'Start Date',      value: campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : '—' },
                  { label: 'Daily Limit',     value: (campaign.daily_limit || 50) + ' emails / day' },
                  { label: 'Target List',     value: campaign.list_name || 'All Lists' },
                  { label: 'Target Industry', value: campaign.target_industry || 'All Industries' },
                ].map(f => (
                  <div key={f.label} style={s.fieldBox}>
                    <div style={s.fieldLbl}>{f.label}</div>
                    <div style={s.fieldVal}>{f.value}</div>
                  </div>
                ))}
                {campaign.description && (
                  <div style={{ ...s.fieldBox, gridColumn: '1 / -1' }}>
                    <div style={s.fieldLbl}>Description</div>
                    <div style={{ ...s.fieldVal, lineHeight: 1.5 }}>{campaign.description}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={s.section}>
            <div style={s.secTitle}>Performance</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
              {[
                { label: 'Matched',     value: (stats.matched || 0).toLocaleString() },
                { label: 'Qualified',   value: (stats.qualified || 0).toLocaleString(), accent: '#10B981' },
                { label: 'Emails Sent', value: (stats.emails_sent || 0).toLocaleString() },
                { label: 'Open Rate',   value: stats.open_rate + '%',  accent: '#38BDF8' },
                { label: 'Click Rate',  value: stats.click_rate + '%', accent: '#8B5CF6' },
              ].map(f => (
                <div key={f.label} style={s.statBox}>
                  <div style={{ ...s.statVal, color: f.accent || '#F1F5F9' }}>{f.value}</div>
                  <div style={s.statLbl}>{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          {sequence && sequence.emails && sequence.emails.length > 0 && (
            <div style={s.section}>
              <div style={s.secTitle}>Email Sequence — {sequence.name}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sequence.emails.map((e, i) => (
                  <div key={i} style={s.emailRow}>
                    <span style={{ ...s.badge, color: '#38BDF8', borderColor: 'rgba(56,189,248,0.3)', background: 'rgba(56,189,248,0.06)' }}>{e.delay}</span>
                    <span style={{ fontSize: 12, color: '#64748B', fontFamily: 'var(--mono)', minWidth: 52 }}>Email {e.step}</span>
                    <span style={{ fontSize: 13, color: '#E2E8F0', flex: 1 }}>{e.subject}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {contacts && contacts.length > 0 && (
            <div style={s.section}>
              <div style={s.secTitle}>Recent Contacts ({contacts.length})</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Name', 'Email', 'Company', 'Score', 'Added'].map(h => (
                      <th key={h} style={{ ...s.contactTh, textAlign: 'left', paddingRight: 16 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, i) => (
                    <tr key={i}>
                      <td style={{ ...s.contactTd, paddingRight: 16, fontWeight: 500 }}>{c.name}</td>
                      <td style={{ ...s.contactTd, paddingRight: 16, fontFamily: 'var(--mono)', fontSize: 11, color: '#94A3B8' }}>{c.email}</td>
                      <td style={{ ...s.contactTd, paddingRight: 16 }}>{c.company}</td>
                      <td style={{ ...s.contactTd, paddingRight: 16, fontFamily: 'var(--mono)', color: c.score >= 70 ? '#10B981' : '#94A3B8' }}>{c.score}</td>
                      <td style={{ ...s.contactTd, fontFamily: 'var(--mono)', fontSize: 11, color: '#64748B' }}>{c.last_activity ? new Date(c.last_activity).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!contacts || contacts.length === 0) && (!sequence || !sequence.emails || sequence.emails.length === 0) && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#475569', fontSize: 13 }}>No contacts synced yet. Activate the campaign to begin outreach.</div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// CAMPAIGN MONITOR — per-campaign live dashboard sub-view
// ============================================================
function CampaignMonitor({ campaignId, onBack }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [lastRefresh, setLastRefresh] = React.useState(null);
  const timerRef = React.useRef(null);

  const fetchData = async () => {
    try {
      const r = await fetch("https://tetrus.ai/api/campaigns/" + campaignId + "/monitor", { headers: apiHeaders });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Failed to load");
      setData(d); setErr(null); setLastRefresh(new Date());
    } catch(e) { setErr(e.message); } finally { setLoading(false); }
  };

  React.useEffect(() => {
    fetchData();
    timerRef.current = setInterval(fetchData, 20000);
    return () => clearInterval(timerRef.current);
  }, [campaignId]);

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>Loading campaign monitor…</div>;
  if (err) return (
    <div style={{ padding: 40 }}>
      <div className="alert alert-red" style={{ marginBottom: 16 }}>⚠ {err}</div>
      <button className="btn btn-ghost" onClick={onBack}>← Back to Campaigns</button>
    </div>
  );
  if (!data) return null;

  const { campaign, deliverability, engagement, pipeline, activity, bot_summary } = data;
  const pct = (n, d) => d > 0 ? ((n / d) * 100).toFixed(1) : "0.0";
  const fmtTime = (s) => { try { const d = new Date((s||"").replace(" ", "T")+"Z"); return isNaN(d)?s:d.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit",second:"2-digit"}); } catch(e) { return s||"—"; } };
  const evColor = (t) => t==="click"?"#8B5CF6":t==="open"?"#10B981":t==="delivered"?"#0EA5E9":t==="bounce"?"#EF4444":t==="spamreport"?"#F59E0B":"#64748B";
  const evIcon  = (t) => t==="click"?"↗":t==="open"?"◉":t==="delivered"?"✓":t==="bounce"?"✕":t==="spamreport"?"⚠":t==="processed"?"⟳":"·";
  const totalBotEv = (bot_summary.human||0) + (bot_summary.bot||0) + (bot_summary.suspicious||0);
  const humanPct  = totalBotEv > 0 ? Math.round(bot_summary.human / totalBotEv * 100) : 0;
  const statusCls = campaign.status==="active"?"tag-green":campaign.status==="paused"?"tag-yellow":campaign.status==="completed"?"tag-blue":"tag-red";
  const pipeColors = ["#0EA5E9","#10B981","#8B5CF6","#F59E0B"];

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24, flexWrap:"wrap" }}>
        <button className="btn btn-ghost" onClick={onBack} style={{ padding:"6px 14px", fontSize:12, flexShrink:0 }}>← Campaigns</button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:18, fontWeight:700, color:"#F1F5F9" }}>{campaign.name}</div>
          <div style={{ fontSize:11, color:"#94A3B8", fontFamily:"var(--mono)", marginTop:2 }}>
            {campaign.list_name||"All Lists"} · {(campaign.total_contacts||0).toLocaleString()} enrolled
            {campaign.from_address ? " · "+campaign.from_address : ""}
          </div>
        </div>
        <span className={"tag "+statusCls} style={{ fontSize:11 }}>{(campaign.status||"").toUpperCase()}</span>
        <button className="btn btn-ghost" onClick={fetchData} style={{ padding:"6px 12px", fontSize:11, flexShrink:0 }}>↺ Refresh</button>
        {lastRefresh && <div style={{ fontSize:10, color:"#475569", fontFamily:"var(--mono)" }}>AUTO·{lastRefresh.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</div>}
      </div>

      {/* ── Deliverability stat row ── */}
      <div className="stat-grid" style={{ gridTemplateColumns:"repeat(5,1fr)", marginBottom:20 }}>
        {[
          { label:"Sent",         value:deliverability.total_sent.toLocaleString(),                    sub:`${(campaign.total_contacts||0).toLocaleString()} enrolled`,       color:"#0EA5E9" },
          { label:"Delivered",    value:pct(deliverability.delivered,deliverability.total_sent)+"%",   sub:`${deliverability.delivered.toLocaleString()} emails`,             color:"#10B981" },
          { label:"Bounce Rate",  value:pct(deliverability.bounce,deliverability.total_sent)+"%",      sub:`${deliverability.bounce} hard bounces`,                           color:deliverability.bounce>0?"#EF4444":"#64748B" },
          { label:"Spam Reports", value:String(deliverability.spam),                                   sub:pct(deliverability.spam,deliverability.total_sent||1)+"% of sent", color:deliverability.spam>0?"#F59E0B":"#64748B" },
          { label:"Dropped",      value:String(deliverability.dropped),                                sub:"suppressed / rejected",                                           color:deliverability.dropped>0?"#F59E0B":"#64748B" },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color:"#fff", fontSize:22 }}>{s.value}</div>
            <div className="stat-sub" style={{ color:s.color }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Engagement + Pipeline ── */}
      <div className="two-col" style={{ marginBottom:20 }}>
        <div className="pipeline-card" style={{ marginBottom:0 }}>
          <div className="card-header">
            <div className="card-title">Engagement Intelligence</div>
            <span className="tag tag-green">{((engagement.human_opens||0)+(engagement.total_clicks||0)).toLocaleString()} verified signals</span>
          </div>
          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:10, color:"#475569", fontFamily:"var(--mono)", letterSpacing:1, marginBottom:8 }}>OPENS</div>
            <div style={{ display:"flex", gap:20, marginBottom:8 }}>
              {[
                { label:"Human",   value:engagement.human_opens||0,   color:"#10B981" },
                { label:"Scanner", value:engagement.scanner_opens||0, color:"#F59E0B" },
                { label:"Unique",  value:engagement.unique_openers||0, color:"#CBD5E1" },
              ].map((b,i) => (
                <React.Fragment key={b.label}>
                  {i>0 && <div style={{ width:1, background:"#1E2A3A" }} />}
                  <div>
                    <div style={{ fontSize:26, fontWeight:700, color:b.color, fontFamily:"var(--mono)" }}>{b.value.toLocaleString()}</div>
                    <div style={{ fontSize:11, color:"#94A3B8" }}>{b.label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div style={{ height:4, background:"#1E2A3A", borderRadius:2, overflow:"hidden" }}>
              <div style={{ height:"100%", background:"#10B981", borderRadius:2,
                width: ((engagement.human_opens||0)+(engagement.scanner_opens||0)>0
                  ? Math.round((engagement.human_opens||0)/((engagement.human_opens||0)+(engagement.scanner_opens||0))*100)
                  : 0)+"%"
              }} />
            </div>
            <div style={{ fontSize:10, color:"#475569", marginTop:3, fontFamily:"var(--mono)" }}>
              open rate {pct(engagement.unique_openers||0, deliverability.total_sent||campaign.total_contacts||1)}%
            </div>
          </div>
          <div>
            <div style={{ fontSize:10, color:"#475569", fontFamily:"var(--mono)", letterSpacing:1, marginBottom:8 }}>CLICKS</div>
            <div style={{ display:"flex", gap:20 }}>
              {[
                { label:"Total",  value:engagement.total_clicks||0,    color:"#8B5CF6" },
                { label:"Unique", value:engagement.unique_clickers||0, color:"#A78BFA" },
                { label:"CTR",    value:pct(engagement.unique_clickers||0, deliverability.total_sent||campaign.total_contacts||1)+"%", color:"#CBD5E1" },
              ].map((b,i) => (
                <React.Fragment key={b.label}>
                  {i>0 && <div style={{ width:1, background:"#1E2A3A" }} />}
                  <div>
                    <div style={{ fontSize:22, fontWeight:700, color:b.color, fontFamily:"var(--mono)" }}>{String(b.value)}</div>
                    <div style={{ fontSize:11, color:"#94A3B8" }}>{b.label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="pipeline-card" style={{ marginBottom:0 }}>
          <div className="card-header">
            <div className="card-title">Pipeline Progression</div>
            <span className="tag tag-blue">{(pipeline[0]?.count||0).toLocaleString()} enrolled</span>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {pipeline.map((stage, i) => (
              <div key={stage.stage}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                  <div style={{ fontSize:12, color:"#CBD5E1", fontWeight:500 }}>{stage.stage}</div>
                  <div style={{ fontSize:13, fontWeight:700, fontFamily:"var(--mono)", color:pipeColors[i] }}>
                    {stage.count.toLocaleString()} <span style={{ fontSize:10, fontWeight:400, color:"#475569" }}>({stage.pct}%)</span>
                  </div>
                </div>
                <div style={{ height:6, background:"#1E2A3A", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:stage.pct+"%", background:pipeColors[i], borderRadius:3, transition:"width 0.4s" }} />
                </div>
              </div>
            ))}
            {pipeline.length === 0 && <div style={{ fontSize:12, color:"#64748B" }}>No pipeline data yet — send this campaign to begin tracking.</div>}
          </div>
        </div>
      </div>

      {/* ── Activity Ticker + Bot Detection ── */}
      <div className="two-col">
        <div className="table-card" style={{ marginBottom:0 }}>
          <div className="card-header" style={{ padding:"18px 20px 14px" }}>
            <div className="card-title">Live Activity</div>
            <span className="tag tag-blue">{activity.length} recent events</span>
          </div>
          <div style={{ maxHeight:360, overflowY:"auto" }}>
            {activity.length === 0
              ? <div style={{ padding:24, textAlign:"center", color:"#64748B", fontSize:12 }}>No events yet — activity appears here as emails are sent and opened.</div>
              : activity.map((ev, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 20px", borderBottom:"1px solid #1E2A3A" }}>
                  <div style={{ width:22, height:22, borderRadius:"50%", background:evColor(ev.event_type)+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:evColor(ev.event_type), flexShrink:0, fontWeight:700 }}>
                    {evIcon(ev.event_type)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, color:"#E2E8F0", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ev.email}</div>
                    <div style={{ fontSize:11, color:"#64748B", marginTop:1 }}>
                      <span style={{ color:evColor(ev.event_type), fontWeight:600 }}>{ev.event_type}</span>
                      {ev.url && <span> · {ev.url.replace(/^https?:\/\/[^/]+/, "").slice(0,28)||"/"}</span>}
                      {ev.is_human===1 && <span style={{ color:"#10B981" }}> · ✓human</span>}
                      {ev.scanner_suspected===1 && <span style={{ color:"#F59E0B" }}> · scanner</span>}
                    </div>
                  </div>
                  <div style={{ fontSize:10, color:"#475569", fontFamily:"var(--mono)", flexShrink:0 }}>{fmtTime(ev.sg_timestamp)}</div>
                </div>
              ))}
          </div>
        </div>

        <div className="pipeline-card" style={{ marginBottom:0 }}>
          <div className="card-header">
            <div className="card-title">Bot Detection Summary</div>
            <span className={"tag "+(humanPct>=80?"tag-green":humanPct>=50?"tag-yellow":"tag-red")}>{humanPct}% Human</span>
          </div>
          <div style={{ display:"flex", gap:10, marginBottom:18 }}>
            {[
              { label:"Human",   count:bot_summary.human||0,      color:"#10B981" },
              { label:"Bot",     count:bot_summary.bot||0,        color:"#EF4444" },
              { label:"Suspect", count:bot_summary.suspicious||0, color:"#F59E0B" },
            ].map(b => (
              <div key={b.label} style={{ flex:1, textAlign:"center", padding:"12px 6px", background:"#111827", borderRadius:8, border:"1px solid #1E2A3A" }}>
                <div style={{ fontSize:20, fontWeight:700, color:b.color, fontFamily:"var(--mono)" }}>{b.count.toLocaleString()}</div>
                <div style={{ fontSize:11, color:"#94A3B8", marginTop:2 }}>{b.label}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize:10, color:"#475569", fontFamily:"var(--mono)", letterSpacing:1, marginBottom:10 }}>ENGAGEMENT SCORE DISTRIBUTION</div>
          {[
            { label:"0–25  (cold/bot)",   idx:0, color:"#EF4444" },
            { label:"26–50 (low signal)", idx:1, color:"#F59E0B" },
            { label:"51–75 (engaged)",    idx:2, color:"#0EA5E9" },
            { label:"76–100 (hot)",       idx:3, color:"#10B981" },
          ].map(b => {
            const cnt = (bot_summary.buckets||[])[b.idx]||0;
            const total = ((bot_summary.buckets||[]).reduce((a,v)=>a+v,0))||1;
            return (
              <div key={b.label} style={{ marginBottom:8 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#94A3B8", marginBottom:3 }}>
                  <span style={{ fontFamily:"var(--mono)" }}>{b.label}</span>
                  <span style={{ fontFamily:"var(--mono)", color:b.color }}>{cnt.toLocaleString()}</span>
                </div>
                <div style={{ height:4, background:"#1E2A3A", borderRadius:2, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:Math.round(cnt/total*100)+"%", background:b.color, borderRadius:2, transition:"width 0.4s" }} />
                </div>
              </div>
            );
          })}
          <div style={{ marginTop:14, padding:"10px 14px", background:"#111827", borderRadius:8, border:"1px solid #1E2A3A", fontSize:12, color:"#CBD5E1" }}>
            {totalBotEv > 0
              ? <><span style={{ color:"#10B981", fontWeight:700 }}>{humanPct}%</span> signal quality · {(bot_summary.bot||0)+(bot_summary.suspicious||0)} flagged events filtered from scoring</>
              : <span style={{ color:"#475569" }}>No engagement events recorded yet</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Campaigns({ onShowCreateCampaign, campaignRefreshKey }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState(null);
  const [activateResult, setActivateResult] = useState(null);
  const [tab, setTab] = useState("campaigns");
  const [detailId, setDetailId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [loadingSteps, setLoadingSteps] = useState(null);
  const [monitorId, setMonitorId] = useState(null);

  const activateCampaign = async (id) => {
    setActivatingId(id);
    setActivateResult(null);
    try {
      const r = await fetch("https://tetrus.ai/api/campaigns/" + id + "/activate", {
        method: "POST",
        headers: { ...apiHeaders, "Content-Type": "application/json" },
      });
      const d = await r.json();
      setActivateResult(d);
      fetchCampaigns();
    } catch (err) {
      setActivateResult({ success: false, error: "Network error" });
    }
    setActivatingId(null);
  };

  const fetchCampaigns = () => {
    fetch("https://tetrus.ai/api/campaigns", { headers: apiHeaders })
      .then((r) => r.json())
      .then((d) => { setCampaigns(d.campaigns || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchCampaigns(); }, []);
  useEffect(() => { if (campaignRefreshKey > 0) fetchCampaigns(); }, [campaignRefreshKey]);

  const toggleStatus = async (campaign) => {
    const newStatus = campaign.status === "active" ? "paused" : "active";
    try {
      await fetch("https://tetrus.ai/api/campaigns/" + campaign.id, {
        method: "PUT",
        headers: { ...apiHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      fetchCampaigns();
    } catch (err) {}
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign? This cannot be undone.")) return;
    try {
      await fetch("https://tetrus.ai/api/campaigns/" + id, {
        method: "DELETE",
        headers: apiHeaders,
      });
      fetchCampaigns();
    } catch (err) {}
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (expandedSteps[id]) return;
    setLoadingSteps(id);
    try {
      const r = await fetch("https://tetrus.ai/api/campaigns/" + id, { headers: apiHeaders });
      const d = await r.json();
      const steps = d.email_steps || d.steps || d.sequence || [];
      setExpandedSteps(prev => ({ ...prev, [id]: steps }));
    } catch (e) {
      setExpandedSteps(prev => ({ ...prev, [id]: [] }));
    }
    setLoadingSteps(null);
  };

  const activeCampaigns = campaigns.filter((c) => c.status === "active");
  const totalMatched = campaigns.reduce((s, c) => s + (c.matched_leads || 0), 0);
  const totalQualified = campaigns.reduce((s, c) => s + (c.qualified_leads || 0), 0);

  if (monitorId) return <CampaignMonitor campaignId={monitorId} onBack={() => setMonitorId(null)} />;

  if (loading) {
    return <div style={{ textAlign: "center", padding: 60, color: "var(--text3)" }}>Loading campaigns...</div>;
  }

  const primaryTabStyle = (active) => ({
    padding: "9px 22px", fontSize: 15, fontWeight: 600, cursor: "pointer",
    borderBottom: active ? "3px solid var(--accent)" : "3px solid transparent",
    color: active ? "var(--accent)" : "var(--text3)",
    transition: "all 0.15s", whiteSpace: "nowrap",
  });

  const toolTabStyle = (active) => ({
    padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
    borderBottom: active ? "2px solid #8b5cf6" : "2px solid transparent",
    color: active ? "#8b5cf6" : "rgba(255,255,255,0.6)",
    transition: "all 0.15s", whiteSpace: "nowrap", fontFamily: "var(--mono)", letterSpacing: "0.03em",
  });

  const TOOL_TABS = ["research", "abtesting", "competitor"];
  const isToolTab = TOOL_TABS.includes(tab);

  const TabBar = () => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "1px solid var(--border)" }}>
        <div style={{ display: "flex", gap: 0 }}>
          <div style={primaryTabStyle(!isToolTab)} onClick={() => setTab("campaigns")}>Campaigns &amp; Sequences</div>
        </div>
        <div style={{ display: "flex", gap: 0, alignItems: "center", paddingBottom: 0, borderLeft: "1px solid var(--border)", paddingLeft: 16 }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", fontFamily: "var(--mono)", marginRight: 8, letterSpacing: "0.05em", paddingBottom: 10 }}>STRATEGIC TOOLS</span>
          <div style={toolTabStyle(tab === "research")} onClick={() => setTab("research")}>Research</div>
          <div style={toolTabStyle(tab === "abtesting")} onClick={() => setTab("abtesting")}>A/B Testing</div>
          <div style={toolTabStyle(tab === "competitor")} onClick={() => setTab("competitor")}>Competitor</div>
        </div>
      </div>
    </div>
  );

  if (tab === "research")   return <><TabBar /><ResearchPanel /></>;
  if (tab === "abtesting")  return <><TabBar /><ABTestingPanel /></>;
  if (tab === "competitor") return <><TabBar /><CompetitorPanel /></>;

  return (
    <>
      <TabBar />

      <div className="stat-grid">
        {[
          { label: "Total Campaigns", value: campaigns.length.toString(), sub: activeCampaigns.length + " active", color: "#0EA5E9" },
          { label: "Matched Leads", value: totalMatched.toLocaleString(), sub: "Across all campaigns", color: "#10B981" },
          { label: "Qualified Leads", value: totalQualified.toLocaleString(), sub: "Score >= target", color: "#8B5CF6" },
          { label: "Active Campaigns", value: activeCampaigns.length.toString(), sub: "Currently running", color: "#F59E0B" },
        ].map((s) => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: "#FFFFFF" }}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {activateResult && (
        <div className={activateResult.success ? "alert alert-green" : "alert alert-red"} style={{ marginBottom: 16 }}>
          <span>{activateResult.success ? "\u2713" : "\u2717"}</span>
          <span>{activateResult.message || activateResult.error}</span>
          {activateResult.success && (
            <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontSize: 11 }}>
              {activateResult.contacts_created} contacts synced{activateResult.sequence_created ? " \u2022 3-email sequence #" + activateResult.mautic_campaign_id + " created" : ""}
            </span>
          )}
          <span style={{ marginLeft: 12, cursor: "pointer", opacity: 0.6 }} onClick={() => setActivateResult(null)}>\u2715</span>
        </div>
      )}

      <div className="table-card">
        <div className="card-header" style={{ padding: "18px 20px 14px" }}>
          <div className="card-title">Campaign Performance</div>
          <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)" }}>Click a campaign to expand its sequence</div>
        </div>
        {campaigns.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>&#9671;</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text2)", marginBottom: 6 }}>No campaigns yet</div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 20, maxWidth: 300, margin: "0 auto 20px" }}>
              Launch your first campaign to start engaging qualified leads across your pipeline.
            </div>
            <button className="btn btn-primary" onClick={onShowCreateCampaign}>Launch Campaign</button>
          </div>
        ) : (
          <table>
            <thead>
              <tr><th></th><th>Campaign</th><th>Target List</th><th>Target Industry</th><th>Min Score</th><th>Matched</th><th>Qualified</th><th>Status</th><th>Action</th></tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <React.Fragment key={c.id}>
                  <tr style={{ cursor: "pointer" }} onClick={() => toggleExpand(c.id)}>
                    <td style={{ width: 28, textAlign: "center", fontSize: 11, color: "var(--text3)" }}>
                      {expandedId === c.id ? "\u25bc" : "\u25b6"}
                    </td>
                    <td className="td-name" style={{ color: "#38BDF8", cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setMonitorId(c.id); }} title="Open Campaign Monitor">{c.name} <span style={{ fontSize:10, color:"#475569" }}>↗</span></td>
                    <td style={{ fontSize: 13, color: "#94A3B8" }}>{c.list_name || "All Lists"}</td>
                    <td style={{ fontSize: 13, color: "#CBD5E1" }}>{c.target_industry || "All Industries"}</td>
                    <td style={{ fontFamily: "var(--mono)" }}>{c.target_min_score || "0"}</td>
                    <td style={{ fontFamily: "var(--mono)" }}>{(c.matched_leads || 0).toLocaleString()}</td>
                    <td style={{ fontWeight: 600, color: "#10B981", fontFamily: "var(--mono)" }}>{(c.qualified_leads || 0).toLocaleString()}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <span className={"tag " + (c.status === "active" ? "tag-green" : c.status === "paused" ? "tag-yellow" : c.status === "completed" ? "tag-blue" : "tag-red")}>
                        {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ display: "flex", gap: 6, alignItems: "center" }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-ghost"
                        style={{ fontSize: 11, padding: "4px 10px", borderColor: "#0EA5E9", color: "#0EA5E9" }}
                        onClick={() => setMonitorId(c.id)}>Monitor</button>
                      <button className={"btn " + (c.status === "active" ? "btn-ghost" : "btn-primary")}
                        style={{ fontSize: 11, padding: "4px 10px" }}
                        onClick={() => toggleStatus(c)}>
                        {c.status === "active" ? "Pause" : "Resume"}
                      </button>
                      <button className="btn btn-primary"
                        style={{ fontSize: 11, padding: "4px 10px", background: "#8B5CF6", borderColor: "#8B5CF6" }}
                        disabled={activatingId === c.id}
                        onClick={() => activateCampaign(c.id)}>
                        {activatingId === c.id ? "Syncing..." : "Send Campaign"}
                      </button>
                      <button className="btn"
                        style={{ fontSize: 11, padding: "4px 10px", background: "#EF4444", borderColor: "#EF4444", color: "#fff" }}
                        onClick={() => deleteCampaign(c.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                  {expandedId === c.id && (
                    <tr>
                      <td colSpan={9} style={{ padding: 0, background: "rgba(14,165,233,0.04)", borderBottom: "2px solid rgba(14,165,233,0.15)" }}>
                        <div style={{ padding: "12px 20px 16px 44px" }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", fontFamily: "var(--mono)", letterSpacing: "0.08em", marginBottom: 10 }}>
                            EMAIL SEQUENCE
                          </div>
                          {loadingSteps === c.id ? (
                            <div style={{ fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)" }}>Loading steps\u2026</div>
                          ) : !expandedSteps[c.id] || expandedSteps[c.id].length === 0 ? (
                            <div style={{ fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                              No sequence steps found. <span style={{ color: "#38BDF8", cursor: "pointer" }} onClick={() => setDetailId(c.id)}>Open campaign details</span> to configure.
                            </div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              {expandedSteps[c.id].map((step, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 16px" }}>
                                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(14,165,233,0.15)", border: "1px solid rgba(14,165,233,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#38BDF8", flexShrink: 0 }}>
                                    {i + 1}
                                  </div>
                                  {i > 0 && (
                                    <div style={{ fontSize: 10, color: "#475569", fontFamily: "var(--mono)", whiteSpace: "nowrap", flexShrink: 0 }}>
                                      +{step.delay_days || step.delay || 0}d
                                    </div>
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {step.subject || step.subject_line || "(No subject)"}
                                    </div>
                                    {step.preview && (
                                      <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {step.preview}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    style={{ padding: "4px 12px", fontSize: 11, fontWeight: 600, borderRadius: 5, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text2)", cursor: "pointer", flexShrink: 0 }}
                                    onClick={() => setDetailId(c.id)}>
                                    Edit
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {detailId && <CampaignDetailModal campaignId={detailId} onClose={() => setDetailId(null)} onUpdated={fetchCampaigns} />}
    </>
  );
}


function WarmupCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('https://tetrus.ai/api/warmup/status?domain=tetrus.ai', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="pipeline-card" style={{ marginBottom: 20 }}>
      <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>Loading warmup data...</div>
    </div>
  );
  if (!data) return null;

  const { days, currentDay, nextDay, overallHealth, completedDays, totalDays, totalSent, avgOpenRate, avgBounceRate, domain, maxTarget } = data;

  const healthColor = overallHealth === 'on-track' ? '#10B981' : overallHealth === 'warning' ? '#F59E0B' : overallHealth === 'critical' ? '#EF4444' : '#CBD5E1';
  const healthLabel = overallHealth === 'on-track' ? 'On Track' : overallHealth === 'warning' ? 'Warning' : overallHealth === 'critical' ? 'Critical' : overallHealth === 'not-started' ? 'Not Started' : 'On Track';

  const barColor = (d) => {
    if (!d.emails_sent) return '#1E2A3A';
    if (d.health === 'critical') return '#EF4444';
    if (d.health === 'warning') return '#F59E0B';
    return '#10B981';
  };

  // SVG chart dimensions
  const W = 660, H = 130, PAD = { t: 8, r: 8, b: 24, l: 44 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;
  const barW = Math.floor(chartW / days.length) - 1;
  const yScale = (v) => chartH - (v / maxTarget) * chartH;

  // Y-axis ticks
  const yTicks = [0, 1000, 2000, 3000, 4000, 5000];

  return (
    <div className="pipeline-card" style={{ marginBottom: 20 }}>
      <div className="card-header" style={{ marginBottom: 16 }}>
        <div>
          <div className="card-title">Domain Warming — {domain}</div>
          <div className="card-sub" style={{ marginTop: 3 }}>30-DAY SEND VOLUME RAMP · DAY {completedDays} OF {totalDays}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: healthColor, boxShadow: `0 0 8px ${healthColor}` }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: healthColor, fontFamily: 'var(--mono)' }}>{healthLabel}</span>
        </div>
      </div>

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 18 }}>
        {[
          { label: 'Day', value: completedDays + ' / ' + totalDays, color: 'var(--accent2)' },
          { label: 'Today Target', value: (nextDay?.target_volume || currentDay?.target_volume || 0).toLocaleString(), color: 'var(--text)' },
          { label: 'Total Sent', value: totalSent >= 1000 ? (totalSent / 1000).toFixed(1) + 'K' : totalSent.toLocaleString(), color: '#10B981' },
          { label: 'Avg Open Rate', value: avgOpenRate + '%', color: avgOpenRate >= 20 ? '#10B981' : avgOpenRate >= 10 ? '#F59E0B' : '#EF4444' },
          { label: 'Avg Bounce Rate', value: avgBounceRate + '%', color: avgBounceRate < 2 ? '#10B981' : avgBounceRate < 5 ? '#F59E0B' : '#EF4444' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--surface2)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--mono)', color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* SVG Bar Chart */}
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 400, display: 'block' }}>
          {/* Y-axis grid + labels */}
          {yTicks.map(tick => {
            const y = PAD.t + yScale(tick);
            return (
              <g key={tick}>
                <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y}
                  stroke="#1E2A3A" strokeWidth="1" />
                <text x={PAD.l - 6} y={y + 4} textAnchor="end"
                  fontSize="9" fill="#CBD5E1" fontFamily="monospace">
                  {tick >= 1000 ? (tick / 1000) + 'K' : tick}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {days.map((d, i) => {
            const x = PAD.l + i * (chartW / days.length);
            const targetH = (d.target_volume / maxTarget) * chartH;
            const sentH = d.emails_sent > 0 ? Math.max(2, (d.emails_sent / maxTarget) * chartH) : 0;
            const isToday = d.day_number === completedDays + 1;
            return (
              <g key={d.day_number}>
                {/* Target bar (background) */}
                <rect
                  x={x + 1} y={PAD.t + yScale(d.target_volume)}
                  width={barW} height={targetH}
                  fill="#1E2A3A" rx="1"
                />
                {/* Actual sent bar */}
                {sentH > 0 && (
                  <rect
                    x={x + 1} y={PAD.t + chartH - sentH}
                    width={barW} height={sentH}
                    fill={barColor(d)} rx="1" opacity="0.9"
                  />
                )}
                {/* Today marker */}
                {isToday && (
                  <rect x={x} y={PAD.t} width={barW + 2} height={chartH}
                    fill="rgba(14,165,233,0.07)" rx="1" />
                )}
                {/* Day label every 5 days */}
                {(d.day_number === 1 || d.day_number % 5 === 0) && (
                  <text x={x + barW / 2} y={H - 6}
                    textAnchor="middle" fontSize="8" fill="#CBD5E1" fontFamily="monospace">
                    {d.day_number}
                  </text>
                )}
              </g>
            );
          })}

          {/* Target line at 5000 */}
          <line x1={PAD.l} y1={PAD.t + 1} x2={W - PAD.r} y2={PAD.t + 1}
            stroke="#F59E0B" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
        </svg>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 11, color: 'var(--text3)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: '#1E2A3A', display: 'inline-block' }} />
          Target
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: '#10B981', display: 'inline-block' }} />
          Sent (healthy)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: '#F59E0B', display: 'inline-block' }} />
          Sent (warning)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: '#EF4444', display: 'inline-block' }} />
          Sent (critical)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 14, height: 2, background: '#F59E0B', opacity: 0.6, display: 'inline-block' }} />
          Max target (5K)
        </span>
      </div>

      {/* Day-by-day table for completed days */}
      {completedDays > 0 && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 10 }}>COMPLETED DAYS</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
            {days.filter(d => d.emails_sent > 0).map(d => (
              <div key={d.day_number} style={{ background: 'var(--surface2)', borderRadius: 6, padding: '7px 8px', borderLeft: `3px solid ${barColor(d)}` }}>
                <div style={{ fontSize: 9, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>DAY {d.day_number}</div>
                <div style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--text)', marginTop: 2 }}>{d.emails_sent.toLocaleString()}</div>
                <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 2 }}>{d.open_rate}% open</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Deliverability({ liveStats }) {
  const fmt = (n) => n != null ? Number(n).toLocaleString() : '0';
  const t = liveStats.totalLeads || 1;
  const deliveryRate = (liveStats.cleanRate != null ? liveStats.cleanRate : 0).toFixed(1);
  const bounceRate = ((liveStats.bounced || 0) / t * 100).toFixed(2);
  const elimRate = (liveStats.eliminationRate != null ? liveStats.eliminationRate : 0).toFixed(1);
  const verifiedRate = ((liveStats.sendgridVerified || 0) / t * 100).toFixed(1);

  return (
    <>
      <WarmupCard />
      <div className="stat-grid">
        {[
          { label: "Clean Rate", value: deliveryRate + "%", sub: "Contacts passing full validation", color: parseFloat(deliveryRate) > 70 ? "#10B981" : "#F59E0B" },
          { label: "Bounce Rate", value: bounceRate + "%", sub: "From active campaign sends", color: parseFloat(bounceRate) < 3 ? "#10B981" : "#EF4444" },
          { label: "Address Validated", value: verifiedRate + "%", sub: fmt(liveStats.sendgridVerified) + " verified", color: parseFloat(verifiedRate) > 70 ? "#10B981" : "#F59E0B" },
          { label: "Eliminated", value: elimRate + "%", sub: fmt(liveStats.eliminated) + " removed", color: parseFloat(elimRate) < 35 ? "#F59E0B" : "#EF4444" },
        ].map((s) => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: '#FFFFFF'}}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="two-col">
        <div className="pipeline-card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <div className="card-title">Deliverability Pipeline</div>
          </div>
          {[
            { label: "Total Imported", val: liveStats.totalLeads, pct: 100 },
            { label: "Syntax Valid", val: liveStats.syntaxValid, pct: t > 0 ? (liveStats.syntaxValid / t) * 100 : 0 },
            { label: "MX Valid", val: liveStats.mxValid, pct: t > 0 ? (liveStats.mxValid / t) * 100 : 0 },
            { label: "Address Validated", val: liveStats.sendgridVerified || 0, pct: t > 0 ? ((liveStats.sendgridVerified || 0) / t) * 100 : 0 },
            { label: "Clean / Deliverable", val: liveStats.cleanLeads, pct: t > 0 ? (liveStats.cleanLeads / t) * 100 : 0 },
          ].map((s) => (
            <div key={s.label} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 12, color: "#CBD5E1" }}>{s.label}</span>
                <span style={{ fontSize: 12, fontFamily: "var(--mono)", color: "#F1F5F9" }}>{(s.val || 0).toLocaleString()}</span>
              </div>
              <div className="pipeline-bar">
                <div className="pipeline-fill" style={{ width: Math.max(2, s.pct) + "%", background: s.pct > 80 ? "#10B981" : s.pct > 50 ? "#F59E0B" : "#EF4444" }} />
              </div>
            </div>
          ))}
        </div>

        <div className="pipeline-card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <div className="card-title">Health Summary</div>
          </div>
          {[
            { metric: "Syntax Validation Pass Rate", value: ((liveStats.syntaxValid / t) * 100).toFixed(1) + "%", status: liveStats.syntaxValid / t > 0.7 ? "Healthy" : "Warning" },
            { metric: "MX Record Pass Rate", value: ((liveStats.mxValid / t) * 100).toFixed(1) + "%", status: liveStats.mxValid / t > 0.7 ? "Healthy" : "Warning" },
            { metric: "Address Validation", value: verifiedRate + "%", status: parseFloat(verifiedRate) > 70 ? "Healthy" : "Warning" },
            { metric: "Overall Clean Rate", value: deliveryRate + "%", status: parseFloat(deliveryRate) > 70 ? "Healthy" : "Warning" },
            { metric: "Elimination Rate", value: elimRate + "%", status: parseFloat(elimRate) < 35 ? "Normal" : "High" },
            { metric: "Bounce Rate", value: bounceRate + "%", status: parseFloat(bounceRate) < 3 ? "Healthy" : "Warning" },
          ].map((r) => (
            <div key={r.metric} className="metric-row">
              <div className="metric-name">{r.metric}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className="metric-val">{r.value}</span>
                <span className={"tag " + (r.status === "Healthy" || r.status === "Normal" ? "tag-green" : "tag-yellow")}>{r.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="table-card">
        <div className="card-header" style={{ padding: "18px 20px 14px" }}>
          <div className="card-title">Elimination Breakdown</div>
          <span className="tag tag-red">{fmt(liveStats.eliminated)} eliminated</span>
        </div>
        <table>
          <thead>
            <tr><th>Reason</th><th>Count</th><th>% of Total</th></tr>
          </thead>
          <tbody>
            {(liveStats.eliminationReasons || []).map((r) => (
              <tr key={r.reason}>
                <td className="td-name">{r.reason}</td>
                <td>{Number(r.cnt).toLocaleString()}</td>
                <td style={{ fontFamily: "var(--mono)" }}>{(r.cnt / t * 100).toFixed(1) + "%"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
function ScoreHistoryTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetch('https://tetrus.ai/api/score-history/hot/all', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setContacts(d.contacts || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const fmtDate = (dt) => {
    const d = new Date(dt);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)' }}>Loading score history...</div>
  );

  if (contacts.length === 0) return (
    <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>No score history yet</div>
      <div style={{ fontSize: 12 }}>Score changes for hot opportunity contacts (61+ pts) will appear here.</div>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
        Score timeline for {contacts.length} hot opportunity contact{contacts.length !== 1 ? 's' : ''} · tracked every 5 minutes
      </div>
      {contacts.map(contact => {
        const isExpanded = expanded === contact.email;
        const hasHistory = contact.history.length > 0;
        const latestScore = contact.currentScore;
        const earliest = hasHistory ? contact.history[contact.history.length - 1] : null;
        const totalDelta = hasHistory
          ? contact.currentScore - (earliest?.score_before ?? contact.currentScore)
          : 0;

        return (
          <div key={contact.email} style={{
            background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 10, marginBottom: 12, overflow: 'hidden',
          }}>
            {/* Contact header row */}
            <div
              onClick={() => setExpanded(isExpanded ? null : contact.email)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                cursor: 'pointer', userSelect: 'none',
              }}
            >
              {/* Score badge */}
              <div style={{
                width: 48, height: 48, borderRadius: 10, flexShrink: 0,
                background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column',
              }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#ef4444', lineHeight: 1 }}>{latestScore}</div>
                <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 1 }}>pts</div>
              </div>

              {/* Name + email */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{contact.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{contact.email}</div>
                {contact.company && (
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{contact.company}</div>
                )}
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', gap: 20, flexShrink: 0 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: totalDelta > 0 ? '#10b981' : 'var(--text3)' }}>
                    {totalDelta > 0 ? '+' : ''}{totalDelta}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>total gain</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text2)' }}>{contact.history.length}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>events</div>
                </div>
              </div>

              {/* Expand chevron */}
              <div style={{ color: 'var(--text3)', fontSize: 12, flexShrink: 0, marginLeft: 4 }}>
                {isExpanded ? '▲' : '▼'}
              </div>
            </div>

            {/* Timeline */}
            {isExpanded && (
              <div style={{ borderTop: '1px solid var(--border)', padding: '0 18px 16px' }}>
                {!hasHistory ? (
                  <div style={{ padding: '16px 0', fontSize: 12, color: 'var(--text3)' }}>
                    No score change events recorded yet. Changes are logged on the next 5-minute poll.
                  </div>
                ) : (
                  <div style={{ position: 'relative', paddingTop: 16 }}>
                    {/* Vertical timeline line */}
                    <div style={{
                      position: 'absolute', left: 15, top: 24, bottom: 8,
                      width: 1, background: 'var(--border)',
                    }} />
                    {contact.history.map((ev, i) => {
                      const d = ev.delta;
                      const isPositive = d > 0;
                      const dotColor = isPositive ? '#10b981' : '#ef4444';
                      return (
                        <div key={ev.id} style={{ display: 'flex', gap: 16, marginBottom: 14, position: 'relative' }}>
                          {/* Dot */}
                          <div style={{
                            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                            background: isPositive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                            border: `1px solid ${dotColor}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 700, color: dotColor, zIndex: 1,
                            position: 'relative',
                          }}>
                            {isPositive ? '+' : '−'}
                          </div>

                          {/* Content */}
                          <div style={{ flex: 1, paddingTop: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{ev.reason}</span>
                              <span style={{
                                fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                                background: isPositive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                                color: dotColor,
                              }}>
                                {isPositive ? '+' : ''}{d}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3, display: 'flex', gap: 10 }}>
                              <span>{ev.score_before} → <strong style={{ color: 'var(--text2)' }}>{ev.score_after}</strong></span>
                              <span style={{ color: 'var(--border2)' }}>·</span>
                              <span>{fmtDate(ev.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ============================================================
// INTELLIGENCE VIEW
// ============================================================
function IntelligenceView() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    fetch("/api/intelligence/summary", {
      headers: { "X-API-Key": "791457bcf3d49ee84acd659e73b28a38bf39578a8f8cd065a57d83470bf343b1" }
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const EVENT_LABELS = {
    bounce_400: "Soft Bounce (400)",
    bounce_500: "Hard Bounce (500)",
    opt_out: "Opt Out",
    human_reply: "Human Reply",
    out_of_office: "Out of Office"
  };
  const EVENT_COLORS = {
    bounce_400: "#f59e0b",
    bounce_500: "#ef4444",
    opt_out: "#8b5cf6",
    human_reply: "#22c55e",
    out_of_office: "#0ea5e9"
  };
  const EVENT_TYPES = ["bounce_400", "bounce_500", "opt_out", "human_reply", "out_of_office"];

  const thS = { textAlign: "left", padding: "10px 12px", color: "#CBD5E1", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "var(--surface)" };
  const tdS = { padding: "10px 12px", fontSize: 13, color: "#E2E8F0", borderBottom: "1px solid #111827" };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 13 }}>Loading intelligence data...</div>;
  if (error) return <div style={{ padding: 40, textAlign: "center", color: "#ef4444", fontSize: 13 }}>Error: {error}</div>;

  const summary = (data && data.summary) || [];

  const totals = {};
  for (const row of summary) {
    totals[row.event_type] = (totals[row.event_type] || 0) + parseInt(row.count || 0);
  }
  const maxCount = Math.max(1, ...Object.values(totals));

  const grouped = {};
  for (const row of summary) {
    if (!grouped[row.event_type]) grouped[row.event_type] = [];
    grouped[row.event_type].push(row);
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 16 }}>Event Breakdown -- Last 30 Days</div>
        {summary.length === 0 ? (
          <div style={{ color: "#475569", fontSize: 13 }}>No events recorded yet.</div>
        ) : (
          EVENT_TYPES.map(et => {
            const count = totals[et] || 0;
            const pct = Math.round((count / maxCount) * 100);
            const color = EVENT_COLORS[et] || "#64748b";
            return (
              <div key={et} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div style={{ width: 160, fontSize: 12, color: "#CBD5E1", flexShrink: 0 }}>{EVENT_LABELS[et] || et}</div>
                <div style={{ flex: 1, background: "var(--surface2)", borderRadius: 4, height: 18, overflow: "hidden" }}>
                  <div style={{ width: pct + "%", background: color, height: "100%", borderRadius: 4, transition: "width 0.3s ease", opacity: 0.85 }} />
                </div>
                <div style={{ width: 40, textAlign: "right", fontSize: 13, fontWeight: 700, color, flexShrink: 0 }}>{count}</div>
              </div>
            );
          })
        )}
      </div>

      {summary.length > 0 && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Domain & Provider Breakdown</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thS}>Event Type</th>
                  <th style={thS}>Domain</th>
                  <th style={thS}>Provider</th>
                  <th style={thS}>Campaign</th>
                  <th style={{ ...thS, textAlign: "right" }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                    <td style={tdS}>
                      <span style={{ display: "inline-block", fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: (EVENT_COLORS[row.event_type] || "#64748b") + "26", color: EVENT_COLORS[row.event_type] || "#94a3b8", border: "1px solid " + (EVENT_COLORS[row.event_type] || "#64748b") + "4d" }}>
                        {EVENT_LABELS[row.event_type] || row.event_type}
                      </span>
                    </td>
                    <td style={{ ...tdS, fontFamily: "var(--mono)", fontSize: 12, color: "#94a3b8" }}>{row.domain || "\u2014"}</td>
                    <td style={{ ...tdS, fontSize: 12, color: "#94a3b8" }}>{row.provider || "\u2014"}</td>
                    <td style={{ ...tdS, fontFamily: "var(--mono)", fontSize: 11, color: "#64748b" }}>{row.campaign_id || "\u2014"}</td>
                    <td style={{ ...tdS, textAlign: "right", fontWeight: 700, color: "#E2E8F0" }}>{parseInt(row.count || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Pipeline() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispositions, setDispositions] = useState({});
  const [saving, setSaving] = useState({});
  const [openPanel, setOpenPanel] = useState(null);
  const [panelDisp, setPanelDisp] = useState(null);
  const [panelNotes, setPanelNotes] = useState('');

  const DISP_OPTIONS = [
    { label: "Called - No Answer", value: "called_no_answer", color: "#f59e0b" },
    { label: "Left Voicemail", value: "left_voicemail", color: "#f59e0b" },
    { label: "Connected - Not Interested", value: "connected_not_interested", color: "#94a3b8" },
    { label: "Connected - Interested", value: "connected_interested", color: "#0ea5e9" },
    { label: "Meeting Booked", value: "meeting_booked", color: "#10b981" },
    { label: "Disqualified", value: "disqualified", color: "#ef4444" },
  ];
  const DISP_LABELS = {
    called_no_answer: "Called - No Answer",
    left_voicemail: "Left Voicemail",
    connected_not_interested: "Not Interested",
    connected_interested: "Interested",
    meeting_booked: "Meeting Booked",
    disqualified: "Disqualified",
    interested: "Interested",
    not_interested: "Not Interested",
    no_answer: "No Answer",
  };
  const DISP_COLORS = {
    called_no_answer: "#f59e0b",
    left_voicemail: "#f59e0b",
    connected_not_interested: "#94a3b8",
    connected_interested: "#0ea5e9",
    meeting_booked: "#10b981",
    disqualified: "#ef4444",
    interested: "#0ea5e9",
    not_interested: "#94a3b8",
    no_answer: "#f59e0b",
  };

  const load = () => {
    setLoading(true);
    fetch("/api/contacts/all?min_score=61&page=1", { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setContacts(d.contacts || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openLogPanel = (contact) => {
    setOpenPanel(contact.email);
    setPanelDisp(null);
    setPanelNotes('');
  };

  const closePanel = () => {
    setOpenPanel(null);
    setPanelDisp(null);
    setPanelNotes('');
  };

  const saveDisposition = async (contact) => {
    if (!panelDisp) return;
    setSaving(p => ({ ...p, [contact.email]: true }));
    try {
      const res = await fetch("/api/contacts/" + contact.id + "/disposition", {
        method: "POST",
        headers: { ...apiHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ disposition: panelDisp, notes: panelNotes }),
      });
      if (!res.ok) throw new Error("Failed");
      setDispositions(p => ({ ...p, [contact.email]: panelDisp }));
      closePanel();
    } catch (e) { alert("Failed to save: " + e.message); }
    setSaving(p => ({ ...p, [contact.email]: false }));
  };

  const whatTheyDid = (c) => {
    const parts = [];
    if (c.open_count > 0) parts.push("Opened " + c.open_count + "+");
    if (c.click_count > 0) parts.push("Clicked " + c.click_count + "+");
    if (!parts.length && c.emails_received > 0) parts.push(c.emails_received + " emails sent");
    return parts.join(", ") || "—";
  };

  const fmtDate = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      if (d.getFullYear() < 2000) return "—";
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return "—"; }
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: 60, color: "var(--text3)", fontSize: 13 }}>Loading hot opportunities...</div>
  );

  return (
    <div className="table-card">
      <div className="card-header" style={{ padding: "18px 20px 14px" }}>
        <div>
          <div className="card-title">Hot Opportunities</div>
          <div className="card-sub" style={{ marginTop: 4 }}>Contacts with 61+ engagement points ready for human outreach</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="tag tag-red">{contacts.length} contacts</span>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={load}>Refresh</button>
        </div>
      </div>
      {contacts.length === 0 ? (
        <div style={{ padding: 48, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
          No contacts with 61+ engagement points yet. Keep running campaigns!
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Company</th>
              <th>Score</th>
              <th>Emails Received</th>
              <th>Opened</th>
              <th>Last Active</th>
              <th>What They Did</th>
              <th style={{ minWidth: 180 }}>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => {
              const disp = dispositions[c.email];
              const isSaving = !!saving[c.email];
              const isPanelOpen = openPanel === c.email;
              const isBooked = disp === "meeting_booked";
              const scoreColor = c.score >= 80 ? "#10b981" : c.score >= 61 ? "#f59e0b" : "#cbd5e1";
              return (
                <React.Fragment key={c.email}>
                  <tr style={{ background: isBooked ? "rgba(16,185,129,0.06)" : undefined }}>
                    <td className="td-name">
                      <div style={{ fontWeight: 600, color: isBooked ? "#10b981" : "#F1F5F9" }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>{c.email}</div>
                    </td>
                    <td style={{ color: "#CBD5E1" }}>{c.company || "—"}</td>
                    <td>
                      <span style={{ fontFamily: "var(--mono)", fontWeight: 700, fontSize: 14, color: scoreColor }}>{c.score}</span>
                    </td>
                    <td style={{ color: "#CBD5E1", fontFamily: "var(--mono)" }}>{c.emails_received || 0}</td>
                    <td style={{ color: "#10b981", fontWeight: 600, fontFamily: "var(--mono)" }}>{c.open_count || 0}</td>
                    <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtDate(c.last_engaged_at)}</td>
                    <td style={{ color: "#94a3b8", fontSize: 12 }}>{whatTheyDid(c)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {disp ? (
                        <span style={{ padding: "4px 10px", borderRadius: 5, fontSize: 11, fontWeight: 700, background: (DISP_COLORS[disp] || "#cbd5e1") + "22", color: DISP_COLORS[disp] || "#cbd5e1" }}>
                          {DISP_LABELS[disp] || disp}
                        </span>
                      ) : (
                        <button onClick={() => isPanelOpen ? closePanel() : openLogPanel(c)} style={{
                          fontSize: 11, padding: "5px 12px", borderRadius: 5, cursor: "pointer",
                          background: isPanelOpen ? "rgba(99,102,241,0.15)" : "transparent",
                          border: "1px solid " + (isPanelOpen ? "#6366f1" : "#475569"),
                          color: isPanelOpen ? "#818cf8" : "#94a3b8", fontWeight: 600,
                        }}>
                          {isPanelOpen ? "✕ Cancel" : "Log Outcome"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isPanelOpen && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0, background: "rgba(15,23,42,0.6)" }}>
                        <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(99,102,241,0.3)", borderBottom: "1px solid rgba(99,102,241,0.3)" }}>
                          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10, fontWeight: 600 }}>SELECT OUTCOME FOR {c.name.toUpperCase()}</div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                            {DISP_OPTIONS.map(opt => (
                              <button key={opt.value} onClick={() => setPanelDisp(opt.value)} style={{
                                fontSize: 12, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
                                background: panelDisp === opt.value ? opt.color + "33" : "transparent",
                                border: "1px solid " + (panelDisp === opt.value ? opt.color : opt.color + "66"),
                                color: panelDisp === opt.value ? opt.color : opt.color + "cc",
                                transition: "all 0.15s",
                              }}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                            <textarea
                              placeholder="Notes (optional)..."
                              value={panelNotes}
                              onChange={e => setPanelNotes(e.target.value)}
                              rows={2}
                              style={{
                                flex: 1, background: "rgba(30,41,59,0.8)", border: "1px solid #334155",
                                borderRadius: 6, color: "#CBD5E1", fontSize: 12, padding: "8px 10px",
                                resize: "vertical", fontFamily: "inherit", outline: "none",
                              }}
                            />
                            <button
                              disabled={!panelDisp || isSaving}
                              onClick={() => saveDisposition(c)}
                              style={{
                                padding: "8px 20px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                                cursor: panelDisp && !isSaving ? "pointer" : "default",
                                background: panelDisp ? "#6366f1" : "#1e293b",
                                border: "1px solid " + (panelDisp ? "#6366f1" : "#334155"),
                                color: panelDisp ? "#fff" : "#475569",
                                transition: "all 0.15s", whiteSpace: "nowrap",
                              }}
                            >
                              {isSaving ? "Saving..." : "Save"}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AccountsView({ onFilterByAccount, setActive }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 200;

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch("/api/accounts?page=" + page + "&limit=" + PAGE_SIZE, { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setAccounts(d.accounts || []); setTotal(d.total || (d.accounts || []).length); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [page]);

  const fmtDate = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      if (d.getFullYear() < 2000) return "—";
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return "—"; }
  };

  const scoreColor = (s) => {
    const n = parseFloat(s);
    if (n >= 60) return "var(--green)";
    if (n >= 30) return "var(--yellow)";
    return "var(--red)";
  };

  const filtered = search
    ? accounts.filter(a => (a.company_name || "").toLowerCase().includes(search.toLowerCase()))
    : accounts;

  return (
    <div className="table-card">
      <div className="card-header" style={{ padding: "18px 20px 14px" }}>
        <div>
          <div className="card-title">Accounts</div>
          <div className="card-sub" style={{ marginTop: 4 }}>
            {loading ? "Loading…" : total.toLocaleString() + " companies · click a name to view contacts"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search companies…"
            style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid var(--border2)", background: "var(--surface2)", color: "var(--text)", fontSize: 12, fontFamily: "var(--mono)", width: 220, outline: "none" }}
          />
        </div>
      </div>
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>Loading accounts…</div>
      ) : error ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--red)", fontSize: 13 }}>{error}</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>No accounts found.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Industry</th>
              <th>State</th>
              <th style={{ textAlign: "center" }}>Contacts</th>
              <th>Avg Score</th>
              <th style={{ textAlign: "center" }}>Active</th>
              <th>Last Activity</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a, i) => (
              <tr key={i}>
                <td className="td-name">
                  <div
                    style={{ fontWeight: 600, color: "var(--accent)", cursor: "pointer" }}
                    onClick={() => { if (onFilterByAccount) onFilterByAccount(a.company_name); if (setActive) setActive("contacts"); }}
                  >
                    {a.company_name}
                  </div>
                  {a.company_url && (
                    <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)", marginTop: 2 }}>{a.company_url}</div>
                  )}
                </td>
                <td style={{ color: "var(--text2)" }}>{a.company_industry || "—"}</td>
                <td style={{ color: "var(--text2)", fontFamily: "var(--mono)", fontSize: 12 }}>{a.company_state || "—"}</td>
                <td style={{ textAlign: "center", color: "var(--text2)", fontFamily: "var(--mono)" }}>{a.contact_count}</td>
                <td>
                  <span style={{ fontFamily: "var(--mono)", fontWeight: 700, color: scoreColor(a.avg_score) }}>{a.avg_score}</span>
                </td>
                <td style={{ textAlign: "center", color: "var(--text2)", fontFamily: "var(--mono)" }}>{a.active_contacts}</td>
                <td style={{ color: "var(--text3)", fontSize: 12 }}>{fmtDate(a.last_activity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && !error && total > PAGE_SIZE && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, padding: "16px 0 8px" }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{ padding: "6px 16px", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 6, color: page <= 1 ? "#475569" : "var(--text)", cursor: page <= 1 ? "default" : "pointer", fontSize: 13 }}
          >← Prev</button>
          <span style={{ fontSize: 12, color: "#64748b" }}>Page {page} of {Math.ceil(total / PAGE_SIZE)}</span>
          <button
            onClick={() => setPage(p => Math.min(Math.ceil(total / PAGE_SIZE), p + 1))}
            disabled={page >= Math.ceil(total / PAGE_SIZE)}
            style={{ padding: "6px 16px", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 6, color: page >= Math.ceil(total / PAGE_SIZE) ? "#475569" : "var(--text)", cursor: page >= Math.ceil(total / PAGE_SIZE) ? "default" : "pointer", fontSize: 13 }}
          >Next →</button>
        </div>
      )}
    </div>
  );
}

function ContactsView({ accountFilter, onClearAccountFilter }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = (p, s) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p || page, search: s !== undefined ? s : search });
    fetch("/api/contacts/all?" + params.toString(), { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setContacts(d.contacts || []); setTotal(d.total || 0); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (accountFilter) {
      setSearch(accountFilter);
      load(1, accountFilter);
    } else {
      load(1, "");
    }
  }, [accountFilter]);

  const doSearch = (val) => {
    setSearch(val);
    setPage(1);
    load(1, val);
  };

  const fmtDate = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      if (d.getFullYear() < 2000) return "—";
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return "—"; }
  };

  const scoreColor = (n) => n >= 80 ? "#10b981" : n >= 61 ? "#f59e0b" : n >= 31 ? "#0ea5e9" : "#94a3b8";
  const statusTag = (n) => n >= 61 ? "tag-red" : n >= 31 ? "tag-yellow" : "tag-blue";

  return (
    <div className="table-card">
      <div className="card-header" style={{ padding: "18px 20px 14px" }}>
        <div>
          <div className="card-title">All Contacts</div>
          <div className="card-sub" style={{ marginTop: 4 }}>
            {loading ? "Loading…" : total.toLocaleString() + " total contacts with engagement data"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {accountFilter && onClearAccountFilter && (
            <button className="btn btn-ghost" style={{ fontSize: 11, padding: "5px 10px", color: "var(--accent)", borderColor: "var(--accent)" }} onClick={onClearAccountFilter}>✕ {accountFilter}</button>
          )}
          <input
            value={search}
            onChange={e => doSearch(e.target.value)}
            placeholder="Search name or company…"
            style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid var(--border2)", background: "var(--surface2)", color: "var(--text)", fontSize: 12, fontFamily: "var(--mono)", width: 220, outline: "none" }}
          />
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => load(page, search)}>Refresh</button>
        </div>
      </div>
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>Loading contacts…</div>
      ) : contacts.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>No contacts found.</div>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>List</th>
                <th>Score</th>
                <th>Emails Received</th>
                <th>Opens</th>
                <th>Clicks</th>
                <th>Last Active</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.email}>
                  <td className="td-name">
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{c.email}</div>
                  </td>
                  <td style={{ color: "#CBD5E1" }}>{c.company || "—"}</td>
                  <td style={{ color: "#94a3b8", fontSize: 12, fontFamily: "var(--mono)" }}>{c.list_id || "—"}</td>
                  <td>
                    <span style={{ fontFamily: "var(--mono)", fontWeight: 700, color: scoreColor(c.score) }}>{c.score}</span>
                  </td>
                  <td style={{ color: "#CBD5E1", fontFamily: "var(--mono)" }}>{c.emails_received || 0}</td>
                  <td style={{ color: "#10b981", fontFamily: "var(--mono)", fontWeight: 600 }}>{c.open_count || 0}</td>
                  <td style={{ color: "#0ea5e9", fontFamily: "var(--mono)" }}>{c.click_count || 0}</td>
                  <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtDate(c.last_engaged_at)}</td>
                  <td>
                    <span className={"tag " + statusTag(c.score)} style={{ fontSize: 10 }}>{c.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > 50 && (
            <div style={{ padding: "14px 20px", display: "flex", gap: 8, alignItems: "center", borderTop: "1px solid var(--border)" }}>
              <button className="btn btn-ghost" style={{ fontSize: 12 }} disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(p, search); }}>‹ Prev</button>
              <span style={{ fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)" }}>Page {page}</span>
              <button className="btn btn-ghost" style={{ fontSize: 12 }} disabled={contacts.length < 50} onClick={() => { const p = page + 1; setPage(p); load(p, search); }}>Next ›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch('https://tetrus.ai/api/analytics', { headers: apiHeaders })
      .then(r => r.json()).then(d => setData(d)).catch(() => {});
  }, []);

  const fmt = (n) => n != null ? Number(n).toLocaleString() : '—';
  const s = data?.summary || {};
  const eng = data?.engagement || {};
  const dist = data?.scoreDistribution || {};
  const byList = data?.byList || [];

  return (
    <>
      <div className="stat-grid">
        {[
          { label: "Total Qualified", value: data ? fmt(s.qualified) : "—", sub: fmt(s.industryCount) + " industries", color: "#10B981" },
          { label: "Engaged Leads", value: data ? fmt(eng.engagedLeads) : "—", sub: fmt(eng.humanEvents) + " human events", color: "#0EA5E9" },
          { label: "Avg. Human Score", value: data ? String(s.avgHumanScore) : "—", sub: fmt(s.activeLeads) + " active leads", color: "#8B5CF6" },
          { label: "Human Replies", value: data ? fmt(eng.replies) : "—", sub: fmt(eng.humanClicks) + " human clicks", color: "#F59E0B" },
        ].map((st) => (
          <div key={st.label} className="stat-card">
            <div className="stat-label">{st.label}</div>
            <div className="stat-value" style={{ color: '#FFFFFF'}}>{st.value}</div>
            <div className="stat-sub">{st.sub}</div>
          </div>
        ))}
      </div>
      <div className="two-col">
        <div className="pipeline-card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <div className="card-title">Lead Quality Distribution</div>
          </div>
          {[
            { label: "Score 90\u2013100 (Top Tier)", val: dist.score_90_100 || 0, pct: s.activeLeads > 0 ? ((dist.score_90_100 || 0) / s.activeLeads) * 100 : 0, color: "#10B981" },
            { label: "Score 80\u201389 (High)", val: dist.score_80_89 || 0, pct: s.activeLeads > 0 ? ((dist.score_80_89 || 0) / s.activeLeads) * 100 : 0, color: "#0EA5E9" },
            { label: "Score 70\u201379 (Qualified)", val: dist.score_70_79 || 0, pct: s.activeLeads > 0 ? ((dist.score_70_79 || 0) / s.activeLeads) * 100 : 0, color: "#8B5CF6" },
            { label: "Score 50\u201369 (Nurture)", val: dist.score_50_69 || 0, pct: s.activeLeads > 0 ? ((dist.score_50_69 || 0) / s.activeLeads) * 100 : 0, color: "#F59E0B" },
            { label: "Score < 50 (Low)", val: dist.score_below_50 || 0, pct: s.activeLeads > 0 ? ((dist.score_below_50 || 0) / s.activeLeads) * 100 : 0, color: "#EF4444" },
          ].map((bar) => (
            <div key={bar.label} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 12, color: "#CBD5E1" }}>{bar.label}</span>
                <span style={{ fontSize: 12, fontFamily: "var(--mono)", color: "#F1F5F9" }}>{fmt(bar.val)}</span>
              </div>
              <div className="pipeline-bar">
                <div className="pipeline-fill" style={{ width: Math.max(2, bar.pct) + "%", background: bar.color }} />
              </div>
            </div>
          ))}
        </div>
        <div className="table-card">
          <div className="card-header" style={{ padding: "18px 20px 14px" }}>
            <div className="card-title">Performance by List</div>
          </div>
          <table>
            <thead>
              <tr><th>List</th><th>Total</th><th>Qualified</th><th>Avg Score</th><th>Clean</th></tr>
            </thead>
            <tbody>
              {byList.map((r) => (
                <tr key={r.list_id}>
                  <td className="td-name">{r.list_id}</td>
                  <td>{fmt(r.total)}</td>
                  <td style={{ fontWeight: 600, color: "#10B981", fontFamily: "var(--mono)" }}>{fmt(r.qualified)}</td>
                  <td>{r.avg_score}</td>
                  <td>{fmt(r.clean)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="table-card">
        <div className="card-header" style={{ padding: "18px 20px 14px" }}>
          <div className="card-title">Performance by Industry</div>
          <span className="tag tag-blue">{fmt(s.industryCount)} industries</span>
        </div>
        <table>
          <thead>
            <tr><th>Industry</th><th>Total Leads</th><th>Qualified</th><th>Avg Score</th><th>High Score</th></tr>
          </thead>
          <tbody>
            {(data?.byIndustry || []).map((r) => (
              <tr key={r.industry}>
                <td className="td-name">{r.industry}</td>
                <td>{fmt(r.total)}</td>
                <td style={{ fontWeight: 600, color: "#10B981", fontFamily: "var(--mono)" }}>{fmt(r.qualified)}</td>
                <td>{r.avg_score}</td>
                <td>{fmt(r.high_score)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ScoringRulesTab() {
  const [rules, setRules] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('https://tetrus.ai/api/scoring-rules', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => {
        setRules(d.rules || []);
        const init = {};
        (d.rules || []).forEach(r => { init[r.event_type] = String(r.points); });
        setDraft(init);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    const payload = rules.map(r => ({ event_type: r.event_type, points: parseInt(draft[r.event_type]) || 0 }));
    try {
      const res = await fetch('https://tetrus.ai/api/scoring-rules', {
        method: 'PUT',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules: payload }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) { setError(d.error || 'Save failed.'); setSaving(false); return; }
      setRules(d.rules);
      const updated = {};
      d.rules.forEach(r => { updated[r.event_type] = String(r.points); });
      setDraft(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { setError('Network error.'); }
    setSaving(false);
  };

  const isDirty = rules.some(r => String(r.points) !== draft[r.event_type]);

  const eventIcon = {
    email_open:  '✉',
    email_click: '↗',
    page_visit:  '⊙',
    form_submit: '◫',
    meeting:     '◷',
  };

  const eventColor = {
    email_open:  '#0EA5E9',
    email_click: '#8B5CF6',
    page_visit:  '#10B981',
    form_submit: '#F59E0B',
    meeting:     '#EF4444',
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading scoring rules...</div>;

  const totalMax = rules.reduce((s, r) => s + (parseInt(draft[r.event_type]) || 0), 0);

  return (
    <>
      <div className="pipeline-card" style={{ marginBottom: 14 }}>
        <div className="card-header" style={{ marginBottom: 20 }}>
          <div>
            <div className="card-title">Lead Scoring Rules</div>
            <div className="card-sub" style={{ marginTop: 3 }}>POINT VALUES ASSIGNED PER ENGAGEMENT EVENT</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {saved && <span style={{ fontSize: 12, color: 'var(--green)', fontFamily: 'var(--mono)' }}>✓ Saved</span>}
            {error && <span style={{ fontSize: 12, color: 'var(--red)' }}>{error}</span>}
            <button
              className={'btn ' + (isDirty ? 'btn-primary' : 'btn-ghost')}
              onClick={handleSave}
              disabled={saving || !isDirty}
              style={{ opacity: isDirty ? 1 : 0.5 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {rules.map(rule => {
          const pts = parseInt(draft[rule.event_type]) || 0;
          const maxPts = 100;
          const barPct = Math.min(100, (pts / maxPts) * 100);
          const color = eventColor[rule.event_type] || '#0EA5E9';
          const changed = String(rule.points) !== draft[rule.event_type];

          return (
            <div key={rule.event_type} style={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr 180px 80px',
              alignItems: 'center',
              gap: 16,
              padding: '14px 0',
              borderBottom: '1px solid var(--border)',
            }}>
              {/* Icon */}
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: color + '18',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, color,
              }}>
                {eventIcon[rule.event_type] || '◈'}
              </div>

              {/* Label + desc + bar */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{rule.label}</span>
                  {changed && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#F59E0B22', color: '#F59E0B', fontFamily: 'var(--mono)' }}>modified</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>{rule.description}</div>
                <div style={{ height: 4, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: barPct + '%', height: '100%', background: color, borderRadius: 2, transition: 'width 0.3s' }} />
                </div>
              </div>

              {/* Slider */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <input
                  type="range" min="0" max="100" step="1"
                  value={pts}
                  onChange={e => setDraft(prev => ({ ...prev, [rule.event_type]: e.target.value }))}
                  style={{ width: '100%', accentColor: color, cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                  <span>0</span><span>50</span><span>100</span>
                </div>
              </div>

              {/* Points input */}
              <div style={{ position: 'relative' }}>
                <input
                  type="number" min="0" max="1000"
                  value={draft[rule.event_type] ?? ''}
                  onChange={e => setDraft(prev => ({ ...prev, [rule.event_type]: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px 8px 10px',
                    background: 'var(--surface2)', border: '1px solid ' + (changed ? '#F59E0B' : 'var(--border2)'),
                    borderRadius: 6, color: color, fontSize: 18, fontWeight: 700,
                    fontFamily: 'var(--mono)', outline: 'none', textAlign: 'center',
                    transition: 'border-color 0.2s',
                  }}
                />
                <div style={{ fontSize: 9, color: 'var(--text3)', textAlign: 'center', marginTop: 3, fontFamily: 'var(--mono)' }}>PTS</div>
              </div>
            </div>
          );
        })}

        {/* Summary row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 16, marginTop: 4 }}>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            Max possible score from a single contact completing all events
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>MAX SCORE</span>
            <span style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--accent2)' }}>{totalMax}</span>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>pts</span>
          </div>
        </div>
      </div>

      {/* Stage thresholds reference */}
      <div className="pipeline-card" style={{ marginBottom: 0 }}>
        <div className="card-header" style={{ marginBottom: 14 }}>
          <div className="card-title">Pipeline Stage Thresholds</div>
          <span className="tag tag-blue">Read-only</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {[
            { stage: 'Lead',            range: '1 – 30 pts',  color: '#8B5CF6', desc: 'Engaged, early interest' },
            { stage: 'Opportunity',     range: '31 – 60 pts', color: '#F59E0B', desc: 'Actively researching' },
            { stage: 'Hot Opportunity', range: '61 – 100 pts',color: '#EF4444', desc: 'Ready for outreach' },
            { stage: 'SQL',             range: '100+ pts',    color: '#10B981', desc: 'Sales-qualified, close-ready' },
          ].map(t => (
            <div key={t.stage} style={{ background: 'var(--surface2)', borderRadius: 8, padding: '12px 14px', borderLeft: '3px solid ' + t.color }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>{t.stage}</div>
              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', color: t.color, marginBottom: 4 }}>{t.range}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function WebhooksTab() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', secret: '', events: [] });
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [logWebhook, setLogWebhook] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const EVENTS = [
    { id: 'hot_opportunity', label: 'Hot Opportunity' },
    { id: 'campaign_activated', label: 'Campaign Activated' },
    { id: 'new_lead', label: 'New Lead' },
  ];

  const fetchWebhooks = () => {
    setLoading(true);
    fetch('https://tetrus.ai/api/webhooks', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setWebhooks(d.webhooks || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchWebhooks(); }, []);

  const toggleEvent = (id) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(id) ? f.events.filter(e => e !== id) : [...f.events, id]
    }));
  };

  const handleAdd = async () => {
    setSaveErr('');
    if (!form.name.trim()) { setSaveErr('Name is required.'); return; }
    if (!form.url.trim() || !form.url.startsWith('http')) { setSaveErr('Valid URL is required.'); return; }
    if (form.events.length === 0) { setSaveErr('Select at least one event.'); return; }
    setSaving(true);
    try {
      const r = await fetch('https://tetrus.ai/api/webhooks', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, url: form.url, secret: form.secret, events: form.events, active: true }),
      });
      const d = await r.json();
      if (!r.ok) { setSaveErr(d.error || 'Save failed'); setSaving(false); return; }
      setForm({ name: '', url: '', secret: '', events: [] });
      setShowAdd(false);
      fetchWebhooks();
    } catch { setSaveErr('Network error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    await fetch('https://tetrus.ai/api/webhooks/' + id, { method: 'DELETE', headers: apiHeaders });
    if (logWebhook === id) { setLogWebhook(null); setDeliveries([]); }
    fetchWebhooks();
  };

  const handleToggleActive = async (wh) => {
    await fetch('https://tetrus.ai/api/webhooks/' + wh.id, {
      method: 'PATCH',
      headers: { ...apiHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !wh.active }),
    });
    fetchWebhooks();
  };

  const viewLog = async (id) => {
    if (logWebhook === id) { setLogWebhook(null); setDeliveries([]); return; }
    setLogWebhook(id);
    setLogLoading(true);
    const r = await fetch('https://tetrus.ai/api/webhooks/' + id + '/deliveries', { headers: apiHeaders });
    const d = await r.json();
    setDeliveries(d.deliveries || []);
    setLogLoading(false);
  };

  const inp = {
    width: '100%', padding: '8px 12px', borderRadius: 6,
    border: '1px solid var(--border2)', background: 'var(--surface2)',
    color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)', outline: 'none', marginBottom: 10,
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Webhooks</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Receive real-time POST notifications for platform events</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowAdd(!showAdd); setSaveErr(''); }}>
          {showAdd ? 'Cancel' : '+ Add Webhook'}
        </button>
      </div>

      {showAdd && (
        <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: 18, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 14, color: 'var(--text2)' }}>New Webhook</div>
          <input placeholder="Name (e.g. Zapier CRM)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} />
          <input placeholder="Endpoint URL (https://...)" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} style={inp} />
          <input placeholder="Secret (optional — used for HMAC signature)" value={form.secret} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} style={inp} />
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8 }}>EVENTS TO SUBSCRIBE</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {EVENTS.map(ev => (
              <div key={ev.id}
                onClick={() => toggleEvent(ev.id)}
                style={{
                  padding: '5px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                  background: form.events.includes(ev.id) ? 'var(--accent)' : 'var(--surface)',
                  color: form.events.includes(ev.id) ? '#fff' : 'var(--text3)',
                  border: '1px solid ' + (form.events.includes(ev.id) ? 'var(--accent)' : 'var(--border2)'),
                }}>
                {ev.label}
              </div>
            ))}
          </div>
          {saveErr && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8 }}>{saveErr}</div>}
          <button className="btn btn-primary" onClick={handleAdd} disabled={saving} style={{ width: '100%' }}>
            {saving ? 'Saving...' : 'Save Webhook'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>
      ) : webhooks.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', background: 'var(--surface2)', borderRadius: 8, border: '1px dashed var(--border2)' }}>
          No webhooks configured yet.<br />
          <span style={{ fontSize: 11 }}>Add one above to start receiving event notifications.</span>
        </div>
      ) : (
        <div>
          {webhooks.map(wh => {
            const evArr = Array.isArray(wh.events) ? wh.events : (typeof wh.events === 'string' ? JSON.parse(wh.events) : []);
            return (
              <div key={wh.id} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 10 }}>
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{wh.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wh.url}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      {evArr.map(ev => (
                        <span key={ev} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'rgba(14,165,233,0.12)', color: 'var(--accent2)' }}>
                          {ev.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <div
                      onClick={() => handleToggleActive(wh)}
                      className={'toggle ' + (wh.active ? 'on' : 'off')}
                      title={wh.active ? 'Active — click to disable' : 'Disabled — click to enable'}
                    />
                    <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => viewLog(wh.id)}>
                      {logWebhook === wh.id ? 'Hide Log' : 'Log'}
                    </button>
                    <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px', color: 'var(--red)', borderColor: 'rgba(239,68,68,0.3)' }}
                      onClick={() => handleDelete(wh.id)}>
                      Delete
                    </button>
                  </div>
                </div>

                {logWebhook === wh.id && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 10 }}>DELIVERY LOG (last 30)</div>
                    {logLoading ? (
                      <div style={{ color: 'var(--text3)', fontSize: 12 }}>Loading...</div>
                    ) : deliveries.length === 0 ? (
                      <div style={{ color: 'var(--text3)', fontSize: 12 }}>No deliveries yet.</div>
                    ) : (
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ color: 'var(--text3)' }}>
                            <th style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 600 }}>Time</th>
                            <th style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 600 }}>Event</th>
                            <th style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 600 }}>Status</th>
                            <th style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 600 }}>Result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deliveries.map(d => (
                            <tr key={d.id} style={{ borderTop: '1px solid var(--border)' }}>
                              <td style={{ padding: '5px 0', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                                {new Date(d.delivered_at).toLocaleString()}
                              </td>
                              <td style={{ padding: '5px 8px', color: 'var(--text2)' }}>
                                {d.event?.replace(/_/g, ' ')}
                              </td>
                              <td style={{ padding: '5px 8px', fontFamily: 'var(--mono)' }}>
                                <span style={{ color: d.response_status >= 200 && d.response_status < 300 ? 'var(--green)' : 'var(--red)' }}>
                                  {d.response_status || '—'}
                                </span>
                              </td>
                              <td style={{ padding: '5px 0' }}>
                                <span style={{
                                  fontSize: 10, padding: '2px 7px', borderRadius: 10,
                                  background: d.success ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                                  color: d.success ? 'var(--green)' : 'var(--red)',
                                }}>
                                  {d.success ? 'delivered' : 'failed'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}



function ExportScheduler() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: '', industry: '', min_score: 0, frequency: 'weekly', day_of_week: 1 });
  const [industries, setIndustries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [running, setRunning] = useState({});

  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const loadSchedules = () => {
    fetch('/api/export/schedules', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setSchedules(d.schedules || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    loadSchedules();
    fetch('/api/industries', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => setIndustries((d.industries || []).sort((a, b) => a.industry.localeCompare(b.industry)).slice(0, 30).map(i => i.industry)))
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!form.email.includes('@')) { setSaveMsg('Enter a valid email address'); return; }
    setSaving(true); setSaveMsg('');
    try {
      const r = await fetch('/api/export/schedule', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (d.ok) {
        setSaveMsg('Schedule created! First export at ' + new Date(d.next_run_at).toLocaleString());
        setForm({ email: '', industry: '', min_score: 0, frequency: 'weekly', day_of_week: 1 });
        loadSchedules();
      } else {
        setSaveMsg('Error: ' + (d.error || 'Unknown error'));
      }
    } catch (e) {
      setSaveMsg('Error: ' + e.message);
    }
    setSaving(false);
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Delete this export schedule?')) return;
    await fetch('/api/export/schedules/' + id, { method: 'DELETE', headers: apiHeaders });
    loadSchedules();
  };

  const runNow = async (id) => {
    setRunning(r => ({ ...r, [id]: true }));
    try {
      const r = await fetch('/api/export/schedules/' + id + '/run', { method: 'POST', headers: apiHeaders });
      const d = await r.json();
      alert(d.message || 'Export queued');
    } catch {}
    setRunning(r => ({ ...r, [id]: false }));
  };

  const inp = { width: '100%', padding: '8px 12px', background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 6, color: '#fff', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: 11, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5, display: 'block' };

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 4 }}>Export Scheduler</div>
      <div style={{ fontSize: 12, color: '#CBD5E1', marginBottom: 18 }}>AUTOMATED CSV DELIVERY — DAILY OR WEEKLY VIA EMAIL</div>

      {/* Create form */}
      <div style={{ background: '#0f0f1e', border: '1px solid #1a1a2e', borderRadius: 8, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#CBD5E1', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.5px' }}>New Schedule</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={lbl}>Recipient Email</label>
            <input style={inp} type="email" placeholder="you@company.com" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          <div>
            <label style={lbl}>Industry Filter <span style={{ color: '#CBD5E1' }}>(optional)</span></label>
            <select style={inp} value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}>
              <option value="">All industries</option>
              {industries.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Min Human Score <span style={{ color: '#00d4ff' }}>{form.min_score}+</span></label>
            <input style={inp} type="range" min="0" max="90" step="10" value={form.min_score}
              onChange={e => setForm(f => ({ ...f, min_score: parseInt(e.target.value) }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={lbl}>Frequency</label>
              <select style={inp} value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            {form.frequency === 'weekly' && (
              <div>
                <label style={lbl}>Day</label>
                <select style={inp} value={form.day_of_week} onChange={e => setForm(f => ({ ...f, day_of_week: parseInt(e.target.value) }))}>
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d,i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={save} disabled={saving}
            style={{ padding: '8px 20px', background: '#00d4ff', color: '#000', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: saving ? 'wait' : 'pointer' }}
          >{saving ? 'Saving…' : 'Create Schedule'}</button>
          {saveMsg && <span style={{ fontSize: 12, color: saveMsg.startsWith('Error') ? '#ef4444' : '#10b981' }}>{saveMsg}</span>}
        </div>
      </div>

      {/* Schedules list */}
      {loading ? (
        <div style={{ color: '#CBD5E1', fontSize: 13 }}>Loading schedules…</div>
      ) : schedules.length === 0 ? (
        <div style={{ color: '#CBD5E1', fontSize: 13, padding: '16px 0' }}>No schedules yet. Create one above.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {['Recipient', 'Filter', 'Frequency', 'Next Run', 'Last Sent', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: '#CBD5E1', borderBottom: '1px solid #1a1a2e', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedules.map(s => {
                const filterDesc = [s.industry || 'All industries', s.min_score > 0 ? `Score ≥ ${s.min_score}` : null].filter(Boolean).join(', ');
                const freqDesc = s.frequency === 'daily' ? 'Daily' : 'Weekly / ' + DAY_NAMES[s.day_of_week] || 'Mon';
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #0f0f1e' }}>
                    <td style={{ padding: '9px 10px', color: '#E2E8F0' }}>{s.email}</td>
                    <td style={{ padding: '9px 10px', color: '#CBD5E1' }}>{filterDesc}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ background: 'rgba(0,212,255,0.1)', color: '#00d4ff', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{freqDesc}</span>
                    </td>
                    <td style={{ padding: '9px 10px', color: '#CBD5E1', whiteSpace: 'nowrap' }}>{new Date(s.next_run_at).toLocaleString()}</td>
                    <td style={{ padding: '9px 10px', color: '#CBD5E1', whiteSpace: 'nowrap' }}>{s.last_sent_at ? new Date(s.last_sent_at).toLocaleString() : '—'}</td>
                    <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                      <button onClick={() => runNow(s.id)} disabled={running[s.id]}
                        style={{ fontSize: 11, padding: '3px 10px', background: 'rgba(16,185,129,0.12)', color: '#10b981', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: 6, fontWeight: 600 }}>
                        {running[s.id] ? '…' : '▶ Run'}
                      </button>
                      <button onClick={() => deleteSchedule(s.id)}
                        style={{ fontSize: 11, padding: '3px 10px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Settings({ liveStats }) {
  const [tab, setTab] = useState('general');
  const [toggles, setToggles] = useState({ botFilter: true, autoRotate: true, alerts: true, dedupe: false });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("client");
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState("");
  const [addingUser, setAddingUser] = useState(false);

  // API Keys state
  const [keys, setKeys]               = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [newKeyName, setNewKeyName]   = useState('');
  const [creating, setCreating]       = useState(false);
  const [revealed, setRevealed]       = useState(null);
  const [revoking, setRevoking]       = useState(null);
  const [copied, setCopied]           = useState(false);
  const [enrichStats, setEnrichStats]   = useState(null);
  const [enriching, setEnriching]       = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);

  const fetchUsers = () => {
    fetch("https://tetrus.ai/api/users", { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setUsers(d.users || []); setUsersLoading(false); })
      .catch(() => setUsersLoading(false));
  };

  const fetchKeys = () => {
    setKeysLoading(true);
    fetch('https://tetrus.ai/api/keys', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setKeys(d.keys || []); setKeysLoading(false); })
      .catch(() => setKeysLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => {
    fetch("https://tetrus.ai/api/stats", { headers: apiHeaders })
      .then(r => r.json())
      .then(d => setEnrichStats(d))
      .catch(() => {});
  }, []);
  useEffect(() => { if (tab === 'apikeys') fetchKeys(); }, [tab]);

  const handleAddUser = async () => {
    setAddError(""); setAddSuccess("");
    if (!newEmail || !newPassword) { setAddError("Email and password are required."); return; }
    if (newPassword.length < 8) { setAddError("Password must be at least 8 characters."); return; }
    setAddingUser(true);
    try {
      const r = await fetch("https://tetrus.ai/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newEmail, password: newPassword, role: newRole }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) { setAddError(d.error || "Failed to create user."); setAddingUser(false); return; }
      setAddSuccess("User " + d.email + " created as " + (d.role || newRole) + ".");
      setNewEmail(""); setNewPassword(""); setNewRole("client");
      setShowAddUser(false);
      fetchUsers();
    } catch { setAddError("Network error."); }
    setAddingUser(false);
  };

  const handleDeleteUser = async (id, email) => {
    if (!window.confirm("Delete user " + email + "?")) return;
    try {
      await fetch("https://tetrus.ai/api/users/" + id, { method: "DELETE", headers: apiHeaders });
      fetchUsers();
    } catch {}
  };

  const generateKey = () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    fetch('https://tetrus.ai/api/keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...apiHeaders },
      body: JSON.stringify({ name: newKeyName.trim() }),
    })
      .then(r => r.json())
      .then(d => { setRevealed({ id: d.id, key: d.key }); setNewKeyName(''); setCreating(false); fetchKeys(); })
      .catch(() => setCreating(false));
  };

  const revokeKey = (id) => {
    setRevoking(id);
    fetch(`https://tetrus.ai/api/keys/${id}`, { method: 'DELETE', headers: apiHeaders })
      .then(() => { setRevoking(null); setRevealed(null); fetchKeys(); })
      .catch(() => setRevoking(null));
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const runEnrichment = async () => {
    setEnriching(true); setEnrichResult(null);
    try {
      const r = await fetch("https://tetrus.ai/api/leads/enrich", {
        method: "POST",
        headers: { ...apiHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ batch_size: 500 }),
      });
      const d = await r.json();
      setEnrichResult(d.enriched != null ? `Enriched ${d.enriched} contacts` : (d.message || JSON.stringify(d)));
    } catch (e) { setEnrichResult("Error: " + e.message); }
    setEnriching(false);
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never';

  const inputStyle = {
    width: "100%", padding: "8px 12px", borderRadius: 6,
    border: "1px solid var(--border2)", background: "var(--surface2)",
    color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none",
    marginBottom: 10,
  };

  const tabStyle = active => ({
    padding: '10px 22px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
    color: active ? 'var(--accent2)' : 'var(--text3)',
    transition: 'all 0.15s',
  });

  if (tab === 'scoring') {
    return (
      <>
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
          <div style={tabStyle(false)} onClick={() => setTab('general')}>General</div>
          <div style={tabStyle(true)} onClick={() => setTab('scoring')}>Scoring Rules</div>
          <div style={tabStyle(false)} onClick={() => setTab('apikeys')}>API Keys</div>
        </div>
        <ScoringRulesTab />
      </>
    );
  }

  if (tab === 'apikeys') {
    const activeKeys  = keys.filter(k => !k.revoked);
    const revokedKeys = keys.filter(k =>  k.revoked);
    const keyInputStyle = { ...inputStyle, marginBottom: 0 };
    return (
      <>
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
          <div style={tabStyle(false)} onClick={() => setTab('general')}>General</div>
          <div style={tabStyle(false)} onClick={() => setTab('scoring')}>Scoring Rules</div>
          <div style={tabStyle(true)} onClick={() => setTab('apikeys')}>API Keys</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Generate */}
          <div className="pipeline-card" style={{ marginBottom: 0 }}>
            <div className="card-header">
              <div>
                <div className="card-title">Generate New API Key</div>
                <div className="card-sub">KEYS ARE SHOWN ONCE — COPY IMMEDIATELY AFTER CREATION</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Key Name</label>
                <input
                  type="text"
                  placeholder="e.g. Production integration, Zapier webhook..."
                  value={newKeyName}
                  onChange={e => setNewKeyName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && generateKey()}
                  style={keyInputStyle}
                />
              </div>
              <button onClick={generateKey} disabled={creating || !newKeyName.trim()} style={{
                padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: creating ? 'not-allowed' : 'pointer',
                background: !newKeyName.trim() ? 'var(--surface2)' : 'var(--accent)',
                border: 'none', color: !newKeyName.trim() ? '#CBD5E1' : '#fff',
                borderRadius: 6, whiteSpace: 'nowrap', opacity: creating ? 0.7 : 1,
              }}>
                {creating ? 'Generating...' : '+ Generate Key'}
              </button>
            </div>

            {revealed && (
              <div style={{ marginTop: 16, padding: '16px', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#10B981' }}>&#10003; Key created — copy now. It will not be shown again.</div>
                  <button onClick={() => setRevealed(null)} style={{ background: 'none', border: 'none', color: '#CBD5E1', cursor: 'pointer', fontSize: 16 }}>&#10005;</button>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{ flex: 1, padding: '10px 14px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, fontFamily: 'var(--mono)', color: '#10B981', wordBreak: 'break-all' }}>
                    {revealed.key}
                  </code>
                  <button onClick={() => copyKey(revealed.key)} style={{
                    padding: '10px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                    background: copied ? 'rgba(16,185,129,0.15)' : 'var(--surface2)',
                    border: `1px solid ${copied ? 'rgba(16,185,129,0.4)' : 'var(--border2)'}`,
                    color: copied ? '#10B981' : 'var(--text)', borderRadius: 6,
                  }}>
                    {copied ? '&#10003; Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Active keys */}
          <div className="table-card">
            <div className="card-header" style={{ padding: '18px 20px 14px' }}>
              <div className="card-title">Active Keys</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#CBD5E1' }}>{activeKeys.length} key{activeKeys.length !== 1 ? 's' : ''}</span>
                <button onClick={fetchKeys} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', background: 'var(--surface2)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 5 }}>&#8635;</button>
              </div>
            </div>
            {keysLoading ? (
              <div style={{ padding: '28px 0', textAlign: 'center', color: '#CBD5E1' }}>Loading...</div>
            ) : activeKeys.length === 0 ? (
              <div style={{ padding: '28px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>&#128273;</div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>No active API keys</div>
                <div style={{ fontSize: 11, color: '#CBD5E1' }}>Generate a key above to authenticate API requests.</div>
              </div>
            ) : (
              <table>
                <thead><tr><th>Name</th><th>Key Prefix</th><th>Created</th><th>Last Used</th><th></th></tr></thead>
                <tbody>
                  {activeKeys.map(k => (
                    <tr key={k.id}>
                      <td className="td-name">{k.name}</td>
                      <td>
                        <code style={{ fontFamily: 'var(--mono)', fontSize: 12, background: 'var(--surface2)', padding: '2px 7px', borderRadius: 4, border: '1px solid var(--border)', color: '#CBD5E1' }}>
                          {k.key_prefix}&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;
                        </code>
                      </td>
                      <td style={{ fontSize: 13, color: '#CBD5E1' }}>{fmtDate(k.created_at)}</td>
                      <td style={{ fontSize: 13, color: '#CBD5E1' }}>
                        {k.last_used_at ? fmtDate(k.last_used_at) : <span style={{ fontStyle: 'italic' }}>Never</span>}
                      </td>
                      <td>
                        <button onClick={() => revokeKey(k.id)} disabled={revoking === k.id} style={{
                          padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: revoking === k.id ? 'not-allowed' : 'pointer',
                          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                          color: revoking === k.id ? '#CBD5E1' : '#EF4444', borderRadius: 5,
                        }}>
                          {revoking === k.id ? 'Revoking...' : 'Revoke'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Revoked keys */}
          {revokedKeys.length > 0 && (
            <div className="table-card">
              <div className="card-header" style={{ padding: '14px 20px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}>{revokedKeys.length} Revoked Key{revokedKeys.length !== 1 ? 's' : ''}</div>
              </div>
              <table>
                <thead><tr><th>Name</th><th>Key Prefix</th><th>Created</th><th>Status</th></tr></thead>
                <tbody>
                  {revokedKeys.map(k => (
                    <tr key={k.id} style={{ opacity: 0.5 }}>
                      <td className="td-name" style={{ textDecoration: 'line-through' }}>{k.name}</td>
                      <td><code style={{ fontFamily: 'var(--mono)', fontSize: 12, color: '#CBD5E1' }}>{k.key_prefix}&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;</code></td>
                      <td style={{ fontSize: 13, color: '#CBD5E1' }}>{fmtDate(k.created_at)}</td>
                      <td><span className="tag tag-yellow" style={{ fontSize: 10 }}>Revoked</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Usage note */}
          <div style={{ padding: '14px 16px', background: 'rgba(14,165,233,0.05)', border: '1px solid rgba(14,165,233,0.15)', borderRadius: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#0EA5E9', marginBottom: 4 }}>Using API Keys</div>
            <div style={{ fontSize: 11, color: '#CBD5E1', lineHeight: 1.7 }}>
              Pass the key in all API requests as a header:
              <code style={{ fontFamily: 'var(--mono)', color: '#CBD5E1', background: 'var(--surface2)', padding: '1px 6px', borderRadius: 3 }}> x-api-key: tk_...</code>
              <br />Protected endpoints require this header. Public routes
              (<code style={{ fontFamily: 'var(--mono)', color: '#CBD5E1' }}>/api/health</code>,
              <code style={{ fontFamily: 'var(--mono)', color: '#CBD5E1' }}> /api/stats</code>,
              <code style={{ fontFamily: 'var(--mono)', color: '#CBD5E1' }}> /api/email-templates</code>) do not.
            </div>
          </div>
        </div>
      </>
    );
  }

  // Default: general tab
  return (
    <>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        <div style={tabStyle(true)} onClick={() => setTab('general')}>General</div>
        <div style={tabStyle(false)} onClick={() => setTab('scoring')}>Scoring Rules</div>
        <div style={tabStyle(false)} onClick={() => setTab('apikeys')}>API Keys</div>
      </div>
      <div className="two-col" style={{ alignItems: "start" }}>
        <div>
          <div className="pipeline-card" style={{ marginBottom: 14 }}>
            <div className="card-header"><div className="card-title">Integrations</div></div>
            {[
              { name: "Lead Database", desc: `Database \u2014 ${liveStats.totalLeads > 0 ? liveStats.totalLeads.toLocaleString() : "0"} records`, status: "Connected", color: "tag-green" },
              { name: "Campaign Engine", desc: "Campaign Automation", status: "Connected", color: "tag-green" },
              { name: "Email Delivery", desc: "Primary email delivery layer", status: "Connected", color: "tag-green" },
              { name: "Validation Layer", desc: "Email validation and warm-up", status: "Connected", color: "tag-green" },
              { name: "Sales CRM", desc: "Sales Pipeline \u2014 :8081", status: "Installing", color: "tag-yellow" },
            ].map((r) => (
              <div key={r.name} className="metric-row">
                <div><div className="metric-name" style={{ fontWeight: 600 }}>{r.name}</div><div style={{ fontSize: 11, color: "#CBD5E1", marginTop: 2 }}>{r.desc}</div></div>
                <span className={`tag ${r.color}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="pipeline-card" style={{ marginBottom: 0 }}>
          <div className="card-header"><div className="card-title">System Configuration</div></div>
          {[
            { key: "botFilter", name: "Bot Filtering", desc: "Apply human engagement scoring to all campaigns" },
            { key: "autoRotate", name: "Auto Domain Rotation", desc: "Rotate sending domains at reputation threshold" },
            { key: "alerts", name: "Real-time Alerts", desc: "Notify on deliverability and engagement anomalies" },
            { key: "dedupe", name: "Auto Deduplication", desc: "Remove duplicate records on import" },
          ].map((s) => (
            <div key={s.key} className="setting-row">
              <div><div className="setting-name">{s.name}</div><div className="setting-desc">{s.desc}</div></div>
              <div className={`toggle ${toggles[s.key] ? "on" : "off"}`} onClick={() => setToggles({ ...toggles, [s.key]: !toggles[s.key] })} />
            </div>
          ))}
        </div>
      </div>

      <div className="table-card" style={{ marginTop: 20 }}>
        <div className="card-header" style={{ padding: "18px 20px 14px" }}>
          <div>
            <div className="card-title">User Management</div>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>Manage admin and client login accounts</div>
          </div>
          <button className="btn btn-primary" onClick={() => { setShowAddUser(!showAddUser); setAddError(""); setAddSuccess(""); }}>
            {showAddUser ? "Cancel" : "+ Add User"}
          </button>
        </div>

        {addSuccess && (
          <div className="alert alert-green" style={{ margin: "0 20px 10px" }}>
            <span>\u2713</span><span>{addSuccess}</span>
            <span style={{ marginLeft: "auto", cursor: "pointer", opacity: 0.6 }} onClick={() => setAddSuccess("")}>\u2715</span>
          </div>
        )}

        {showAddUser && (
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", background: "rgba(14,165,233,0.03)" }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: "var(--text2)" }}>Create New User</div>
            <input type="email" placeholder="Email address" value={newEmail} onChange={e => setNewEmail(e.target.value)} style={inputStyle} />
            <input type="text" placeholder="Temporary password (min 8 chars)" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} />
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div className={`filter-tag ${newRole === "client" ? "selected" : ""}`} onClick={() => setNewRole("client")} style={{ cursor: "pointer" }}>Client</div>
              <div className={`filter-tag ${newRole === "admin" ? "selected" : ""}`} onClick={() => setNewRole("admin")} style={{ cursor: "pointer" }}>Admin</div>
            </div>
            {addError && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 8 }}>{addError}</div>}
            <button className="btn btn-primary" onClick={handleAddUser} disabled={addingUser} style={{ width: "100%" }}>
              {addingUser ? "Creating..." : "Create User"}
            </button>
          </div>
        )}

        {usersLoading ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--text3)" }}>Loading users...</div>
        ) : users.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--text3)" }}>No users found</div>
        ) : (
          <table>
            <thead><tr><th>Email</th><th>Role</th><th>Created</th><th>Action</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td className="td-name">{u.email}</td>
                  <td><span className={`tag ${u.role === "admin" ? "tag-blue" : "tag-green"}`}>{u.role ? u.role.charAt(0).toUpperCase() + u.role.slice(1) : "Client"}</span></td>
                  <td style={{ fontSize: 13, color: "var(--text3)", fontFamily: "var(--mono)" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "\u2014"}</td>
                  <td>
                    <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 10px", color: "var(--red)", borderColor: "rgba(239,68,68,0.3)" }}
                      onClick={() => handleDeleteUser(u.id, u.email)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="pipeline-card" style={{ marginTop: 20, marginBottom: 14 }}>
        <div className="card-header">
          <div className="card-title">Support &amp; Contact</div>
        </div>
        <div className="metric-row" style={{ alignItems: 'flex-start' }}>
          <div>
            <div className="metric-name" style={{ fontWeight: 600 }}>Phone</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--accent)', letterSpacing: '.04em', marginTop: 4 }}>888.983.8787</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>(888) 983-8787</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="metric-name" style={{ fontWeight: 600 }}>Email</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
              <a href="mailto:support@tetrus.ai" style={{ color: 'var(--accent)', textDecoration: 'none' }}>support@tetrus.ai</a>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Mon – Fri, 8am – 6pm CT</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '0 24px 24px' }}>
        <ExportScheduler />
      </div>
      <div className="pipeline-card" style={{ marginTop: 20, marginBottom: 14 }}>
        <div className="card-header">
          <div>
            <div className="card-title">Data Enrichment</div>
            <div className="card-sub">SIC / NAICS INDUSTRY CODE COVERAGE</div>
          </div>
        </div>
        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 13, color: "var(--text3)" }}>
            {enrichStats
              ? (() => {
                  const enriched = enrichStats.enriched_cnt || 0;
                  const total = enrichStats.totalLeads || 0;
                  const pct = total > 0 ? ((enriched / total) * 100).toFixed(1) : "0.0";
                  return <span><span style={{ color: "var(--text)", fontWeight: 700, fontFamily: "var(--mono)" }}>{enriched.toLocaleString()}</span> of <span style={{ fontFamily: "var(--mono)" }}>{total.toLocaleString()}</span> contacts enriched with SIC/NAICS codes <span style={{ color: "var(--accent)", fontWeight: 600 }}>({pct}%)</span></span>;
                })()
              : <span style={{ color: "var(--text3)" }}>Loading…</span>
            }
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button className="btn btn-primary" onClick={runEnrichment} disabled={enriching} style={{ minWidth: 160 }}>
              {enriching ? "Running…" : "Run Enrichment"}
            </button>
            {enrichResult && <span style={{ fontSize: 13, color: "#10b981", fontWeight: 600 }}>✓ {enrichResult}</span>}
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", fontStyle: "italic" }}>
            Enrichment runs in batches of 500. Run multiple times to process all contacts.
          </div>
        </div>
      </div>
    </>
  );
}



function ClientReportModal({ clientId, clientName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/clients/' + clientId + '/report', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [clientId]);

  const printReport = () => window.print();

  const stat = (label, value, color = '#00d4ff', sub = null) => (
    <div style={{ background: '#0a0a1a', border: '1px solid #1a1a2e', borderRadius: 8, padding: '14px 18px', textAlign: 'center' }}>
      <div style={{ fontSize: 26, fontWeight: 800, color, fontFamily: 'monospace', letterSpacing: -1 }}>{value}</div>
      <div style={{ fontSize: 11, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: '#CBD5E1', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const section = (title, accent, children) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 3, height: 16, background: accent, borderRadius: 2 }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: '#E2E8F0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</div>
      </div>
      {children}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 3000, overflowY: 'auto', padding: '32px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 820, background: '#0f0f23', border: '1px solid #2a2a4a', borderRadius: 12, overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#0f0f23 0%,#1a1a3e 100%)', borderBottom: '1px solid #2a2a4a', padding: '24px 28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: '#00d4ff', fontWeight: 700, letterSpacing: '1.5px', marginBottom: 6 }}>TETRUS.AI — PERFORMANCE REPORT</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>{clientName}</div>
              {data && <div style={{ fontSize: 12, color: '#CBD5E1', marginTop: 4 }}>Generated {new Date(data.generated_at).toLocaleString()}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={printReport} style={{ padding: '7px 16px', background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: '#00d4ff', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>⎙ Print</button>
              <button onClick={onClose} style={{ padding: '7px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid #2a2a4a', color: '#CBD5E1', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>✕ Close</button>
            </div>
          </div>
        </div>

        <div style={{ padding: 28 }}>
          {loading && <div style={{ textAlign: 'center', padding: 60, color: '#CBD5E1' }}>Generating report…</div>}
          {error && <div style={{ color: '#ef4444', padding: 20 }}>Error: {error}</div>}

          {data && (<>
            {/* Client meta */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
              {[
                { l: 'Status', v: data.client.status },
                { l: 'Website', v: data.client.website || '—' },
                { l: 'Segments', v: data.client.segments.map(s => s.name).join(', ') || '—' },
                { l: 'Client since', v: new Date(data.client.created_at).toLocaleDateString() },
              ].map(({ l, v }) => (
                <div key={l} style={{ fontSize: 12 }}>
                  <span style={{ color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: 10 }}>{l}: </span>
                  <span style={{ color: '#CBD5E1', textTransform: l === 'Status' ? 'capitalize' : 'none' }}>{v}</span>
                </div>
              ))}
            </div>

            {/* KPI grid */}
            {section('Key Performance Indicators', '#00d4ff',
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 4 }}>
                {stat('Total Leads Targeted', (data.lead_stats.total || 0).toLocaleString(), '#00d4ff')}
                {stat('Qualified Leads', (data.lead_stats.qualified || 0).toLocaleString(), '#10b981', 'Score ≥ 60')}
                {stat('Hot Opportunities', (data.lead_stats.hot || 0).toLocaleString(), '#ff6b35', 'Score ≥ 80')}
                {stat('Avg Lead Score', (data.lead_stats.avg_score || 0).toFixed(1), '#a78bfa')}
              </div>
            )}

            {section('Email Campaign Performance', '#10b981',
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {stat('Emails Sent', (data.email_stats.emails_sent || 0).toLocaleString(), '#60a5fa')}
                {stat('Opens', (data.email_stats.opens || 0).toLocaleString(), '#34d399')}
                {stat('Open Rate', (data.email_stats.open_rate || 0) + '%', '#34d399')}
                {stat('Click Rate', (data.email_stats.click_rate || 0) + '%', '#f59e0b')}
              </div>
            )}

            {section('Pipeline & Conversion', '#ff6b35',
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {stat('Campaign Automation Contacts', (data.email_stats.contacts || 0).toLocaleString(), '#00d4ff')}
                {stat('Active Campaigns', (data.campaigns.filter(c => c.status === 'active').length), '#a78bfa')}
                {stat('Meetings Booked', data.meetings_booked || 0, '#10b981')}
              </div>
            )}

            {/* Campaigns table */}
            {data.campaigns.length > 0 && section('Campaigns', '#a78bfa',
              <div style={{ border: '1px solid #1a1a2e', borderRadius: 6, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#0a0a1a' }}>
                      {['Campaign', 'Status', 'Industry', 'Min Score', 'Created'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#CBD5E1', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.campaigns.map((c, i) => (
                      <tr key={c.id} style={{ borderTop: '1px solid #111' }}>
                        <td style={{ padding: '8px 12px', color: '#E2E8F0' }}>{c.name}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{
                            fontSize: 10, padding: '2px 7px', borderRadius: 3, fontWeight: 700,
                            background: c.status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)',
                            color: c.status === 'active' ? '#10b981' : '#CBD5E1',
                          }}>{c.status}</span>
                        </td>
                        <td style={{ padding: '8px 12px', color: '#CBD5E1' }}>{c.industry || 'All'}</td>
                        <td style={{ padding: '8px 12px', color: '#CBD5E1', fontFamily: 'monospace' }}>{c.min_score}</td>
                        <td style={{ padding: '8px 12px', color: '#CBD5E1' }}>{new Date(c.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Hot opportunities */}
            {data.hot_opportunities.length > 0 && section('Hot Opportunities (61+ pts)', '#ff6b35',
              <div style={{ display: 'grid', gap: 6 }}>
                {data.hot_opportunities.map((h, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,107,53,0.06)', border: '1px solid rgba(255,107,53,0.15)', borderRadius: 6, padding: '8px 14px' }}>
                    <div>
                      <span style={{ color: '#E2E8F0', fontSize: 13, fontWeight: 600 }}>{h.name}</span>
                      {h.company && <span style={{ color: '#CBD5E1', fontSize: 12, marginLeft: 8 }}>@ {h.company}</span>}
                    </div>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#ff6b35', fontSize: 14 }}>{h.points} pts</span>
                  </div>
                ))}
              </div>
            )}

            {/* 30-day engagement timeline */}
            {data.timeline.length > 0 && section('30-Day Engagement', '#60a5fa', (() => {
              const maxVal = Math.max(...data.timeline.map(d => (parseInt(d.opens) || 0) + (parseInt(d.clicks) || 0)), 1);
              return (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 60, padding: '0 4px' }}>
                  {data.timeline.map((d, i) => {
                    const opens = parseInt(d.opens) || 0;
                    const clicks = parseInt(d.clicks) || 0;
                    const total = opens + clicks;
                    const h = Math.round((total / maxVal) * 56);
                    return (
                      <div key={i} title={d.day + ': ' + opens + ' opens, ' + clicks + ' clicks'}
                        style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 1, cursor: 'default' }}>
                        {clicks > 0 && <div style={{ height: Math.round((clicks / maxVal) * 56), background: '#f59e0b', borderRadius: '2px 2px 0 0', minHeight: 2 }} />}
                        {opens > 0 && <div style={{ height: Math.round((opens / maxVal) * 56), background: '#60a5fa', borderRadius: clicks > 0 ? 0 : '2px 2px 0 0', minHeight: 2 }} />}
                        {total === 0 && <div style={{ height: 2, background: '#1a1a2e' }} />}
                      </div>
                    );
                  })}
                </div>
              );
            })())}
            {data.timeline.length > 0 && (
              <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#CBD5E1' }}>
                  <div style={{ width: 10, height: 10, background: '#60a5fa', borderRadius: 2 }} /> Opens
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#CBD5E1' }}>
                  <div style={{ width: 10, height: 10, background: '#f59e0b', borderRadius: 2 }} /> Clicks
                </div>
              </div>
            )}

            {/* Footer */}
            <div style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid #1a1a2e', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#CBD5E1' }}>
              <span>TETRUS.AI — Confidential client report</span>
              <span>{new Date().toLocaleDateString()}</span>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

function Onboarding() {
  const WELCOME = "Welcome to TETRUS AI. I’m going to ask you a few quick questions to build your first campaign. What does your company sell?";
  const [messages, setMessages] = useState([{ role: 'assistant', content: WELCOME }]);
  const [input, setInput]       = useState('');
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);
  const [brief, setBrief]       = useState(null);
  const bottomRef = useRef(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);


  const send = async () => {
    const userMsg = input.trim();
    if (!userMsg || isSendingRef.current) return;
    isSendingRef.current = true;
    setInput('');
    const updated = [...messages, { role: 'user', content: userMsg }];
    setMessages(updated);
    setIsSending(true);
    try {
      const r = await fetch('https://tetrus.ai/api/onboarding/chat', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updated.slice(0, -1), userMessage: userMsg }),
      });
      const d = await r.json();
      if (d.reply) {
        const displayReply = d.reply.replace(/<campaign_brief>[\s\S]*?<\/campaign_brief>/g, '').trim();
        setMessages(prev => [...prev, { role: 'assistant', content: displayReply || 'Your campaign brief is ready.' }]);
        if (d.brief) setBrief(d.brief);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
    } finally {
      setIsSending(false); isSendingRef.current = false;
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const briefFields = brief ? [
    { label: 'Product / Service',  value: brief.product_description },
    { label: 'Target Title',       value: brief.target_title },
    { label: 'Target Industry',    value: brief.target_industry },
    { label: 'Company Size',       value: brief.target_company_size },
    { label: 'Pain Point',         value: brief.pain_point },
    { label: 'Proof Points',       value: brief.proof_points },
    { label: 'Tone',               value: brief.tone },
  ] : [];

  const s = {
    wrap:      { display: 'grid', gridTemplateColumns: brief ? '1fr 380px' : '1fr', gap: 20, alignItems: 'start' },
    chatCard:  { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 250px)', minHeight: 500 },
    header:    { padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 },
    dot:       { width: 8, height: 8, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 6px #10B981' },
    title:     { fontSize: 14, fontWeight: 600, color: '#F1F5F9' },
    sub:       { fontSize: 11, color: '#64748B', fontFamily: 'var(--mono)', marginLeft: 'auto' },
    msgs:      { height: 'calc(100vh - 320px)', overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 14 },
    agentRow:  { display: 'flex', gap: 10, alignItems: 'flex-start' },
    userRow:   { display: 'flex', gap: 10, alignItems: 'flex-start', flexDirection: 'row-reverse' },
    avatar:    { width: 28, height: 28, borderRadius: '50%', background: 'rgba(14,165,233,0.15)', border: '1px solid rgba(14,165,233,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 },
    userAv:    { width: 28, height: 28, borderRadius: '50%', background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 },
    agentBub:  { background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '4px 12px 12px 12px', padding: '10px 14px', fontSize: 13, color: '#E2E8F0', lineHeight: 1.6, maxWidth: '80%' },
    userBub:   { background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.25)', borderRadius: '12px 4px 12px 12px', padding: '10px 14px', fontSize: 13, color: '#E2E8F0', lineHeight: 1.6, maxWidth: '80%' },
    inputWrap: { padding: '14px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 },
    inputEl:   { flex: 1, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 8, padding: '10px 14px', color: '#F1F5F9', fontSize: 13, fontFamily: 'var(--font)', outline: 'none', resize: 'none', maxHeight: 150, overflowY: 'auto' },
    sendBtn:   { padding: '10px 20px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
    briefCard: { background: 'var(--surface)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, padding: '20px', display: 'flex', flexDirection: 'column', gap: 0 },
    briefHdr:  { fontSize: 13, fontWeight: 700, color: '#10B981', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 },
    briefRow:  { padding: '10px 0', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 3 },
    briefLbl:  { fontSize: 10, color: '#64748B', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.07em' },
    briefVal:  { fontSize: 13, color: '#E2E8F0', lineHeight: 1.5 },
    seqBtn:    { marginTop: 16, padding: '11px 0', background: '#10B981', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: '.03em' },
  };

  const exchangeCount = messages.filter(m => m.role === 'user').length;

  return (
    <div style={s.wrap}>
      <div style={s.chatCard}>
        <div style={s.header}>
          <div style={s.dot} />
          <div style={s.title}>TETRUS Onboarding Agent</div>
          <div style={s.sub}>{exchangeCount}/10 EXCHANGES</div>
        </div>
        <div style={s.msgs}>
          {messages.map((m, i) => (
            <div key={i} style={m.role === 'user' ? s.userRow : s.agentRow}>
              <div style={m.role === 'user' ? s.userAv : s.avatar}>
                {m.role === 'user' ? 'U' : 'T'}
              </div>
              <div style={m.role === 'user' ? s.userBub : s.agentBub}>
                {m.content}
              </div>
            </div>
          ))}
          {isSending && (
            <div style={s.agentRow}>
              <div style={s.avatar}>T</div>
              <div style={{ ...s.agentBub, color: '#475569' }}>⋯⋯⋯</div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <div style={s.inputWrap}>
          <textarea
            style={s.inputEl}
            rows={1}
            placeholder="Type your answer…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            disabled={isSending || !!brief}
          />
          {(typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)) && (
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
              onMouseEnter={e => { const t = e.currentTarget.querySelector('.mic-tip'); if (t) t.style.opacity = '1'; }}
              onMouseLeave={e => { const t = e.currentTarget.querySelector('.mic-tip'); if (t) t.style.opacity = '0'; }}>
              <button
                disabled={!!brief}
                onClick={() => {
                  if (listening) { recognitionRef.current && recognitionRef.current.stop(); setListening(false); return; }
                  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
                  const rec = new SR();
                  rec.continuous = false;
                  rec.interimResults = false;
                  rec.lang = 'en-US';
                  rec.onstart = () => setListening(true);
                  rec.onresult = (e) => { const t = Array.from(e.results).map(r => r[0].transcript).join(''); setInput(prev => { const base = prev.trimEnd(); return base ? base + ' ' + t : t; }); };
                  rec.onend = () => setListening(false);
                  rec.onerror = () => setListening(false);
                  recognitionRef.current = rec;
                  rec.start();
                }}
                style={{
                  width: 40, height: 40, borderRadius: 8, border: 'none', cursor: brief ? 'not-allowed' : 'pointer',
                  background: listening ? 'rgba(239,68,68,0.15)' : 'var(--surface2)',
                  color: listening ? '#EF4444' : '#94A3B8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                  outline: listening ? '2px solid rgba(239,68,68,0.4)' : '1px solid var(--border2)',
                  transition: 'all 0.15s', flexShrink: 0,
                }}>
                {listening ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="9" y1="22" x2="15" y2="22"/></svg>
                )}
              </button>
              <div className="mic-tip" style={{
                position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
                background: '#1E293A', border: '1px solid var(--border2)', borderRadius: 5,
                padding: '4px 8px', fontSize: 11, color: '#CBD5E1', whiteSpace: 'nowrap',
                pointerEvents: 'none', opacity: 0, transition: 'opacity 0.15s',
              }}>
                {listening ? 'Listening… click to stop' : 'Click mic to speak'}
              </div>
            </div>
          )}
          <button
            style={{ ...s.sendBtn, opacity: (isSending || !input.trim() || !!brief) ? 0.5 : 1 }}
            onClick={send}
            disabled={isSending || !input.trim() || !!brief}>
            {isSending ? '⋯' : 'Send →'}
          </button>
        </div>
      </div>
      {brief && (
        <div style={s.briefCard}>
          <div style={s.briefHdr}><span style={{ fontSize: 16 }}>✓</span> Campaign Brief</div>
          {briefFields.map(f => (
            <div key={f.label} style={s.briefRow}>
              <div style={s.briefLbl}>{f.label}</div>
              <div style={s.briefVal}>{f.value || '—'}</div>
            </div>
          ))}
          <button style={s.seqBtn} onClick={() => alert('Email sequence generation coming soon.')}>
            Generate Email Sequence →
          </button>
        </div>
      )}
    </div>
  );
}


function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch('/api/reports', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const cardStyle = { background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, padding: 20, marginBottom: 24 };
  const thStyle = { textAlign: 'left', padding: '8px 12px', color: '#CBD5E1', borderBottom: '1px solid #2a2a4a', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '8px 12px', color: '#E2E8F0' };

  const DataTable = ({ headers, rows }) => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>{headers.map((h, i) => <th key={i} style={thStyle}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: '1px solid #1a1a2e' }}>
              {row.map((cell, ci) => <td key={ci} style={tdStyle}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <div style={{ color: '#CBD5E1', padding: 40 }}>Loading reports...</div>;
  if (error) return <div style={{ color: '#ff4444', padding: 40 }}>Error: {error}</div>;
  if (!data) return null;

  const campRows = (data.campaign_performance || []).map(c => [
    c.name, c.status, c.industry,
    c.opens, c.clicks, c.conversions,
    c.open_rate + '%', c.click_rate + '%'
  ]);

  const stageOrder = ['Hot Opportunity', 'Qualified Lead', 'MQL', 'Raw Lead', 'Cold'];
  const funnelRows = stageOrder.map(s => {
    const row = (data.funnel_velocity || []).find(r => r.stage === s);
    return [s, row ? row.lead_count : 0, row ? (row.avg_days_in_stage + ' days') : '-', row ? row.avg_score : '-'];
  });

  const indRows = (data.industry_breakdown || []).map(r => [
    r.industry, r.lead_count, r.qualified_leads, r.avg_score, r.engagement_events
  ]);

  const topRows = (data.top_leads || []).map(r => [
    ((r.contact_first_name || '') + ' ' + (r.contact_last_name || '')).trim() || '-',
    r.contact_title || '-',
    r.company_name || '-',
    r.company_industry || '-',
    r.human_score,
    (r.clean === 1 || r.clean === '1') ? '\u2713' : '\u2014'
  ]);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ color: '#fff', margin: 0 }}>Reports</h2>
        <span style={{ color: '#CBD5E1', fontSize: 12 }}>Generated: {new Date(data.generated_at).toLocaleString()}</span>
      </div>

      <div style={cardStyle}>
        <h3 style={{ color: '#00d4ff', marginBottom: 16, fontSize: 16, fontWeight: 600 }}>Campaign Performance</h3>
        {campRows.length === 0
          ? <div style={{ color: '#CBD5E1' }}>No campaigns yet.</div>
          : <DataTable headers={['Campaign', 'Status', 'Industry', 'Opens', 'Clicks', 'Conv.', 'Open Rate', 'Click Rate']} rows={campRows} />}
      </div>

      <div style={cardStyle}>
        <h3 style={{ color: '#00d4ff', marginBottom: 16, fontSize: 16, fontWeight: 600 }}>Funnel Velocity</h3>
        <DataTable headers={['Stage', 'Leads', 'Avg Days in Stage', 'Avg Score']} rows={funnelRows} />
      </div>

      <div style={cardStyle}>
        <h3 style={{ color: '#00d4ff', marginBottom: 16, fontSize: 16, fontWeight: 600 }}>Industry Breakdown</h3>
        {indRows.length === 0
          ? <div style={{ color: '#CBD5E1' }}>No industry data yet.</div>
          : <DataTable headers={['Industry', 'Total Leads', 'Qualified', 'Avg Score', 'Engagements']} rows={indRows} />}
      </div>

      <div style={cardStyle}>
        <h3 style={{ color: '#00d4ff', marginBottom: 16, fontSize: 16, fontWeight: 600 }}>Top Leads by Score</h3>
        {topRows.length === 0
          ? <div style={{ color: '#CBD5E1' }}>No scored leads yet.</div>
          : <DataTable headers={['Name', 'Title', 'Company', 'Industry', 'Score', 'Clean']} rows={topRows} />}
      </div>
    </div>
  );
}


// ============================================================
// ACTIVITY FEED VIEW
// ============================================================
function ActivityFeedView() {
  const [days, setDays] = useState(7);
  const [kindFilter, setKindFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/api/activity?days=${days}`, { headers: { 'x-api-key': TETRUS_API_KEY } })
      .then(r => r.json())
      .then(d => { setEvents(d.events || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [days]);

  const KIND_META = {
    'Email Opened':  { color: '#10B981' },
    'Email Clicked': { color: '#06B6D4' },
    'Bounced':       { color: '#EF4444' },
    'Spam Report':   { color: '#F97316' },
    'Dropped':       { color: '#EF4444' },
    'Unsubscribed':  { color: '#F59E0B' },
    'Email Sent':    { color: '#64748B' },
    'Delivered':     { color: '#3B82F6' },
    'Processed':     { color: '#475569' },
    'Deferred':      { color: '#94A3B8' },
  };

  const FILTERS = [
    { id: 'all',     label: 'All' },
    { id: 'opened',  label: 'Opened',  kind: 'Email Opened' },
    { id: 'clicked', label: 'Clicked', kind: 'Email Clicked' },
    { id: 'bounced', label: 'Bounced', kind: 'Bounced' },
    { id: 'sent',    label: 'Sent',    kind: 'Email Sent' },
  ];

  function relTime(ts) {
    const diff = Date.now() - new Date(ts.includes('T') ? ts : ts + 'Z').getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60) return s + 's ago';
    const m = Math.floor(s / 60);
    if (m < 60) return m + 'm ago';
    const h = Math.floor(m / 60);
    if (h < 24) return h + 'h ago';
    return Math.floor(h / 24) + 'd ago';
  }

  const activeKind = FILTERS.find(f => f.id === kindFilter)?.kind || null;
  const filtered = activeKind ? events.filter(e => e.kind === activeKind) : events;

  const btnBase = {
    padding: '5px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6,
    border: '1px solid var(--border2)', cursor: 'pointer', fontFamily: 'var(--font)',
  };
  const btnOn  = { ...btnBase, background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' };
  const btnOff = { ...btnBase, background: 'var(--surface2)', color: 'var(--text2)' };

  return (
    <div style={{ padding: '28px 32px', maxWidth: 920 }}>
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', letterSpacing: 1, textTransform: 'uppercase', marginRight: 4 }}>Period</span>
          {[7, 14, 30].map(d => (
            <button key={d} style={days === d ? btnOn : btnOff} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', letterSpacing: 1, textTransform: 'uppercase', marginRight: 4 }}>Filter</span>
          {FILTERS.map(f => (
            <button key={f.id} style={kindFilter === f.id ? btnOn : btnOff} onClick={() => setKindFilter(f.id)}>{f.label}</button>
          ))}
        </div>
        {!loading && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            {filtered.length} event{filtered.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {loading && (
          <div style={{ padding: '56px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            <div style={{ fontSize: 22, opacity: 0.3, marginBottom: 10 }}>⊛</div>
            Loading activity…
          </div>
        )}
        {error && (
          <div style={{ padding: '56px 0', textAlign: 'center', color: 'var(--red)', fontSize: 13 }}>
            Failed to load: {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ padding: '56px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            <div style={{ fontSize: 22, opacity: 0.3, marginBottom: 10 }}>⊛</div>
            No events in the last {days} day{days !== 1 ? 's' : ''}
          </div>
        )}
        {!loading && !error && filtered.map((ev, i) => {
          const meta = KIND_META[ev.kind] || { color: '#64748B' };
          return (
            <div key={i}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '11px 20px',
                borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: meta.color, boxShadow: `0 0 6px ${meta.color}88`,
              }} />
              <div style={{ minWidth: 118, fontSize: 12, fontWeight: 600, color: meta.color, fontFamily: 'var(--mono)' }}>
                {ev.kind}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ev.name !== ev.email ? ev.name : ev.email}
                  {ev.company ? <span style={{ color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>— {ev.company}</span> : null}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1, fontFamily: 'var(--mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ev.email}
                  {ev.url ? <span style={{ marginLeft: 8, color: 'var(--accent2)' }}>{ev.url.length > 48 ? ev.url.slice(0, 48) + '…' : ev.url}</span> : null}
                  {ev.campaign ? <span style={{ marginLeft: 8, color: 'var(--text3)' }}>· {ev.campaign}</span> : null}
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', flexShrink: 0, textAlign: 'right', minWidth: 56 }}>
                {relTime(ev.timestamp)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// INTEGRATIONS VIEW
// ============================================================
function IntegrationsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/integrations', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const s = {
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 20 },
    card: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: "22px 24px" },
    cardTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 },
    name: { fontSize: 15, fontWeight: 600, color: "var(--text)" },
    badge: (color) => ({
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 11, fontWeight: 600, fontFamily: "var(--mono)",
      padding: "3px 9px", borderRadius: 20,
      background: color === "green" ? "rgba(16,185,129,0.12)" : color === "yellow" ? "rgba(245,158,11,0.12)" : "rgba(239,68,68,0.12)",
      color: color === "green" ? "var(--green)" : color === "yellow" ? "var(--yellow)" : "var(--red)",
    }),
    dot: (color) => ({
      width: 6, height: 6, borderRadius: "50%",
      background: color === "green" ? "var(--green)" : color === "yellow" ? "var(--yellow)" : "var(--red)",
    }),
    divider: { borderTop: "1px solid var(--border)", margin: "14px 0" },
    row: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
    label: { fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)" },
    val: { fontSize: 12, fontWeight: 600, color: "var(--text2)" },
    valGreen: { fontSize: 12, fontWeight: 600, color: "var(--green)" },
    loadBox: { display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "var(--text3)", fontSize: 13, fontFamily: "var(--mono)" },
    errBox: { background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, padding: "14px 18px", color: "var(--red)", fontSize: 13 },
  };

  function Detail({ label, value, highlight }) {
    return (
      <div style={s.row}>
        <span style={s.label}>{label}</span>
        <span style={highlight ? s.valGreen : s.val}>{value}</span>
      </div>
    );
  }

  function Badge({ color, label }) {
    return (
      <span style={s.badge(color)}>
        <span style={s.dot(color)} />
        {label}
      </span>
    );
  }

  if (loading) return <div style={s.loadBox}>Checking integrations...</div>;
  if (error) return <div style={s.errBox}>Failed to load: {error}</div>;
  if (!data) return null;

  const { sendgrid, mautic, clickhouse, suitecrm, amazon_ses, warmup } = data;

  const cards = [
    {
      key: "sendgrid", name: "SendGrid",
      color: sendgrid.connected ? "green" : "red",
      statusLabel: sendgrid.connected ? "Connected" : "Not Connected",
      details: [
        { label: "PLAN", value: (sendgrid.plan || "").toUpperCase() },
        { label: "WEBHOOK", value: sendgrid.webhook_active ? "Active" : "Inactive", highlight: sendgrid.webhook_active },
        { label: "EVENTS CAPTURED", value: (sendgrid.events_captured || 0).toLocaleString() },
      ],
    },
    {
      key: "mautic", name: "Mautic",
      color: mautic.connected ? "green" : "red",
      statusLabel: mautic.connected ? "Connected" : "Not Connected",
      details: [
        { label: "CONTACTS", value: (mautic.contacts || 0).toLocaleString(), highlight: true },
        { label: "CAMPAIGNS", value: mautic.campaigns || 0 },
      ],
    },
    {
      key: "clickhouse", name: "ClickHouse",
      color: clickhouse.connected ? "green" : "red",
      statusLabel: clickhouse.connected ? "Connected" : "Not Connected",
      details: [
        { label: "TOTAL LEADS", value: (clickhouse.leads || 0).toLocaleString(), highlight: true },
      ],
    },
    {
      key: "suitecrm", name: "SuiteCRM",
      color: suitecrm.connected ? "green" : "red",
      statusLabel: suitecrm.connected ? "Connected" : "Not Connected",
      details: [
        { label: "STATUS", value: suitecrm.connected ? "Operational" : "Unreachable" },
      ],
    },
    {
      key: "amazon_ses", name: "Amazon SES",
      color: "red", statusLabel: "Not Connected",
      details: [{ label: "STATUS", value: "Coming soon" }],
    },
    {
      key: "warmup", name: "Email Warmup",
      color: warmup.active ? "green" : "yellow",
      statusLabel: warmup.active ? "Active" : "Inactive",
      details: [
        { label: "WARMUP DAY", value: "Day " + warmup.day },
        { label: "DOMAINS", value: warmup.domains },
        { label: "SENDER", value: warmup.active ? "Running" : "Stopped" },
      ],
    },
  ];

  return (
    <div style={s.grid}>
      {cards.map(card => (
        <div key={card.key} style={s.card}>
          <div style={s.cardTop}>
            <div style={s.name}>{card.name}</div>
            <Badge color={card.color} label={card.statusLabel} />
          </div>
          <div style={s.divider} />
          {card.details.map(d => (
            <Detail key={d.label} label={d.label} value={d.value} highlight={d.highlight} />
          ))}
        </div>
      ))}
    </div>
  );
}


// ============================================================
// PLACEMENT VIEW
// ============================================================
function PlacementView({ setActive }) {
  const API_KEY = '791457bcf3d49ee84acd659e73b28a38bf39578a8f8cd065a57d83470bf343b1';
  const hdrs = { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' };

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState(null);

  const [showLog, setShowLog] = useState(false);
  const [logForm, setLogForm] = useState({ test_id: '', campaign_id: '', esp: '', seed_address: '' });
  const [logSubmitting, setLogSubmitting] = useState(false);
  const [logMsg, setLogMsg] = useState(null);

  const [showResult, setShowResult] = useState(false);
  const [resultForm, setResultForm] = useState({ test_id: '', seed_address: '', landed_inbox: false, landed_spam: false, bounced: false, notes: '' });
  const [resultSubmitting, setResultSubmitting] = useState(false);
  const [resultMsg, setResultMsg] = useState(null);

  const [seeds, setSeeds] = useState([]);
  const [seedsLoading, setSeedsLoading] = useState(true);

  const PROVIDERS = [
    { name: 'Gmail',            domain: 'gmail.com' },
    { name: 'Outlook',          domain: 'outlook.com' },
    { name: 'Yahoo',            domain: 'yahoo.com' },
    { name: 'iCloud',           domain: 'icloud.com' },
    { name: 'Verizon',          domain: 'verizon.net' },
    { name: 'AOL',              domain: 'aol.com' },
    { name: 'Comcast',          domain: 'comcast.net' },
    { name: 'Google Workspace', domain: 'googleworkspace' },
    { name: 'Microsoft 365',    domain: 'microsoft365' },
  ];

  useEffect(() => {
    setSeedsLoading(true);
    Promise.all([
      fetch('/api/contacts/all?list_id=TETRUS%20Seed%20List&limit=50', { headers: hdrs })
        .then(r => r.json()).catch(() => ({ contacts: [] })),
      fetch('/api/placement/summary', { headers: hdrs })
        .then(r => r.json()).catch(() => ({ by_esp: [] })),
    ]).then(([contactsData, placementData]) => {
      const contacts = contactsData.contacts || [];
      const byEsp = placementData.by_esp || [];
      const espMap = {};
      byEsp.forEach(e => { espMap[e.esp ? e.esp.toLowerCase() : ''] = e; });
      const emailMap = {};
      contacts.forEach(c => {
        if (c.email) {
          const dom = c.email.split('@')[1] || '';
          emailMap[dom.toLowerCase()] = c.email;
        }
      });
      const rows = PROVIDERS.map(p => {
        const email = emailMap[p.domain] || null;
        const espKey = p.name.toLowerCase();
        const espData = espMap[espKey] || espMap[Object.keys(espMap).find(k => k.includes(p.name.toLowerCase().split(' ')[0])) || ''] || null;
        return {
          ...p,
          email,
          total_tests: espData ? parseInt(espData.total_tests || 0) : 0,
          inbox_rate: espData ? parseFloat(espData.inbox_rate || 0) : null,
          spam_rate: espData ? parseFloat(espData.spam_rate || 0) : null,
          bounce_rate: espData ? parseFloat(espData.bounce_rate || 0) : null,
          last_tested: espData ? (espData.last_tested || null) : null,
        };
      });
      setSeeds(rows);
      setSeedsLoading(false);
    });
  }, []);

  const load = () => {
    setLoading(true); setFetchErr(null);
    fetch('/api/placement/summary', { headers: hdrs })
      .then(r => r.json())
      .then(d => { setSummary(d); setLoading(false); })
      .catch(e => { setFetchErr(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const badgeColor = (rate) => rate >= 80 ? 'var(--green)' : rate >= 50 ? 'var(--yellow)' : 'var(--red)';
  const badgeBg   = (rate) => rate >= 80 ? 'rgba(16,185,129,0.12)' : rate >= 50 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)';

  const s = {
    topBar:   { display: 'flex', gap: 10, marginBottom: 24 },
    btn:      { padding: '8px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--mono)' },
    btnPrimary: { padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    grid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 },
    card:     { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '20px 22px' },
    espName:  { fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 12 },
    badge:    (rate) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', background: badgeBg(rate), color: badgeColor(rate), marginBottom: 14 }),
    row:      { display: 'flex', justifyContent: 'space-between', marginBottom: 7, fontSize: 12 },
    lbl:      { color: 'var(--text3)', fontFamily: 'var(--mono)' },
    val:      { color: 'var(--text2)', fontWeight: 600 },
    divider:  { borderTop: '1px solid var(--border)', margin: '12px 0' },
    loadBox:  { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180, color: 'var(--text3)', fontSize: 13, fontFamily: 'var(--mono)' },
    emptyBox: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13, fontFamily: 'var(--mono)' },
    errBox:   { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '14px 18px', color: 'var(--red)', fontSize: 13, marginBottom: 18 },
    overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal:    { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '28px 32px', width: 420, maxWidth: '90vw' },
    mTitle:   { fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 20 },
    field:    { marginBottom: 14 },
    fieldLbl: { fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 5, display: 'block' },
    input:    { width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--mono)', boxSizing: 'border-box' },
    checkRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, color: 'var(--text2)' },
    mActions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22 },
    msgOk:    { fontSize: 12, color: 'var(--green)', fontFamily: 'var(--mono)', marginTop: 8 },
    msgErr:   { fontSize: 12, color: 'var(--red)',   fontFamily: 'var(--mono)', marginTop: 8 },
  };

  async function submitLog(e) {
    e.preventDefault(); setLogSubmitting(true); setLogMsg(null);
    try {
      const r = await fetch('/api/placement/test', { method: 'POST', headers: hdrs, body: JSON.stringify(logForm) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || r.statusText);
      setLogMsg({ ok: true, text: 'Test logged: ' + (d.test_id || logForm.test_id) });
      setLogForm({ test_id: '', campaign_id: '', esp: '', seed_address: '' });
      load();
    } catch (err) { setLogMsg({ ok: false, text: err.message }); }
    setLogSubmitting(false);
  }

  async function submitResult(e) {
    e.preventDefault(); setResultSubmitting(true); setResultMsg(null);
    try {
      const r = await fetch('/api/placement/result', { method: 'PATCH', headers: hdrs, body: JSON.stringify(resultForm) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || r.statusText);
      setResultMsg({ ok: true, text: 'Result saved.' });
      setResultForm({ test_id: '', seed_address: '', landed_inbox: false, landed_spam: false, bounced: false, notes: '' });
      load();
    } catch (err) { setResultMsg({ ok: false, text: err.message }); }
    setResultSubmitting(false);
  }

  const espRows = summary && summary.by_esp ? summary.by_esp : [];

  const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
  };

  return (
    <div>
      {/* SEED LIST STATUS */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            SEED LIST STATUS
          </div>
          <button
            style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--mono)' }}
            onClick={() => setActive && setActive('campaigns')}
          >
            ▶ Run Seed Test
          </button>
        </div>
        {seedsLoading ? (
          <div style={{ color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--mono)', padding: '12px 0' }}>Loading seed list…</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {seeds.map(p => {
              const tested = p.total_tests > 0;
              const statusColor = !tested ? 'var(--text3)' : p.bounce_rate > 20 ? 'var(--red)' : p.inbox_rate >= 80 ? 'var(--green)' : p.inbox_rate >= 50 ? 'var(--yellow)' : 'var(--red)';
              const statusLabel = !tested ? 'Not tested' : p.bounce_rate > 20 ? 'Bounced' : p.inbox_rate >= 80 ? 'Inbox' : p.inbox_rate >= 50 ? 'Mixed' : 'Spam';
              const statusBg = !tested ? 'rgba(100,116,139,0.1)' : p.bounce_rate > 20 ? 'rgba(239,68,68,0.1)' : p.inbox_rate >= 80 ? 'rgba(16,185,129,0.1)' : p.inbox_rate >= 50 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)';
              return (
                <div key={p.name} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{p.name}</div>
                  {p.email && (
                    <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email}</div>
                  )}
                  <div style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: statusBg, color: statusColor, marginBottom: 8 }}>
                    {statusLabel}
                  </div>
                  {tested && (
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                      <div>Inbox: {p.inbox_rate.toFixed(0)}% · Spam: {p.spam_rate.toFixed(0)}%</div>
                      {p.last_tested && <div style={{ marginTop: 3 }}>Tested: {fmtDate(p.last_tested)}</div>}
                    </div>
                  )}
                  {!tested && !p.email && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>No seed address</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={s.topBar}>
        <button style={s.btn} onClick={() => { setShowLog(true); setLogMsg(null); }}>+ Log Test</button>
        <button style={s.btn} onClick={() => { setShowResult(true); setResultMsg(null); }}>+ Log Result</button>
        <button style={s.btn} onClick={load}>Refresh</button>
      </div>

      {fetchErr && <div style={s.errBox}>Failed to load: {fetchErr}</div>}

      {loading ? (
        <div style={s.loadBox}>Loading placement data...</div>
      ) : espRows.length === 0 ? (
        <div style={s.emptyBox}>No placement tests yet. Use &quot;Log Test&quot; to record a seed send.</div>
      ) : (
        <div style={s.grid}>
          {espRows.map(esp => {
            const inbox = parseFloat(esp.inbox_rate ?? 0);
            return (
              <div key={esp.esp} style={s.card}>
                <div style={s.espName}>{esp.esp}</div>
                <div style={s.badge(inbox)}>{inbox.toFixed(1)}% Inbox</div>
                <div style={s.divider} />
                <div style={s.row}><span style={s.lbl}>SPAM RATE</span><span style={s.val}>{parseFloat(esp.spam_rate ?? 0).toFixed(1)}%</span></div>
                <div style={s.row}><span style={s.lbl}>BOUNCE RATE</span><span style={s.val}>{parseFloat(esp.bounce_rate ?? 0).toFixed(1)}%</span></div>
                <div style={s.row}><span style={s.lbl}>TOTAL TESTS</span><span style={s.val}>{esp.total_tests}</span></div>
              </div>
            );
          })}
        </div>
      )}

      {showLog && (
        <div style={s.overlay} onClick={() => setShowLog(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.mTitle}>Log Placement Test</div>
            <form onSubmit={submitLog}>
              {[['TEST ID', 'test_id'], ['CAMPAIGN ID', 'campaign_id'], ['ESP', 'esp'], ['SEED ADDRESS', 'seed_address']].map(([lbl, key]) => (
                <div key={key} style={s.field}>
                  <label style={s.fieldLbl}>{lbl}</label>
                  <input style={s.input} value={logForm[key]} onChange={e => setLogForm(f => ({ ...f, [key]: e.target.value }))} required={key === 'test_id' || key === 'esp'} />
                </div>
              ))}
              {logMsg && <div style={logMsg.ok ? s.msgOk : s.msgErr}>{logMsg.text}</div>}
              <div style={s.mActions}>
                <button type="button" style={s.btn} onClick={() => setShowLog(false)}>Cancel</button>
                <button type="submit" style={s.btnPrimary} disabled={logSubmitting}>{logSubmitting ? 'Saving...' : 'Log Test'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showResult && (
        <div style={s.overlay} onClick={() => setShowResult(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.mTitle}>Log Placement Result</div>
            <form onSubmit={submitResult}>
              {[['TEST ID', 'test_id'], ['SEED ADDRESS', 'seed_address'], ['NOTES', 'notes']].map(([lbl, key]) => (
                <div key={key} style={s.field}>
                  <label style={s.fieldLbl}>{lbl}</label>
                  <input style={s.input} value={resultForm[key]} onChange={e => setResultForm(f => ({ ...f, [key]: e.target.value }))} required={key === 'test_id'} />
                </div>
              ))}
              {[['landed_inbox', 'Landed in Inbox'], ['landed_spam', 'Landed in Spam'], ['bounced', 'Bounced']].map(([key, lbl]) => (
                <label key={key} style={s.checkRow}>
                  <input type="checkbox" checked={resultForm[key]} onChange={e => setResultForm(f => ({ ...f, [key]: e.target.checked }))} />
                  {lbl}
                </label>
              ))}
              {resultMsg && <div style={resultMsg.ok ? s.msgOk : s.msgErr}>{resultMsg.text}</div>}
              <div style={s.mActions}>
                <button type="button" style={s.btn} onClick={() => setShowResult(false)}>Cancel</button>
                <button type="submit" style={s.btnPrimary} disabled={resultSubmitting}>{resultSubmitting ? 'Saving...' : 'Save Result'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// UNIVERSE VIEW
// ============================================================

function UniverseView() {
  const [total, setTotal]           = useState(null);
  const [activity, setActivity]     = useState([]);
  const [industry, setIndustry]     = useState('');
  const [uState, setUState]         = useState('');
  const [size, setSize]             = useState('');
  const [matchCount, setMatchCount] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [newIds, setNewIds]         = useState(new Set());
  const [activeTab, setActiveTab]   = useState('overview');
  const [quality, setQuality]       = useState(null);
  const [loadingQuality, setLoadingQuality] = useState(false);
  const [pulling, setPulling]           = useState(false);
  const [pullResult, setPullResult]     = useState(null);

  useEffect(() => {
    fetch('/api/universe/stats', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => setTotal(d.total ?? d.count ?? 0))
      .catch(() => setTotal(0));
  }, []);

  useEffect(() => {
    const load = () =>
      fetch('/api/universe/activity', { headers: apiHeaders })
        .then(r => r.json())
        .then(d => {
          const rows = Array.isArray(d) ? d : (d.rows || []);
          setActivity(prev => {
            const prevIds = new Set(prev.map(r => r.id || (r.company_name + r.added_at)));
            const fresh = rows.filter(r => !prevIds.has(r.id || (r.company_name + r.added_at)));
            if (fresh.length) setNewIds(new Set(fresh.map(r => r.id || (r.company_name + r.added_at))));
            return rows;
          });
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (newIds.size === 0) return;
    const t = setTimeout(() => setNewIds(new Set()), 1200);
    return () => clearTimeout(t);
  }, [newIds]);

  useEffect(() => {
    if (!industry && !uState && !size) { setMatchCount(null); return; }
    setLoadingMatch(true);
    const params = new URLSearchParams();
    if (industry) params.set('industry', industry);
    if (uState)   params.set('state', uState);
    if (size)     params.set('size', size);
    fetch('/api/universe/count?' + params.toString(), { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setMatchCount(d.count ?? d.total ?? 0); setLoadingMatch(false); })
      .catch(() => { setMatchCount(0); setLoadingMatch(false); });
  }, [industry, uState, size]);

  useEffect(() => {
    if (activeTab !== 'quality') return;
    if (quality) return;
    setLoadingQuality(true);
    fetch('/api/universe/quality', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setQuality(d); setLoadingQuality(false); })
      .catch(() => setLoadingQuality(false));
  }, [activeTab]);

  const filtersSet = industry || uState || size;
  const displayCount = matchCount !== null ? matchCount : 0;

  const timeAgo = (ts) => {
    if (!ts) return '';
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60)    return diff + 's ago';
    if (diff < 3600)  return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return Math.floor(diff / 86400) + 'd ago';
  };

  const INDUSTRIES = ['Technology','Healthcare','Finance','Manufacturing','Retail','Real Estate','Education','Legal','Marketing','Logistics','Energy','Construction','Media','Hospitality','Other'];
  const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
  const SIZES = ['1-10','11-50','51-200','201-500','501-1000','1001-5000','5000+'];

  const s = {
    wrap:     { padding: '32px 32px 48px', maxWidth: 900 },
    counter:  { textAlign: 'center', marginBottom: 40, padding: '40px 32px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16 },
    num:      { fontSize: 72, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--accent)', letterSpacing: -2, lineHeight: 1 },
    numLbl:   { fontSize: 11, letterSpacing: 3, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 10, textTransform: 'uppercase' },
    pulseDot: { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 8px #10B981', marginRight: 8, animation: 'pulse 1.5s ease-in-out infinite' },
    ticker:   { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, marginBottom: 32, overflow: 'hidden' },
    tickHdr:  { padding: '12px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--mono)' },
    tickList: { maxHeight: 220, overflowY: 'auto', padding: '8px 0' },
    tickRow:  { padding: '8px 18px', fontSize: 12, color: 'var(--text2)', borderBottom: '1px solid rgba(30,42,58,0.5)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.4s' },
    tickNew:  { background: 'rgba(14,165,233,0.08)' },
    filters:  { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 20px 24px', marginBottom: 24 },
    filtHdr:  { fontSize: 11, fontWeight: 700, letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 16 },
    filtRow:  { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' },
    sel:      { flex: 1, minWidth: 160, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 8, padding: '8px 10px', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)', outline: 'none', cursor: 'pointer' },
    match:    { marginTop: 14, fontSize: 13, color: 'var(--text2)', fontFamily: 'var(--mono)', minHeight: 20 },
  };

  const FIELD_LABELS = {
    email: 'Email', company_name: 'Company Name', company_industry: 'Industry',
    company_state: 'State', company_size: 'Company Size', company_country: 'Country',
    company_url: 'Website', contact_first_name: 'First Name', contact_last_name: 'Last Name',
    contact_title: 'Job Title', source: 'Source'
  };

  return (
    <div style={s.wrap}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {['overview', 'quality'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '8px 20px', fontSize: 12, fontWeight: 700, border: 'none', borderRadius: '6px 6px 0 0',
            cursor: 'pointer', letterSpacing: 1, textTransform: 'uppercase', fontFamily: 'var(--mono)',
            background: activeTab === tab ? 'var(--accent)' : 'var(--surface2)',
            color: activeTab === tab ? '#000' : 'var(--text3)',
            borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
          }}>
            {tab === 'overview' ? 'LIVE UNIVERSE' : 'DATA QUALITY'}
          </button>
        ))}
      </div>

      {activeTab === 'quality' ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 28px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 24 }}>
            FIELD COVERAGE — UNIVERSE DATA QUALITY
          </div>
          {loadingQuality ? (
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading quality data…</div>
          ) : !quality ? (
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>No data.</div>
          ) : (quality.coverage || []).map(item => {
            const color = item.pct >= 80 ? '#10B981' : item.pct >= 40 ? '#F59E0B' : '#EF4444';
            return (
              <div key={item.field} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 0', borderBottom: '1px solid rgba(30,42,58,0.4)' }}>
                <div style={{ width: 160, flexShrink: 0, fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)' }}>{FIELD_LABELS[item.field] || item.field}</div>
                <div style={{ flex: 1, height: 8, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: item.pct + '%', height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
                </div>
                <div style={{ width: 44, textAlign: 'right', fontSize: 12, fontWeight: 700, fontFamily: 'var(--mono)', flexShrink: 0, color }}>{item.pct}%</div>
              </div>
            );
          })}
        </div>
      ) : (
      <>
      <div style={s.counter}>
        <div style={s.num}>{total === null ? '—' : total.toLocaleString()}</div>
        <div style={s.numLbl}><span style={s.pulseDot} />TOTAL PROSPECTS IN UNIVERSE</div>
      </div>

      <div style={s.ticker}>
        <div style={s.tickHdr}><span style={s.pulseDot} />LIVE ADDITIONS</div>
        <div style={s.tickList}>
          {activity.length === 0 ? (
            <div style={{ padding: '16px 18px', fontSize: 12, color: 'var(--text3)' }}>Waiting for activity…</div>
          ) : activity.map((row, i) => {
            const rowId = row.id || (row.company_name + row.added_at);
            const isNew = newIds.has(rowId);
            return (
              <div key={rowId + i} style={{ ...s.tickRow, ...(isNew ? s.tickNew : {}) }}>
                <span>✦ <strong>{row.company_name || '—'}</strong> added — {row.industry || '—'}, {row.state || '—'}</span>
                <span style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'var(--mono)', flexShrink: 0, marginLeft: 16 }}>{timeAgo(row.added_at || row.created_at)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={s.filters}>
        <div style={s.filtHdr}>FILTER AUDIENCE</div>
        <div style={s.filtRow}>
          <select style={s.sel} value={industry} onChange={e => setIndustry(e.target.value)}>
            <option value=''>All Industries</option>
            {INDUSTRIES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <select style={s.sel} value={uState} onChange={e => setUState(e.target.value)}>
            <option value=''>All States</option>
            {STATES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <select style={s.sel} value={size} onChange={e => setSize(e.target.value)}>
            <option value=''>All Company Sizes</option>
            {SIZES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div style={s.match}>
          {filtersSet && (loadingMatch
            ? 'Counting…'
            : matchCount !== null
              ? <><strong style={{ color: 'var(--accent)' }}>{displayCount.toLocaleString()}</strong> prospects match your filters</>
              : null
          )}
        </div>
        <button
          style={{ marginTop: 8, padding: '10px 24px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, transition: 'opacity 0.2s', background: filtersSet ? 'var(--accent)' : 'var(--surface2)', color: filtersSet ? '#fff' : 'var(--text3)', opacity: filtersSet ? 1 : 0.5, cursor: filtersSet ? 'pointer' : 'default' }}
          disabled={!filtersSet}
          onClick={async () => {
            setPulling(true);
            setPullResult(null);
            try {
              const body = {};
              if (industry) body.industry = industry;
              if (uState)   body.state    = uState;
              if (size)     body.size     = size;
              const r = await fetch('/api/universe/pull', {
                method: 'POST',
                headers: { ...apiHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
              });
              const d = await r.json();
              if (!r.ok) throw new Error(d.error || 'Pull failed');
              setPullResult({ ok: true, message: d.message });
            } catch (err) {
              setPullResult({ ok: false, message: err.message });
            } finally {
              setPulling(false);
            }
          }}
        >
          {pulling ? 'Pulling…' : 'Pull This Audience'}
        </button>
        {pullResult && (
          <div style={{ marginTop: 10, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: pullResult.ok ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            color: pullResult.ok ? 'var(--green)' : '#f87171', border: pullResult.ok ? '1px solid var(--green)' : '1px solid #f87171' }}>
            {pullResult.ok ? '✓ ' : '✗ '}{pullResult.message}
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}


// ============================================================
// VERIFICATION TOOLS
// ============================================================
function VerificationTools() {
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);

  const run = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const r = await fetch('/api/verify/all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...apiHeaders },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Verification failed');
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const badge = (pass, label) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
      background: pass ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
      color: pass ? 'var(--green)' : 'var(--red)',
      border: `1px solid ${pass ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
    }}>
      {pass ? '✓' : '✗'} {label}
    </span>
  );

  const card = (title, sub, children) => (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '20px 22px', flex: 1, minWidth: 200,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>{sub}</div>}
      {children}
    </div>
  );

  const row = (label, val) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 12, color: 'var(--text3)' }}>{label}</span>
      <span style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text)' }}>{val ?? '—'}</span>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '24px 28px', marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 16, letterSpacing: '0.5px' }}>Single Email Verification</div>
        <div style={{ display: 'flex', gap: 12 }}>
          <input
            type="email"
            placeholder="email@domain.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && run()}
            style={{
              flex: 1, background: 'var(--surface2)', border: '1px solid var(--border2)',
              borderRadius: 6, padding: '9px 14px', color: 'var(--text)', fontSize: 13,
              fontFamily: 'var(--mono)', outline: 'none',
            }}
          />
          <button
            onClick={run}
            disabled={loading || !email.trim()}
            style={{
              background: loading ? 'var(--surface2)' : 'var(--accent)',
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '9px 22px', fontSize: 13, fontWeight: 600,
              cursor: loading || !email.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !email.trim() ? 0.6 : 1,
              transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}
          >
            {loading ? 'Running…' : 'Run Full Verification'}
          </button>
        </div>
        {error && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: 'var(--red)', fontSize: 13 }}>
            {error}
          </div>
        )}
      </div>

      {result && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text2)' }}>{result.email}</span>
            {result.verdict && badge(result.verdict === 'pass', result.verdict === 'pass' ? 'PASS' : 'FAIL')}
            {result.score != null && (
              <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 4 }}>Score: <b style={{ color: 'var(--text)' }}>{result.score}</b></span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            {['layer1', 'layer2', 'layer3', 'layer4'].map((lk, idx) => {
              const layer = result[lk] || {};
              const pass = layer.verdict === 'pass' || layer.pass === true || layer.valid === true;
              const titles = ['Layer 1 — Syntax', 'Layer 2 — DNS / MX', 'Layer 3 — SMTP', 'Layer 4 — Human Score'];
              const subs = ['Format and structure check', 'Domain resolution and mail server', 'Mailbox acceptance probe', 'Engagement signal analysis'];
              return card(titles[idx], subs[idx],
                <div key={lk}>
                  <div style={{ marginBottom: 10 }}>{badge(pass, pass ? 'PASS' : 'FAIL')}</div>
                  {Object.entries(layer).filter(([k]) => k !== 'verdict' && k !== 'pass').map(([k, v]) =>
                    row(k, typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v))
                  )}
                  {Object.keys(layer).length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)' }}>No data returned</div>}
                </div>
              );
            })}
          </div>

          <details style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' }}>
            <summary style={{ fontSize: 12, color: 'var(--text3)', cursor: 'pointer', userSelect: 'none' }}>Raw response JSON</summary>
            <pre style={{ marginTop: 10, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text2)', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </>
      )}

      <div style={{
        marginTop: 28, background: 'var(--surface)', border: '2px dashed var(--border2)',
        borderRadius: 10, padding: '36px 28px', textAlign: 'center',
      }}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>⬆</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Bulk Email Verification</div>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>Upload a CSV or paste a list to verify thousands of emails at once across all 4 layers</div>
        <div style={{
          display: 'inline-block', padding: '8px 20px', borderRadius: 6,
          background: 'var(--surface2)', border: '1px solid var(--border2)',
          color: 'var(--text3)', fontSize: 12, fontWeight: 500,
        }}>Coming soon</div>
      </div>
    </div>
  );
}


const PAGE_COMPONENTS = {
  overview: Overview,
  leads: LeadIntelligence,
  cleaning: CleaningEngine,
  campaigns: Campaigns,
  engagement: EngagementIntelligence,
  placement: PlacementView,
  deliverability: Deliverability,
  pipeline: Pipeline,
  contacts: ContactsView,
  activity: ActivityFeedView,
  intelligence: IntelligenceView,
  accounts: AccountsView,
  analytics: Analytics,
  settings: Settings,
  integrations: IntegrationsView,
  onboarding: Onboarding,
  reports: Reports,
  search: ContactSearch,
  universe: UniverseView,
  verification: VerificationTools,
};



// ============================================================
// CLIENT DASHBOARD
// ============================================================

function ClientApp({ userEmail, onLogout }) {
  const [view, setView] = useState('campaigns');

  const navStyle = (active) => ({
    padding: '10px 22px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    color: active ? 'var(--accent2)' : 'var(--text3)',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  });

  return (
    <>
      <style>{styles}</style>
      <div className="app" style={{ flexDirection: 'column' }}>
        {/* Client Topbar */}
        <div style={{
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          padding: '0 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 56,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.5px', color: 'var(--text)' }}>
                TETRUS<span style={{ color: 'var(--accent)' }}>.</span>AI
              </div>
            </div>
            <div style={{ display: 'flex', gap: 0, height: 56, alignItems: 'stretch' }}>
              <div style={navStyle(view === 'campaigns')} onClick={() => setView('campaigns')}>My Campaigns</div>
              <div style={navStyle(view === 'pipeline')} onClick={() => setView('pipeline')}>My Pipeline</div>
              <div style={navStyle(view === 'opportunities')} onClick={() => setView('opportunities')}>Hot Opportunities</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{userEmail}</span>
            <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={onLogout}>Sign Out</button>
          </div>
        </div>

        {/* Client Content */}
        <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)' }}>
          <div className="content" style={{ maxWidth: 1200, margin: '0 auto' }}>
            {view === 'campaigns' && <ClientCampaigns />}
            {view === 'pipeline' && <ClientPipeline />}
            {view === 'opportunities' && <ClientOpportunities />}
          </div>
        </div>
      </div>
    </>
  );
}

function ClientCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('https://tetrus.ai/api/campaigns', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setCampaigns(d.campaigns || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const fmt = (n) => n != null ? Number(n).toLocaleString() : '0';
  const activeCampaigns = campaigns.filter(c => c.status === 'active');
  const totalMatched = campaigns.reduce((s, c) => s + (c.matched_leads || 0), 0);
  const totalQualified = campaigns.reduce((s, c) => s + (c.qualified_leads || 0), 0);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Loading campaigns...</div>;

  return (
    <>
      <div style={{ marginBottom: 24, paddingTop: 8 }}>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 4 }}>My Campaigns</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>CAMPAIGN PERFORMANCE OVERVIEW</div>
      </div>

      <div className="stat-grid">
        {[
          { label: 'Total Campaigns', value: campaigns.length.toString(), sub: activeCampaigns.length + ' active', color: '#0EA5E9' },
          { label: 'Matched Leads', value: fmt(totalMatched), sub: 'Across all campaigns', color: '#10B981' },
          { label: 'Qualified Leads', value: fmt(totalQualified), sub: 'Score above target', color: '#8B5CF6' },
          { label: 'Active Campaigns', value: activeCampaigns.length.toString(), sub: 'Currently running', color: '#F59E0B' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: '#FFFFFF'}}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {campaigns.length === 0 ? (
        <div className="pipeline-card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 28, opacity: 0.2, marginBottom: 12 }}>◷</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>No campaigns yet</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Your campaigns will appear here once they are launched.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {campaigns.map(c => (
            <div key={c.id} className="pipeline-card" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                    {c.target_industry || 'All Industries'} &nbsp;·&nbsp; Min Score: {c.target_min_score || 0}
                  </div>
                </div>
                <span className={'tag ' + (c.status === 'active' ? 'tag-green' : c.status === 'paused' ? 'tag-yellow' : 'tag-blue')}>
                  {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                {[
                  { label: 'Matched Leads', value: fmt(c.matched_leads), color: '#0EA5E9' },
                  { label: 'Qualified Leads', value: fmt(c.qualified_leads), color: '#10B981' },
                  { label: 'Avg. Score', value: c.avg_score || '—', color: '#F59E0B' },
                ].map(m => (
                  <div key={m.label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--mono)', color: '#FFFFFF' }}>{m.value}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 2 }}>{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function ClientPipeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('https://tetrus.ai/api/pipeline/stages', { headers: apiHeaders })
      .then(r => { if (!r.ok) throw new Error('API error'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const fmt = (n) => {
    if (n == null) return '—';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return Number(n).toLocaleString();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Loading pipeline...</div>;
  if (error) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--red)' }}>Failed to load pipeline data.</div>;

  const stages = data?.stages || [];
  const maxCount = stages.length > 0 ? Math.max(...stages.map(s => s.count || 0), 1) : 1;

  return (
    <>
      <div style={{ marginBottom: 24, paddingTop: 8 }}>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 4 }}>My Pipeline</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>LEAD QUALIFICATION STAGES</div>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {stages.map(st => (
          <div key={st.name} className="stat-card">
            <div className="stat-label">{st.name}</div>
            <div className="stat-value" style={{ color: '#FFFFFF', fontSize: 24 }}>{fmt(st.count)}</div>
            <div className="stat-sub">{st.desc}</div>
          </div>
        ))}
      </div>

      <div className="pipeline-card">
        <div className="card-header">
          <div className="card-title">Qualification Funnel</div>
          <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>
            {fmt(data?.totalClickHouse)} total &nbsp;→&nbsp; {fmt(data?.totalMautic)} engaged
            {(data?.crmSynced || 0) > 0 && <span style={{ color: 'var(--green)' }}> &nbsp;→&nbsp; {data.crmSynced} in CRM</span>}
          </div>
        </div>
        {stages.map((st, i) => {
          const pct = Math.max(3, ((st.count || 0) / maxCount) * 100);
          return (
            <div key={st.name} style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
              <div style={{ width: 148, fontSize: 12, color: 'var(--text2)', textAlign: 'right', flexShrink: 0 }}>{st.name}</div>
              <div style={{ flex: 1 }}>
                <div style={{ height: 32, borderRadius: 6, background: st.color, width: pct + '%', opacity: 0.85, transition: 'width 0.6s ease', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 12, minWidth: 64 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', color: '#fff' }}>{fmt(st.count)}</span>
                </div>
              </div>
              {i === stages.length - 1 && <div style={{ fontSize: 11, color: 'var(--red)', flexShrink: 0, fontWeight: 600 }}>CRM →</div>}
            </div>
          );
        })}
      </div>
    </>
  );
}

function ClientOpportunities() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(null);
  const [inviteResult, setInviteResult] = useState({});

  useEffect(() => {
    fetch('https://tetrus.ai/api/pipeline/stages', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const sendInvite = async (contact) => {
    setInviting(contact.id);
    try {
      const r = await fetch('https://tetrus.ai/api/send-meeting-invite', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: contact.email,
          first_name: contact.first_name,
          last_name: contact.last_name,
          company: contact.company,
          title: contact.title,
        }),
      });
      const d = await r.json();
      setInviteResult(prev => ({ ...prev, [contact.id]: d }));
    } catch {
      setInviteResult(prev => ({ ...prev, [contact.id]: { success: false, error: 'Network error' } }));
    }
    setInviting(null);
  };

  const fmt = (n) => {
    if (n == null) return '—';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return Number(n).toLocaleString();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Loading opportunities...</div>;

  const hotOps = data?.hotOpportunities || [];

  return (
    <>
      <div style={{ marginBottom: 24, paddingTop: 8 }}>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 4 }}>Hot Opportunities</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>HIGH-ENGAGEMENT CONTACTS — 61+ ENGAGEMENT POINTS</div>
      </div>

      <div className="stat-grid">
        {[
          { label: 'Hot Opportunities', value: hotOps.length.toString(), sub: '61+ engagement points', color: '#EF4444' },
          { label: 'Invites Sent', value: Object.values(inviteResult).filter(r => r?.success).length.toString(), sub: 'This session', color: '#10B981' },
          { label: 'In Pipeline', value: fmt(data?.totalMautic), sub: 'Total engaged contacts', color: '#0EA5E9' },
          { label: 'CRM Synced', value: fmt(data?.crmSynced), sub: 'Auto-synced contacts', color: '#8B5CF6' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: '#FFFFFF'}}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {hotOps.length === 0 ? (
        <div className="pipeline-card" style={{ textAlign: 'center', padding: 48, marginBottom: 0 }}>
          <div style={{ fontSize: 28, opacity: 0.2, marginBottom: 12 }}>◉</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>No hot opportunities yet</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 320, margin: '0 auto' }}>
            Contacts reach this stage after accumulating 61+ engagement points in your campaigns.
          </div>
        </div>
      ) : (
        <div className="table-card" style={{ marginBottom: 0 }}>
          <div className="card-header" style={{ padding: '18px 20px 14px' }}>
            <div className="card-title">Hot Opportunities</div>
            <span className="tag tag-red">{hotOps.length} contacts ready for outreach</span>
          </div>
          <table>
            <thead>
              <tr><th>Contact</th><th>Email</th><th>Company</th><th>Title</th><th>Points</th><th>Action</th></tr>
            </thead>
            <tbody>
              {hotOps.map(r => {
                const res = inviteResult[r.id];
                const name = [r.first_name, r.last_name].filter(Boolean).join(' ') || '—';
                return (
                  <tr key={r.id}>
                    <td className="td-name">{name}</td>
                    <td style={{ fontSize: 13, color: '#CBD5E1' }}>{r.email}</td>
                    <td>{r.company || '—'}</td>
                    <td style={{ fontSize: 13, color: '#CBD5E1' }}>{r.title || '—'}</td>
                    <td><span className="tag tag-red" style={{ fontWeight: 700 }}>{r.points}</span></td>
                    <td>
                      {res?.success ? (
                        <span className="tag tag-green" style={{ fontSize: 11 }}>✓ Invite Sent</span>
                      ) : res?.error ? (
                        <span className="tag tag-red" style={{ fontSize: 11, cursor: 'pointer' }} onClick={() => sendInvite(r)}>Failed — Retry</span>
                      ) : (
                        <button
                          className="btn btn-primary"
                          style={{ fontSize: 11, padding: '4px 14px', background: '#EF4444', borderColor: '#EF4444' }}
                          disabled={inviting === r.id}
                          onClick={() => sendInvite(r)}
                        >
                          {inviting === r.id ? 'Sending...' : 'Book Meeting'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('https://tetrus.ai/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Invalid email or password.');
        setLoading(false);
        return;
      }
      onLogin(data.email, data.token, data.role);
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="login-page">
        <div className="login-card">
          <div className="login-logo">
            <div className="login-logo-name">TETRUS<span>.</span>AI</div>
            <div className="login-logo-tag">Lead-to-Revenue Platform</div>
          </div>
          <div className="login-divider" />
          <div className="login-title">Sign in to your account</div>
          <div className="login-subtitle">Access your lead intelligence dashboard</div>
          {error && <div className="login-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="login-field">
              <label className="login-label">Email</label>
              <input
                className="login-input"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </div>
            <div className="login-field">
              <label className="login-label">Password</label>
              <input
                className="login-input"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          <div className="login-footer">Secured by TETRUS.AI</div>
        </div>
      </div>
    </>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const ref = useRef(null);

  const load = () => {
    fetch('/api/notifications?limit=15', { headers: apiHeaders })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = (e) => {
    e.stopPropagation();
    fetch('/api/notifications/all/read', { method: 'PUT', headers: apiHeaders })
      .then(() => load()).catch(() => {});
  };

  const markRead = (id) => {
    fetch(`/api/notifications/${id}/read`, { method: 'PUT', headers: apiHeaders })
      .then(() => load()).catch(() => {});
  };

  const typeIcon = (type) => {
    if (type === 'hot_opportunity') return '\uD83D\uDD25';
    if (type === 'deliverability_alert') return '\u26A0\uFE0F';
    if (type === 'data_update') return '\uD83D\uDCCA';
    return '\uD83D\uDD14';
  };

  const typeColor = (type) => {
    if (type === 'hot_opportunity') return '#ff6b35';
    if (type === 'deliverability_alert') return '#ffcc00';
    if (type === 'data_update') return '#00d4ff';
    return '#CBD5E1';
  };

  const unread = data.unread_count || 0;
  const notes = data.notifications || [];

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        onClick={() => { setOpen(o => !o); }}
        style={{
          background: 'none', border: '1px solid #2a2a4a', borderRadius: 6,
          cursor: 'pointer', position: 'relative', padding: '5px 10px',
          color: '#E2E8F0', fontSize: 18, lineHeight: 1, display: 'flex',
          alignItems: 'center', gap: 6
        }}
        title="Notifications"
      >
        &#128276;
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: '#ff4444', color: '#fff', borderRadius: '50%',
            fontSize: 9, fontWeight: 700, minWidth: 16, height: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px', lineHeight: 1, border: '1px solid #0f0f23'
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '110%',
          width: 360, background: '#0f0f23', border: '1px solid #2a2a4a',
          borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          zIndex: 2000, overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderBottom: '1px solid #2a2a4a'
          }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
              Notifications{unread > 0 ? <span style={{ color: '#CBD5E1', fontWeight: 400 }}> ({unread} unread)</span> : ''}
            </span>
            {unread > 0 && (
              <button onClick={markAllRead} style={{
                background: 'none', border: 'none', color: '#00d4ff',
                cursor: 'pointer', fontSize: 12, padding: 0
              }}>Mark all read</button>
            )}
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {notes.length === 0 ? (
              <div style={{ padding: '24px 16px', color: '#CBD5E1', textAlign: 'center', fontSize: 13 }}>
                No notifications yet
              </div>
            ) : notes.map(n => (
              <div
                key={n.id}
                onClick={() => markRead(n.id)}
                style={{
                  padding: '10px 16px', borderBottom: '1px solid #1a1a2e',
                  background: n.read ? 'transparent' : 'rgba(0,212,255,0.04)',
                  cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start'
                }}
              >
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{typeIcon(n.type)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: typeColor(n.type), fontSize: 11, fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {n.type.replace(/_/g, ' ')}
                  </div>
                  <div style={{ color: n.read ? '#CBD5E1' : '#E2E8F0', fontSize: 13, lineHeight: 1.4, wordBreak: 'break-word' }}>
                    {n.message}
                  </div>
                  <div style={{ color: '#CBD5E1', fontSize: 11, marginTop: 4 }}>
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
                {!n.read && (
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#00d4ff', flexShrink: 0, marginTop: 5 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function ContactSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [mauticStatus, setMauticStatus] = useState({});
  const [tagsMap, setTagsMap] = useState({});
  const [showTagInput, setShowTagInput] = useState({});
  const [tagInputs, setTagInputs] = useState({});

  const doSearch = async (q) => {
    if (!q || q.trim().length < 2) return;
    setLoading(true);
    setSearched(true);
    try {
      const r = await fetch('/api/search?q=' + encodeURIComponent(q.trim()), { headers: apiHeaders });
      const d = await r.json();
      const results = d.results || [];
      setResults(results);
      const emails = results.map(r => r.email).filter(Boolean);
      if (emails.length) {
        fetch('/api/leads/tags/batch?emails=' + emails.map(encodeURIComponent).join(','), { headers: apiHeaders })
          .then(r => r.json()).then(tm => setTagsMap(tm)).catch(() => {});
      }
    } catch (e) {
      setResults([]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') doSearch(query);
  };

  const addToMautic = async (lead) => {
    const key = lead.email;
    setMauticStatus(s => ({ ...s, [key]: 'loading' }));
    try {
      const r = await fetch('/api/search/add-to-mautic', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: lead.email,
          firstname: lead.contact_first_name,
          lastname: lead.contact_last_name,
          title: lead.contact_title,
          company: lead.company_name,
        }),
      });
      const d = await r.json();
      if (d.ok) {
        setMauticStatus(s => ({ ...s, [key]: d.already_existed ? 'exists' : 'added' }));
      } else {
        setMauticStatus(s => ({ ...s, [key]: 'error' }));
      }
    } catch {
      setMauticStatus(s => ({ ...s, [key]: 'error' }));
    }
  };

  const addTag = async (email, rawTag) => {
    const tag = rawTag.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '').slice(0, 80);
    if (!tag) return;
    setTagInputs(m => ({ ...m, [email]: '' }));
    setShowTagInput(m => ({ ...m, [email]: false }));
    try {
      await fetch('/api/leads/' + encodeURIComponent(email) + '/tags', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag }),
      });
      setTagsMap(m => ({ ...m, [email]: [...new Set([...(m[email] || []), tag])] }));
    } catch {}
  };

  const removeTag = async (email, tag) => {
    setTagsMap(m => ({ ...m, [email]: (m[email] || []).filter(t => t !== tag) }));
    try {
      await fetch('/api/leads/' + encodeURIComponent(email) + '/tags/' + encodeURIComponent(tag), {
        method: 'DELETE', headers: apiHeaders,
      });
    } catch {}
  };

  const TAG_PALETTE = ['#a78bfa','#34d399','#60a5fa','#f59e0b','#f87171','#00d4ff','#fb923c'];
  const hashColor = (tag) => TAG_PALETTE[Math.abs(tag.split('').reduce((a,c) => a + c.charCodeAt(0), 0)) % TAG_PALETTE.length];

  const stageColor = (stage) => {
    const map = {
      'Hot Opportunity': '#ff6b35',
      'Qualified Lead': '#00d4ff',
      'MQL': '#a78bfa',
      'Raw Lead': '#CBD5E1',
      'Cold': '#CBD5E1',
    };
    return map[stage] || '#CBD5E1';
  };

  const mauticBtnLabel = (email) => {
    const s = mauticStatus[email];
    if (s === 'loading') return '...';
    if (s === 'added') return '✓ Added';
    if (s === 'exists') return '✓ In Campaign Automation';
    if (s === 'error') return '✕ Error';
    return '+ Campaign Automation';
  };

  const mauticBtnStyle = (email) => {
    const s = mauticStatus[email];
    const base = { fontSize: 11, padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' };
    if (s === 'added') return { ...base, background: 'rgba(16,185,129,0.15)', color: '#10b981', cursor: 'default' };
    if (s === 'exists') return { ...base, background: 'rgba(0,212,255,0.1)', color: '#00d4ff', cursor: 'default' };
    if (s === 'error') return { ...base, background: 'rgba(239,68,68,0.15)', color: '#ef4444', cursor: 'default' };
    if (s === 'loading') return { ...base, background: '#1a1a2e', color: '#CBD5E1', cursor: 'wait' };
    return { ...base, background: 'rgba(0,212,255,0.12)', color: '#00d4ff' };
  };

  const thStyle = { textAlign: 'left', padding: '10px 12px', color: '#CBD5E1', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #1a1a2e', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '10px 12px', fontSize: 13, color: '#E2E8F0', borderBottom: '1px solid #111' };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      {/* Search bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#CBD5E1', fontSize: 16, pointerEvents: 'none' }}>⌕</span>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Search by email, company name, or contact name..."
            autoFocus
            style={{
              width: '100%', padding: '11px 14px 11px 38px',
              background: '#1a1a2e', border: '1px solid #2a2a4a',
              borderRadius: 8, color: '#fff', fontSize: 14,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        <button
          onClick={() => doSearch(query)}
          disabled={loading || query.trim().length < 2}
          style={{
            padding: '11px 24px', background: '#00d4ff', color: '#000',
            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13,
            cursor: loading ? 'wait' : 'pointer', whiteSpace: 'nowrap',
          }}
        >{loading ? 'Searching...' : 'Search'}</button>
      </div>

      {/* Hint before first search */}
      {!searched && (
        <div style={{ color: '#CBD5E1', textAlign: 'center', padding: '60px 0', fontSize: 14 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⌕</div>
          Search across {(19000).toLocaleString()}+ leads by email, company, or contact name
        </div>
      )}

      {/* No results */}
      {searched && !loading && results.length === 0 && (
        <div style={{ color: '#CBD5E1', textAlign: 'center', padding: '48px 0', fontSize: 14 }}>
          No leads found matching <strong style={{ color: '#CBD5E1' }}>"{query}"</strong>
        </div>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div style={{ background: '#0f0f1e', border: '1px solid #1a1a2e', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #1a1a2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#CBD5E1', fontSize: 13 }}>
              {results.length} result{results.length !== 1 ? 's' : ''} for <strong style={{ color: '#E2E8F0' }}>"{query}"</strong>
              {results.length === 50 && <span style={{ color: '#CBD5E1' }}> (showing top 50 by score)</span>}
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Company</th>
                  <th style={thStyle}>Industry</th>
                  <th style={thStyle}>Location</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Score</th>
                  <th style={thStyle}>Stage</th>
                  <th style={thStyle}>Action</th>
                  <th style={thStyle}>Tags</th>
                </tr>
              </thead>
              <tbody>
                {results.map((lead, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                    <td style={tdStyle}>
                      <a href={'mailto:' + lead.email} style={{ color: '#00d4ff', textDecoration: 'none' }}>{lead.email}</a>
                    </td>
                    <td style={tdStyle}>
                      {[lead.contact_first_name, lead.contact_last_name].filter(Boolean).join(' ') || <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, color: '#CBD5E1', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lead.contact_title}>
                      {lead.contact_title || <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lead.company_name}>
                      {lead.company_name || <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, color: '#CBD5E1', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lead.company_industry}>
                      {lead.company_industry || <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, color: '#CBD5E1', whiteSpace: 'nowrap' }}>
                      {[lead.company_city, lead.company_state].filter(Boolean).join(', ') || <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <span style={{
                        fontWeight: 700, fontFamily: 'monospace', fontSize: 14,
                        color: lead.human_score >= 80 ? '#10b981' : lead.human_score >= 60 ? '#00d4ff' : lead.human_score >= 40 ? '#a78bfa' : '#666'
                      }}>{lead.human_score}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        background: stageColor(lead.stage) + '1a',
                        color: stageColor(lead.stage),
                        padding: '2px 8px', borderRadius: 4,
                        fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'
                      }}>{lead.stage}</span>
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => addToMautic(lead)}
                        disabled={!!mauticStatus[lead.email]}
                        style={mauticBtnStyle(lead.email)}
                      >{mauticBtnLabel(lead.email)}</button>
                    </td>
                    <td style={{ ...tdStyle, minWidth: 180 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                        {(tagsMap[lead.email] || []).map(tag => (
                          <span key={tag} style={{
                            background: hashColor(tag) + '1a', color: hashColor(tag),
                            padding: '2px 7px', borderRadius: 4, fontSize: 11,
                            display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap',
                          }}>
                            {tag}
                            <span
                              onClick={() => removeTag(lead.email, tag)}
                              style={{ cursor: 'pointer', opacity: 0.6, fontSize: 12, lineHeight: 1, marginLeft: 1 }}
                              title="Remove tag"
                            >×</span>
                          </span>
                        ))}
                        {showTagInput[lead.email] ? (
                          <input
                            autoFocus
                            type="text"
                            value={tagInputs[lead.email] || ''}
                            onChange={e => setTagInputs(m => ({ ...m, [lead.email]: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === 'Enter') addTag(lead.email, tagInputs[lead.email] || '');
                              if (e.key === 'Escape') setShowTagInput(m => ({ ...m, [lead.email]: false }));
                            }}
                            onBlur={() => {
                              if (tagInputs[lead.email]?.trim()) addTag(lead.email, tagInputs[lead.email]);
                              else setShowTagInput(m => ({ ...m, [lead.email]: false }));
                            }}
                            placeholder="tag name…"
                            style={{
                              width: 90, background: '#0f0f1e', border: '1px solid #2a2a4a',
                              borderRadius: 4, color: '#E2E8F0', padding: '2px 6px',
                              fontSize: 11, outline: 'none', fontFamily: 'inherit',
                            }}
                          />
                        ) : (
                          <span
                            onClick={() => setShowTagInput(m => ({ ...m, [lead.email]: true }))}
                            style={{ color: '#CBD5E1', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
                            title="Add tag"
                          >+</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// CHAT LANDING PAGE
// ============================================================
function ChatLanding() {
  const params = new URLSearchParams(window.location.search);
  const company = params.get('company') || params.get('c') || '';
  const contactName = params.get('contact') || params.get('n') || '';

  const [name, setName]           = useState(contactName);
  const [email, setEmail]         = useState('');
  const [phone, setPhone]         = useState('');
  const [bestTime, setBestTime]   = useState('');
  const [message, setMessage]     = useState('');
  const [status, setStatus]       = useState(null); // null | 'loading' | 'success' | 'error'
  const [errMsg, setErrMsg]       = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim())  { setErrMsg('Please enter your name.'); setStatus('error'); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrMsg('Please enter a valid email address.'); setStatus('error'); return;
    }
    setStatus('loading');
    try {
      const res = await fetch('/api/chat/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: company || null,
          name: name.trim(), email: email.trim(),
          phone: phone.trim() || null,
          best_time: bestTime || null,
          message: message.trim() || null,
        }),
      });
      const d = await res.json();
      if (res.ok && d.ok) { setStatus('success'); }
      else { setErrMsg(d.error || 'Submission failed. Please try again.'); setStatus('error'); }
    } catch { setErrMsg('Network error. Please try again.'); setStatus('error'); }
  };

  const inp = {
    width: '100%', padding: '10px 13px', background: 'var(--surface2)',
    border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 7,
    fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
  };

  return (
    <div style={{ fontFamily: 'var(--font)', background: 'var(--bg)', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Top bar */}
      <div style={{ width: '100%', background: 'rgba(10,15,30,0.97)', borderBottom: '1px solid var(--border2)', padding: '16px 32px', display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ width: 32, height: 32, background: 'linear-gradient(135deg,#38bdf8,#818cf8)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: '#fff', fontSize: 16 }}>T</div>
        <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.06em', color: 'var(--text)' }}>TETRUS<span style={{ color: 'var(--accent)' }}>.</span>AI</span>
      </div>

      {/* Hero */}
      <div style={{ maxWidth: 680, width: '100%', padding: '56px 24px 32px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: 'var(--accent)', borderRadius: 20, padding: '5px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 20 }}>
          &#9679; Exclusive Opportunity
        </div>
        <h1 style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-.02em', marginBottom: 12, color: 'var(--text)' }}>
          Let&#39;s <span style={{ color: 'var(--accent)' }}>Talk Revenue</span>
        </h1>
        {company && (
          <p style={{ fontSize: 13, color: '#CBD5E1', marginBottom: 8 }}>
            You&#39;re connecting on behalf of <strong style={{ color: '#CBD5E1' }}>{company}</strong>
          </p>
        )}
        <p style={{ fontSize: 15, color: '#CBD5E1', lineHeight: 1.65, maxWidth: 480, margin: '0 auto 32px' }}>
          Our team is ready to walk you through a personalized demo and build a custom outbound strategy for your business. Fill out the form and we&#39;ll reach out within one business day.
        </p>

        {/* Stats strip */}
        <div style={{ display: 'flex', border: '1px solid var(--border2)', borderRadius: 10, overflow: 'hidden', maxWidth: 500, margin: '0 auto 36px' }}>
          {[['512M+','Verified Leads'],['98.2%','Deliverability'],['3.4×','Avg Revenue Lift']].map(([v,l]) => (
            <div key={l} style={{ flex: 1, padding: '14px 10px', textAlign: 'center', borderRight: '1px solid var(--border2)', background: 'var(--surface)' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-.02em' }}>{v}</div>
              <div style={{ fontSize: 10, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '.07em', marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact alternatives */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 28, marginBottom: 28, justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 4 }}>Call Us Directly</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent)', letterSpacing: '.04em' }}>888.983.8787</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>(888) 983-8787</div>
        </div>
        <div style={{ width: 1, height: 36, background: 'var(--border2)' }} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 4 }}>Or Fill Out the Form</div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>We reply within 1 business day</div>
        </div>
      </div>

      {/* Form card */}
      <div style={{ width: '100%', maxWidth: 560, background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 14, padding: '36px 36px 32px', margin: '0 24px 48px' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Request a Strategy Call</div>
        <div style={{ fontSize: 11, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 28 }}>All fields marked <span style={{ color: '#ef4444' }}>*</span> are required</div>

        {status === 'error' && (
          <div style={{ padding: '13px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 20, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', color: '#ef4444' }}>{errMsg}</div>
        )}
        {status === 'success' ? (
          <div style={{ padding: '24px', borderRadius: 8, background: 'rgba(16,185,129,.08)', border: '1px solid rgba(16,185,129,.3)', color: '#10b981', fontSize: 14, fontWeight: 600, textAlign: 'center', lineHeight: 1.6 }}>
            &#10003; Thank you! We will be in touch shortly.<br />
            <span style={{ fontSize: 12, fontWeight: 400, color: '#CBD5E1' }}>A member of our team will reach out within one business day.</span>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 7 }}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input style={inp} type="text" placeholder="Jane Smith" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 7 }}>Work Email <span style={{ color: '#ef4444' }}>*</span></label>
                <input style={inp} type="email" placeholder="jane@company.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 7 }}>Phone Number</label>
                <input style={inp} type="tel" placeholder="+1 (555) 000-0000" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 7 }}>Best Time to Call</label>
                <select style={{ ...inp, cursor: 'pointer' }} value={bestTime} onChange={e => setBestTime(e.target.value)}>
                  <option value="">Select a time...</option>
                  <option>Morning (8am – 12pm CT)</option>
                  <option>Afternoon (12pm – 4pm CT)</option>
                  <option>Late Afternoon (4pm – 6pm CT)</option>
                  <option>Flexible / Anytime</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 7 }}>Message / What Are You Looking to Achieve?</label>
              <textarea style={{ ...inp, minHeight: 90, resize: 'vertical', lineHeight: 1.5 }} placeholder="Tell us about your current outbound efforts, target audience, and goals..." value={message} onChange={e => setMessage(e.target.value)} />
            </div>
            <button type="submit" disabled={status === 'loading'} style={{ width: '100%', padding: 13, background: 'linear-gradient(135deg,#0ea5e9,#6366f1)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, borderRadius: 8, cursor: status === 'loading' ? 'not-allowed' : 'pointer', opacity: status === 'loading' ? 0.7 : 1, letterSpacing: '.02em', marginTop: 6 }}>
              {status === 'loading' ? 'Submitting...' : 'Request Strategy Call \u2192'}
            </button>
          </form>
        )}
        <p style={{ fontSize: 11, color: '#CBD5E1', textAlign: 'center', marginTop: 14, lineHeight: 1.6 }}>
          &#128274; Your information is kept private and never shared.
        </p>
      </div>
    </div>
  );
}

function App() {
  const [liveStats, setLiveStats] = useState({
    totalLeads: 0, cleanLeads: 0, syntaxValid: 0, mxValid: 0, verified: 0,
    bounced: 0, eliminated: 0, avgHumanScore: 0, highScore: 0, medScore: 0,
    lowScore: 0, industryCount: 0, countryCount: 0, companyCount: 0, humanEngagementRate: 0, sendgridVerified: 0,
    topIndustries: [], topLists: [], dailyImports: [], topCountries: []
  });
  const [showImport, setShowImport] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignRefreshKey, setCampaignRefreshKey] = useState(0);
  const [topbarCleaning, setTopbarCleaning] = useState(false);
  const [topbarExtracting, setTopbarExtracting] = useState(false);

  const runTopbarClean = async () => {
    setTopbarCleaning(true);
    try {
      const r = await fetch('https://tetrus.ai/api/enrich', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchSize: 2000 }),
      });
      const d = await r.json();
      if (!r.ok) alert(d.error || 'Cleaning failed');
      else { fetchStats(); }
    } catch (e) { alert('Error: ' + e.message); }
    setTopbarCleaning(false);
  };

  const extractTopbarSegment = async () => {
    setTopbarExtracting(true);
    try {
      const resp = await fetch('https://tetrus.ai/api/leads/extract', { headers: apiHeaders });
      if (!resp.ok) { const e = await resp.json(); alert('Extract failed: ' + (e.error || 'Unknown')); setTopbarExtracting(false); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tetrus-segment-' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Network error: ' + e.message); }
    setTopbarExtracting(false);
  };
  const [active, setActive] = useState("overview");
  const [accountFilter, setAccountFilter] = useState(null);
  const [loggedIn, setLoggedIn] = useState(() => sessionStorage.getItem('tetrus_auth') === 'true' && !!sessionStorage.getItem('tetrus_token'));
  const [userEmail, setUserEmail] = useState(() => sessionStorage.getItem('tetrus_user') || '');
  const [userRole, setUserRole] = useState(() => sessionStorage.getItem('tetrus_role') || 'client');
  const fetchStats = () =>
    fetch('https://tetrus.ai/api/stats', { headers: apiHeaders })
      .then(r => r.json()).then(d => setLiveStats(d)).catch(() => {});
  useEffect(() => { fetchStats(); }, []);

  const handleLogin = (email, token, role) => {
    sessionStorage.setItem('tetrus_auth', 'true');
    sessionStorage.setItem('tetrus_user', email);
    sessionStorage.setItem('tetrus_token', token);
    sessionStorage.setItem('tetrus_role', role || 'client');
    setLoggedIn(true);
    setUserEmail(email);
    setUserRole(role || 'client');
  };

  const handleLogout = () => {
    sessionStorage.clear();
    setLoggedIn(false);
    setUserEmail('');
    setUserRole('client');
    setActive('overview');
  };

  if (window.location.pathname === '/chat') return <ChatLanding />;
  if (!loggedIn) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Client role — simplified read-only dashboard
  if (userRole === 'client') {
    return <ClientApp userEmail={userEmail} onLogout={handleLogout} />;
  }

  // Admin role — full dashboard
  const page = PAGES[active];
  const PageComponent = PAGE_COMPONENTS[active];

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <div className="sidebar">
          <div className="logo" onClick={() => setActive("overview")} style={{ cursor: "pointer" }}>
            <div className="logo-name">TETRUS<span>.</span>AI</div>
            <div className="logo-tag">Lead-to-Revenue Platform</div>
          </div>
          <nav className="nav">
            {(userRole === 'client' ? NAV.filter(i => ['overview','campaigns','pipeline','reports'].includes(i.id)) : NAV).map((item) => (
              <React.Fragment key={item.id}>
                {(item.id === "pipeline" || item.id === "integrations") && <div style={{ height: 1, background: "rgba(255,255,255,0.25)", margin: "16px 10px 14px 10px" }} />}
                <div
                className={`nav-item ${active === item.id ? "active" : ""}`}
                onClick={() => setActive(item.id)}
              >
                <span className="nav-icon">{item.icon}</span>
                {item.label}
                {item.badge && active !== item.id && (
                  <span className="nav-badge">{item.badge}</span>
                )}
              </div>
              </React.Fragment>
            ))}
          </nav>
          <div className="status-bar">
            <div className="status-dot" />
            <div className="status-text">All Systems <span>Healthy</span></div>
          </div>
        </div>

        <div className="main">
          <div className="topbar">
            <div>
              <div className="page-title">{page.title}</div>
              <div className="page-sub">{page.sub}</div>
            </div>
            <div className="topbar-actions">
              {active === 'leads' && (
                <button className="btn btn-ghost" onClick={() => setShowImport(true)}>Import Leads</button>
              )}
              {active === 'cleaning' && (
                <button className="btn btn-primary" onClick={runTopbarClean} disabled={topbarCleaning}>
                  {topbarCleaning ? 'Running…' : 'Run Cleaning'}
                </button>
              )}
              {active === 'campaigns' && (
                <button className="btn btn-primary" onClick={() => setShowCampaignModal(true)}>New Campaign</button>
              )}
              {(active === 'analytics' || active === 'reports') && (
                <button className="btn btn-ghost" onClick={() => window.open('https://tetrus.ai/api/export', '_blank')}>Export</button>
              )}
              {userRole === 'operator' && (
                <div style={{display:'flex',gap:4,background:'var(--surface2)',borderRadius:20,padding:'3px 4px',border:'1px solid var(--border2)'}}>
                  <button onClick={()=>setUserRole('operator')} style={{padding:'4px 12px',borderRadius:16,border:'none',cursor:'pointer',fontSize:11,fontWeight:600,background:'var(--accent)',color:'#fff'}}>Operator</button>
                  <button onClick={()=>{setUserRole('client');}} style={{padding:'4px 12px',borderRadius:16,border:'none',cursor:'pointer',fontSize:11,fontWeight:600,background:'transparent',color:'var(--text3)'}}>Client</button>
                </div>
              )}
              <NotificationBell />
            </div>
          </div>
          <div className="content">
            <PageComponent liveStats={liveStats} campaignRefreshKey={campaignRefreshKey} onShowCreateCampaign={() => setShowCampaignModal(true)} onRefreshStats={fetchStats} accountFilter={accountFilter} onFilterByAccount={(name) => setAccountFilter(name)} onClearAccountFilter={() => setAccountFilter(null)} setActive={setActive} />
          </div>
        </div>
      </div>
      {showImport && <ImportTool onClose={() => setShowImport(false)} />}
      {showCampaignModal && (
        <CreateCampaignModal
          onClose={() => setShowCampaignModal(false)}
          onCreated={() => {
            setShowCampaignModal(false);
            setCampaignRefreshKey(k => k + 1);
            if (active !== "campaigns") setActive("campaigns");
          }}
        />
      )}
      <HelpChatWidget active={active} userEmail={userEmail} />
    </>
  );
}


// ============================================================
// HELP CHAT WIDGET
// ============================================================
const SCREEN_LABELS = {
  overview: 'Overview', leads: 'Lead Intelligence', cleaning: 'Cleaning Engine',
  campaigns: 'Campaign Automation', sequences: 'Sequences', engagement: 'Engagement Intelligence',
  deliverability: 'Deliverability', pipeline: 'Sales Pipeline', contacts: 'Contacts', intelligence: 'Reply Intelligence',
  analytics: 'Analytics',
  placement: 'Inbox Placement', settings: 'Settings', integrations: 'Integrations', onboarding: 'Onboarding', reports: 'Reports', search: 'Search', universe: 'Universe', verification: 'Verification Tools',
};

function HelpChatWidget({ active, userEmail }) {
  const WELCOME = 'Hi! I’m the TETRUS AI support agent. How can I help you with the platform today?';
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: WELCOME }]);
  const [input, setInput]       = useState('');
  const [sending, setSending]   = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const bottomRef      = useRef(null);

  const screenName = SCREEN_LABELS[active] || active || 'Dashboard';

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Reset on screen change
  useEffect(() => {
    if (!open) return;
    setMessages([{ role: 'assistant', content: WELCOME }]);
    setEscalated(false);
  }, [active]);

  const send = async (overrideMsg) => {
    const userMsg = (overrideMsg || input).trim();
    if (!userMsg || sending) return;
    setInput('');
    const updated = [...messages, { role: 'user', content: userMsg }];
    setMessages(updated);
    setSending(true);
    try {
      const r = await fetch('/api/help/chat', {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: updated.slice(0, -1),
          userMessage: userMsg,
          currentScreen: screenName,
          userEmail: userEmail || '',
        }),
      });
      const d = await r.json();
      if (d.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: d.reply }]);
        if (d.escalation) setEscalated(true);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
    }
    setSending(false);
  };

  const escalateNow = () => {
    send('I need to speak with a real person. Please escalate this to a human agent.');
  };

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const toggleMic = () => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Speech recognition not supported in this browser.'); return; }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'en-US';
    rec.onresult = (e) => {
      const t = Array.from(e.results).map(r => r[0].transcript).join('');
      setInput(prev => { const base = prev.trimEnd(); return base ? base + ' ' + t : t; });
    };
    rec.onerror = () => setListening(false);
    rec.onend   = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  const s = {
    bubble: {
      position: 'fixed', bottom: 28, right: 28, zIndex: 1200,
      width: 52, height: 52, borderRadius: '50%',
      background: 'linear-gradient(135deg, #0EA5E9, #8B5CF6)',
      border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 20px rgba(14,165,233,0.4)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      color: '#fff', fontSize: 22, fontWeight: 700,
    },
    panel: {
      position: 'fixed', bottom: 92, right: 28, zIndex: 1200,
      width: 360, height: 520,
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
      display: 'flex', flexDirection: 'column',
      boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
      animation: 'fadeInUp 0.2s ease',
    },
    hdr: {
      padding: '14px 16px', borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', gap: 8,
    },
    dot: { width: 7, height: 7, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 5px #10B981', flexShrink: 0 },
    hdrTitle: { flex: 1, fontSize: 13, fontWeight: 700, color: '#F1F5F9' },
    hdrScreen: { fontSize: 10, color: '#64748B', fontFamily: 'var(--mono)', textTransform: 'uppercase' },
    msgs: { flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: 10 },
    agentRow: { display: 'flex', gap: 8, alignItems: 'flex-start' },
    userRow:  { display: 'flex', gap: 8, alignItems: 'flex-start', flexDirection: 'row-reverse' },
    av:     { width: 26, height: 26, borderRadius: '50%', background: 'rgba(14,165,233,0.15)', border: '1px solid rgba(14,165,233,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 },
    userAv: { width: 26, height: 26, borderRadius: '50%', background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 },
    agentBub: { background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '4px 10px 10px 10px', padding: '8px 12px', fontSize: 12, color: '#E2E8F0', lineHeight: 1.6, maxWidth: '82%' },
    userBub:  { background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: '10px 4px 10px 10px', padding: '8px 12px', fontSize: 12, color: '#E2E8F0', lineHeight: 1.6, maxWidth: '82%' },
    footer: { padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 },
    inputRow: { display: 'flex', gap: 6, alignItems: 'flex-end' },
    inputEl: { flex: 1, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 8, padding: '8px 10px', color: '#F1F5F9', fontSize: 12, fontFamily: 'var(--font)', outline: 'none', resize: 'none' },
    sendBtn: { padding: '8px 14px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-end' },
    micBtn: { width: 32, height: 32, border: 'none', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-end', flexShrink: 0 },
    personBtn: { width: '100%', padding: '7px 0', background: 'none', border: '1px solid rgba(248,113,113,0.4)', borderRadius: 8, color: '#f87171', fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: '.02em' },
  };

  return (
    <>
      {open && (
        <div style={s.panel}>
          <div style={s.hdr}>
            <div style={s.dot} />
            <div style={s.hdrTitle}>TETRUS Support</div>
            <div style={s.hdrScreen}>{screenName}</div>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 0 }}>&#10005;</button>
          </div>
          <div style={s.msgs}>
            {messages.map((m, i) => (
              <div key={i} style={m.role === 'user' ? s.userRow : s.agentRow}>
                <div style={m.role === 'user' ? s.userAv : s.av}>{m.role === 'user' ? 'U' : 'T'}</div>
                <div style={m.role === 'user' ? s.userBub : s.agentBub}>{m.content}</div>
              </div>
            ))}
            {sending && (
              <div style={s.agentRow}>
                <div style={s.av}>T</div>
                <div style={{ ...s.agentBub, color: '#475569' }}>⋯⋯⋯</div>
              </div>
            )}
            {escalated && (
              <div style={{ textAlign: 'center', padding: '6px 10px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, fontSize: 11, color: '#10B981', marginTop: 4 }}>
                ✓ A support agent has been notified and will reach out shortly.
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div style={s.footer}>
            <div style={s.inputRow}>
              <textarea
                style={s.inputEl}
                rows={1}
                placeholder="Ask about any TETRUS feature…"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                disabled={sending}
              />
              <button
                style={{ ...s.micBtn, background: listening ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)', border: '1px solid ' + (listening ? 'rgba(239,68,68,0.5)' : 'var(--border)') }}
                onClick={toggleMic}
                title={listening ? 'Stop listening' : 'Speak your question'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={listening ? '#EF4444' : '#64748B'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="2" width="6" height="12" rx="3"/>
                  <path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/>
                </svg>
              </button>
              <button style={{ ...s.sendBtn, opacity: (sending || !input.trim()) ? 0.5 : 1 }} onClick={() => send()} disabled={sending || !input.trim()}>
                {sending ? '⋯' : 'Send'}
              </button>
            </div>
            {!escalated && (
              <button style={s.personBtn} onClick={escalateNow} disabled={sending}>
                Talk to a person →
              </button>
            )}
          </div>
        </div>
      )}
      <button
        style={{ ...s.bubble, transform: open ? 'scale(0.92)' : 'scale(1)' }}
        onClick={() => setOpen(o => !o)}
        title="TETRUS Support"
      >
        {open ? '✕' : '?'}
      </button>
    </>
  );
}

export default App;
